// @ts-nocheck

/** @typedef {import('./types').BundleItem} BundleItem */
/** @typedef {import('./types').BundlePriceStrategy} BundlePriceStrategy */
/** @typedef {import('./types').AddOnLimits} AddOnLimits */
/** @typedef {import('./types').BundleState} BundleState */

const newDate = new Date();
window.currentTime = newDate.getTime();
if (window.oldTime) {
  setTimeout(() => {
    window.location.reload();
  }, 100);
}
window.oldTime = window.currentTime;

/**
 * Bundle state management singleton for Swatch Bundle Builder
 * Handles global state for the swatch bundle builder
 * Uses separate namespace to avoid conflicts with regular bundle builder
 * @type {Object}
 */
const SwatchBundleStateManager = {
  /**
   * Initialize the bundle state
   * @returns {void}
   */
  initBundleState() {
    // Initialize the bundle state if it doesn't exist
    if (!window.fsSwatchBundleState) {
      /**
       * @type {import('./types').BundleState}
       */
      window.fsSwatchBundleState = {
        bundleItems: {},
        addOnItems: {},
        currentQuantity: 0,
        bundleQuantity: 0,
        addOnQuantity: 0,
        addOnLimits: { minimum: 0, maximum: 0 },
        priceStrategy: { strategy: 'fixed_pricing', value: 0 },
        selectedOption: null,
        totalPrice: 0,
        originalPrice: null,
        discountedPrice: null,
        subscribers: new Map(),
        autoUpgradeEnabled: fsbAdditionalSettings.auto_advance_bundle || true,
        cachedBundleOptions: null,
        bundleOptionsElement: document.querySelector('fsb-bundleoptions'),
        allProductsHaveSingleVariant: false,
        productVariantCounts: new Map(),

        /**
         * Updates multiple state properties at once
         * @param {Object} settings - Settings object
         * @param {number} [settings.bundleQuantity] - Bundle quantity
         * @param {AddOnLimits} [settings.addOnLimits] - Add-on limits
         * @param {BundlePriceStrategy} [settings.priceStrategy] - Price strategy
         * @param {string} [settings.selectedOption] - Selected option ID
         */
        setInitialState(settings = {}) {
          if (settings.bundleQuantity !== undefined) {
            this.bundleQuantity = settings.bundleQuantity;
          }
          if (settings.addOnLimits) {
            this.addOnLimits = settings.addOnLimits;
          }
          if (settings.priceStrategy) {
            this.priceStrategy = settings.priceStrategy;
          }
          if (settings.selectedOption) {
            this.selectedOption = settings.selectedOption;
          }
          this.cacheBundleOptions();
          try {
            this.notifySubscribers('stateInitialized');
          } catch (e) {
            console.error('Error notifying subscribers:', e);
          }
        },

        /**
         * Caches bundle options to avoid repeated DOM queries
         */
        cacheBundleOptions() {
          this.cachedBundleOptions = Array.from(document.querySelectorAll('.bundle-option'))
            .map((option) => ({
              element: option,
              quantity: parseInt(option.getAttribute('data-quantity'), 10),
              id: option.getAttribute('data-option-id'),
              settings: JSON.parse(option.dataset.option || '{}'),
            }))
            .reduce((uniqueOptions, option) => {
              if (!uniqueOptions.some((uniqueOption) => uniqueOption.id === option.id)) {
                uniqueOptions.push(option);
              }
              return uniqueOptions;
            }, [])
            .sort((a, b) => a.quantity - b.quantity);
        },

        /**
         * Finds the best fitting bundle for the current quantity
         * @returns {Object|null} Best fitting bundle option
         */
        findOptimalBundle() {
          if (!this.cachedBundleOptions) {
            this.cacheBundleOptions();
          }

          // Find the smallest bundle that can fit current quantity
          return this.cachedBundleOptions.find((option) => option.quantity >= this.currentQuantity);
        },

        /**
         * Finds the next larger bundle than current
         * @returns {Object|null} Next larger bundle or null
         */
        findNextBundle() {
          if (!this.cachedBundleOptions) {
            this.cacheBundleOptions();
          }
          return this.cachedBundleOptions.find((option) => option.quantity > this.bundleQuantity);
        },

        /**
         * Tracks variant counts for a product
         * @param {string|number} productId - Product identifier
         * @param {number} variantCount - Number of available variants
         */
        trackProductVariants(productId, variantCount) {
          this.productVariantCounts.set(productId.toString(), variantCount);
        },

        /**
         * Checks if all products have only single variants
         * @returns {boolean} True if all products have single variants
         */
        checkAllProductsHaveSingleVariant() {
          // If no products tracked yet, default to false
          if (this.productVariantCounts.size === 0) {
            this.allProductsHaveSingleVariant = false;
            return false;
          }

          // Check each product's variant count
          for (const variantCount of this.productVariantCounts.values()) {
            // If any product has more than 1 variant, not all products have single variants
            if (variantCount > 1) {
              this.allProductsHaveSingleVariant = false;
              return false;
            }
          }

          // All products have single variants
          this.allProductsHaveSingleVariant = true;
          return true;
        },

        /**
         * Adds an item to the bundle with auto-sizing
         * @param {string} itemKey - Item identifier
         * @param {import('./types').BundleItem} itemData - Item data
         * @returns {boolean} Success status
         */
        addBundleItem(itemKey, itemData) {
          // Check if we need to auto-upgrade
          if (this.currentQuantity >= this.bundleQuantity && this.autoUpgradeEnabled) {
            const nextBundle = this.findNextBundle();
            if (nextBundle && this.selectedOption !== nextBundle.id) {
              this.autoSwitchBundle(nextBundle);
              // After upgrade, check if we can add the item
              if (this.currentQuantity >= this.bundleQuantity) {
                return false;
              }
            } else {
              return false;
            }
          }

          // Add the item
          if (this.bundleItems[itemKey]) {
            this.bundleItems[itemKey].quantity++;
          } else {
            this.bundleItems[itemKey] = {
              ...itemData,
              quantity: 1,
              addedAt: Date.now(),
            };
          }
          this.currentQuantity++;
          if (window.fsbSettings && window.fsbSettings.added_toast_message) {
            Fsb.showToast(window.fsbSettings.added_toast_message);
          }
          this.notifySubscribers('bundleItems');
          return true;
        },

        /**
         * Switches bundle size with UI updates
         * @param {import('./types').BundleOption} bundleOption - Target bundle option
         */
        autoSwitchBundle(bundleOption) {
          // Update state as before...
          this.setInitialState({
            bundleQuantity: bundleOption.quantity,
            selectedOption: bundleOption.id,
            addOnLimits: bundleOption.settings.add_on || { minimum: 0, maximum: 0 },
            priceStrategy: bundleOption.settings.price || { strategy: 'fixed_pricing', value: 0 },
          });

          // Use cached bundle options component and popover
          if (this.bundleOptionsElement) {
            this.bundleOptionsElement.updateBundleDetails(bundleOption.id);
            this.bundleOptionsElement.updateSelectedAttr(bundleOption.element);
            this.bundleOptionsElement.updatePriceVisibility(bundleOption.settings.price?.strategy);
            if (this.popoverEl) {
              this.bundleOptionsElement.updatePopoverText(bundleOption);
            }
          }

          // Create and show notification (could also be debounced/reused)
          const direction = bundleOption.quantity > this.bundleQuantity ? 'increased' : 'decreased';
          const notification = document.createElement('div');
          notification.className = 'fsb__notification fsb__notification--resize';
          notification.textContent = `Bundle size ${direction} to ${bundleOption.quantity} items`;
          document.querySelector('.fsb-wrapper')?.appendChild(notification);
          setTimeout(() => notification.remove(), 3000);

          // Dispatch event as before
          const bundleOptionChangeEvent = new CustomEvent('bundleOptionChange', {
            detail: {
              addOnLimits: this.addOnLimits,
              bundleQuantity: this.bundleQuantity,
              selectedOptionId: bundleOption.id,
              priceStrategy: this.priceStrategy,
              isAutomatic: true,
            },
            bubbles: true,
            composed: true,
          });
          document.dispatchEvent(bundleOptionChangeEvent);
        },

        /**
         * Removes a bundle item and handles auto-downsizing
         * @param {string} itemKey - Item identifier
         */
        removeBundleItem(itemKey) {
          if (!this.bundleItems[itemKey]) return;

          // Remove the item
          this.bundleItems[itemKey].quantity--;
          if (this.bundleItems[itemKey].quantity === 0) {
            delete this.bundleItems[itemKey];
          }
          this.currentQuantity--;

          // Auto-adjust bundle size if enabled
          if (this.autoUpgradeEnabled && this.currentQuantity > 0) {
            const optimalBundle = this.findOptimalBundle();
            if (optimalBundle && optimalBundle.id !== this.selectedOption) {
              this.autoSwitchBundle(optimalBundle);
            }
          }

          this.notifySubscribers('bundleItems');
        },

        /**
         * Adds an add-on item to the bundle
         * @param {string} variantId - Variant ID
         * @param {import('./types').AddOnItem} itemData - Item data
         * @returns {string} Generated unique key for the item
         */
        addAddonItem(variantId, itemData) {
          const uniqueKey = `${variantId}_${Date.now()}`;
          this.addOnItems[uniqueKey] = {
            ...itemData,
            variantId, // Store the original variantId
            quantity: 1,
          };
          this.addOnQuantity++;
          if (window.fsbSettings && window.fsbSettings.added_toast_message) {
            Fsb.showToast(window.fsbSettings.added_toast_message);
          }
          this.notifySubscribers('addOnItems');
          return uniqueKey; // Return the generated key
        },

        /**
         * Removes an add-on item from the bundle
         * @param {string} itemKey - Item identifier
         */
        removeAddonItem(itemKey) {
          if (this.addOnItems[itemKey]) {
            delete this.addOnItems[itemKey];
            this.addOnQuantity--;
            this.notifySubscribers('addOnItems');
          }
        },

        /**
         * Notifies subscribers of state changes
         * @param {string} changeType - Type of change
         */
        notifySubscribers(changeType) {
          for (const [component, callback] of this.subscribers) {
            if (typeof callback === 'function') {
              try {
                callback.call(component, changeType);
              } catch (e) {
                console.error('Error calling subscriber callback:', e);
              }
            }
          }
        },

        /**
         * Subscribes a component to state changes
         * @param {Object} component - Component to subscribe
         * @param {function(string):void} callback - Callback function
         */
        subscribe(component, callback) {
          if (typeof callback === 'function') {
            this.subscribers.set(component, callback);
          }
        },

        /**
         * Unsubscribes a component from state changes
         * @param {Object} component - Component to unsubscribe
         */
        unsubscribe(component) {
          this.subscribers.delete(component);
        },

        /**
         * Clears all selected items from the bundle
         */
        clearAllSelections() {
          // Clear bundle items
          this.bundleItems = {};
          this.currentQuantity = 0;
          
          // Clear add-on items
          this.addOnItems = {};
          this.addOnQuantity = 0;
          
          // Notify subscribers of the changes
          this.notifySubscribers('bundleItems');
          this.notifySubscribers('addOnItems');
          this.notifySubscribers('currentQuantity');
          this.notifySubscribers('addOnQuantity');
        },
      };
    }
    // Store reference to the global state
    this.bundleState = window.fsSwatchBundleState;
  },
};

var Fsb = Fsb || {};

// ---------------------------------------------------------------------------
// Money format handler
Fsb.formatMoney = (cents, format = fsMoneyFormat) => {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  const currencyRate = window.Shopify?.currency?.rate ?? 1;
  cents *= currencyRate;
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    const [dollars, cents] = number.split('.');
    const dollarsAmount = dollars.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const centsAmount = cents ? `${decimal}${cents}` : '';
    return dollarsAmount + centsAmount;
  };
  let value = '';
  const formatKey = format.match(placeholderRegex)?.[1];
  switch (formatKey) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    case 'amount_no_decimals_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  return format.replace(placeholderRegex, value);
};

Fsb.showToast = (message) => {
  // Remove any existing toast
  let toast = document.querySelector('.fsb__toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'fsb__toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('fsb__toast--visible');

  // Remove after 2.5s
  clearTimeout(Fsb._toastTimeout);
  Fsb._toastTimeout = setTimeout(() => {
    toast.classList.remove('fsb__toast--visible');
  }, 2500);
};

window.Fsb = Fsb;

/**
 * Bundle options component
 * @customElement
 * @extends HTMLElement
 */
class FsbBundleOptions extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();
    this.initializeProperties();
  }

  /**
   * Called when element is connected to the DOM
   */
  connectedCallback() {
    this.initializeElements();
    this.initializeBundleState();
    this.initializePopover();
    this.addEventListeners();
  }

  /**
   * Initialize component properties
   */
  initializeProperties() {
    /**
     * @type {import('./types').BundleState}
     */
    this.bundleState = null;
  }

  initializeElements() {
    this.bundleOptions = Array.from(this.querySelectorAll('.bundle-option'));
    this.selectedBundleOption = this.querySelector('.bundle-option[selected]');
    this.popoverEl = this.querySelector('fsb-popover');
    this.aside = this.closest('fsb-aside');
    SwatchBundleStateManager.initBundleState.call(this);
    this.bundleState.subscribe(this, this.handleStateChange);
  }

  initializePopover() {
    const popover = this.querySelector('fsb-popover');
    if (popover) {
      const trigger = popover.querySelector('.fsb__popover-trigger');
      if (trigger && this.selectedBundleOption) {
        trigger.innerHTML = this.selectedBundleOption.innerHTML;
      }
    }
  }

  addEventListeners() {
    this.bundleOptions.forEach((bundleOption) => {
      bundleOption.addEventListener('click', (event) => {
        this.handleBundleOptionChange(bundleOption);
        const popover = this.querySelector('fsb-popover');
        if (popover) {
          popover.close();
          event.stopPropagation(); // Prevent the popover from reopening
        }
      });
    });
  }

  handleStateChange(changeType) {
    if (changeType === 'stateInitialized') {
      // Find the currently selected bundle option based on state
      const selectedId = this.bundleState.selectedOption;
      if (selectedId) {
        const newSelectedOption = this.querySelector(`.bundle-option[data-option-id="${selectedId}"]`);
        if (newSelectedOption && !newSelectedOption.hasAttribute('selected')) {
          this.updatePopoverText(newSelectedOption);
          this.updateSelectedAttr(newSelectedOption);
        }
      }
    }
  }

  initializeBundleState() {
    if (this.selectedBundleOption) {
      const bundleVariantId = this.selectedBundleOption.getAttribute('data-option-id');
      const optionSettings = JSON.parse(this.selectedBundleOption.dataset.option || '{}');
      this.bundleState.setInitialState({
        bundleQuantity: parseInt(this.selectedBundleOption.getAttribute('data-quantity'), 10) || 0,
        selectedOption: bundleVariantId,
        addOnLimits: optionSettings.add_on || { minimum: 0, maximum: 0 },
        priceStrategy: optionSettings.price || { strategy: 'fixed_pricing', value: 0 },
      });
      // Update UI to reflect initial state
      this.updateBundleDetails(bundleVariantId);
      this.updateSelectedAttr(this.selectedBundleOption);
      this.updatePriceVisibility(this.bundleState.priceStrategy.strategy);
    }
  }

  updateBundleState(bundleOption) {
    const bundleVariantId = bundleOption.getAttribute('data-option-id');
    this.updateWindowUrl(bundleVariantId);
    this.updateBundleDetails(bundleVariantId);
    this.updateSelectedAttr(bundleOption);
    this.updatePopoverText(bundleOption);
    this.bundleQuantity = parseInt(bundleOption.getAttribute('data-quantity'), 10) || 0;
    const optionSettings = JSON.parse(bundleOption.dataset.option || '{}');
    const newAddOnLimits = optionSettings.add_on || { minimum: 0, maximum: 0 };
    const newPriceStrategy = optionSettings.price || { strategy: 'fixed_pricing', value: 0 };
    this.updatePriceVisibility(newPriceStrategy.strategy);
    return { newAddOnLimits, newPriceStrategy, bundleVariantId };
  }

  handleBundleOptionChange(bundleOption) {
    const bundleVariantId = bundleOption.getAttribute('data-option-id');
    const optionSettings = JSON.parse(bundleOption.dataset.option || '{}');
    this.bundleState.setInitialState({
      bundleQuantity: parseInt(bundleOption.getAttribute('data-quantity'), 10) || 0,
      selectedOption: bundleVariantId,
      addOnLimits: optionSettings.add_on || { minimum: 0, maximum: 0 },
      priceStrategy: optionSettings.price || { strategy: 'fixed_pricing', value: 0 },
    });
    this.updatePopoverText(bundleOption);
    this.updateBundleDetails(bundleVariantId);
    this.updateSelectedAttr(bundleOption);
    const bundleOptionChangeEvent = new CustomEvent('bundleOptionChange', {
      detail: {
        addOnLimits: this.bundleState.addOnLimits,
        bundleQuantity: this.bundleState.bundleQuantity,
        selectedOptionId: bundleVariantId,
        priceStrategy: this.bundleState.priceStrategy,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(bundleOptionChangeEvent);
  }

  updatePopoverText(bundleOption) {
    const popover = this.querySelector('fsb-popover');
    if (!popover) return;

    const trigger = popover.querySelector('.fsb__popover-trigger');
    if (!trigger) return;

    // If the option has HTML content, use it directly
    if (bundleOption.innerHTML.trim()) {
      trigger.innerHTML = bundleOption.innerHTML;
    } else {
      // Fallback to textContent if no HTML
      trigger.textContent = bundleOption.textContent;
    }

    // Force repaint to ensure UI update
    trigger.style.display = 'none';
    trigger.offsetHeight;
    trigger.style.display = '';
  }

  updatePriceVisibility(priceStrategy) {
    const tabsContainer = document.querySelector('.fsb__tabs-container');
    if (tabsContainer) {
      if (priceStrategy === 'fixed_pricing') {
        tabsContainer.classList.add('fsb__price-hidden');
      } else {
        tabsContainer.classList.remove('fsb__price-hidden');
      }
    }
  }

  updateWindowUrl(variantId) {
    const url = new URL(window.location.href);
    variantId ? url.searchParams.set('variant', variantId) : url.searchParams.delete('variant');
    window.history.replaceState({}, '', url.toString());
  }

  updateBundleDetails(bundleVariantId) {
    if (!this.aside) return;
    this.aside.querySelectorAll('.bundle-details, .bundle__additional-info').forEach((option) => {
      option.classList.add('hidden');
    });
    this.aside
      .querySelectorAll(
        `.bundle-details[data-option-id="${bundleVariantId}"],.bundle__additional-info[data-option-id="${bundleVariantId}"]`
      )
      .forEach((activeOption) => {
        activeOption.classList.remove('hidden');
      });
  }

  updateSelectedAttr(bundleOption) {
    this.bundleOptions.forEach((bundle) => {
      bundle.removeAttribute('selected');
    });
    bundleOption.setAttribute('selected', 'selected');
  }

  removeExcessItems() {
    while (this.bundleState.currentQuantity > this.bundleState.bundleQuantity) {
      const variantId = [...this.aside.bundleItems].pop();
      if (variantId) {
        const variantRemovedEvent = new CustomEvent('variantRemovedFromBundle', {
          detail: { variantId },
          bubbles: true,
          composed: true,
        });
        document.dispatchEvent(variantRemovedEvent);
      }
    }
  }
}

customElements.define('fsb-bundleoptions', FsbBundleOptions);

/**
 * Product variant selector component
 * Handles variant selection and option combinations
 */
class FsVariantSelector extends HTMLElement {
  constructor() {
    super();
    console.log('FsVariantSelector constructor called');
    this.currentVariant = {};
    this.variants = [];
    this.handleChange = this.handleChange.bind(this);
  }

  connectedCallback() {
    console.log('connectedCallback called for', this, this.getAttribute('data-modal'));
    console.log('FsVariantSelector connectedCallback called');
    this.variants = this.getJSONFromScript('data-all-variants') || [];
    console.log('Loaded variants:', this.variants);
    
    // Debug: Log each variant's details
    if (this.variants.length > 0) {
      console.log('Variant details:');
      this.variants.forEach((variant, index) => {
        console.log(`  Variant ${index}:`, {
          id: variant.id,
          title: variant.title,
          price: variant.price,
          available: variant.available,
          options: variant.options
        });
      });
    } else {
      console.warn('No variants loaded!');
    }
    
    // Check if we have any select or radio elements
    const selects = this.querySelectorAll('select');
    const radios = this.querySelectorAll('input[type="radio"]');
    console.log('Found select elements:', selects.length);
    console.log('Found radio elements:', radios.length);
    
    // Log the actual HTML structure for debugging
    console.log('Variant selector HTML structure:', this.innerHTML);
    
    // Log details about each select element
    selects.forEach((select, index) => {
      console.log(`Select ${index}:`, {
        id: select.id,
        className: select.className,
        dataOptionIndex: select.dataset.optionIndex,
        dataOptionName: select.dataset.optionName,
        value: select.value,
        options: Array.from(select.options).map(opt => ({ value: opt.value, text: opt.textContent.trim() }))
      });
      
      // Check if this select is visible
      const computedStyle = getComputedStyle(select);
      console.log(`Select ${index} visibility:`, {
        display: computedStyle.display,
        visibility: computedStyle.visibility,
        opacity: computedStyle.opacity,
        height: computedStyle.height,
        width: computedStyle.width
      });
    });
    
    // Log details about each radio element
    radios.forEach((radio, index) => {
      console.log(`Radio ${index}:`, {
        id: radio.id,
        className: radio.className,
        dataOptionIndex: radio.dataset.optionIndex,
        dataOptionName: radio.dataset.optionName,
        value: radio.value,
        checked: radio.checked
      });
    });
    
    this.initializeProperties();
    this.addEventListener('change', this.handleChange.bind(this));
    console.log('Event listener attached for change events');
    
    // Add direct click listeners for debugging
    selects.forEach((select, index) => {
      console.log(`Adding event listeners to select ${index}:`, select.id);
      select.addEventListener('change', (e) => {
        console.log(`Select ${index} changed:`, e.target.value);
        // Call the handleChange method directly to ensure it's processed
        this.handleChange(e);
      });
      select.addEventListener('mousedown', (e) => {
        console.log(`Select ${index} mousedown:`, e.target.value);
      });
      select.addEventListener('focus', (e) => {
        console.log(`Select ${index} focused:`, e.target.value);
      });
    });
    
    radios.forEach((radio, index) => {
      console.log(`Adding event listeners to radio ${index}:`, radio.id);
      radio.addEventListener('click', (e) => {
        console.log(`Radio ${index} clicked:`, e.target.value);
      });
      radio.addEventListener('change', this.handleChange.bind(this));
      radio.addEventListener('mousedown', (e) => {
        console.log(`Radio ${index} mousedown:`, e.target.value);
      });
      radio.addEventListener('focus', (e) => {
        console.log(`Radio ${index} focused:`, e.target.value);
      });
    });
    
    // Add event listeners to labels as well (since radios are hidden)
    const labels = this.querySelectorAll('label');
    console.log('Found label elements:', labels.length);
    labels.forEach((label, index) => {
      console.log(`Label ${index}:`, {
        for: label.getAttribute('for'),
        className: label.className,
        textContent: label.textContent.trim()
      });
      
      label.addEventListener('click', (e) => {
        console.log(`Label ${index} clicked:`, e.target.textContent.trim());
      });
      label.addEventListener('mousedown', (e) => {
        console.log(`Label ${index} mousedown:`, e.target.textContent.trim());
      });
    });
    
    // Add specific debugging for color swatch labels
    const colorSwatches = this.querySelectorAll('.color-swatch');
    console.log('Found color swatch labels:', colorSwatches.length);
    colorSwatches.forEach((swatch, index) => {
      console.log(`Color swatch ${index}:`, {
        className: swatch.className,
        dataColorName: swatch.dataset.colorName,
        for: swatch.getAttribute('for'),
        textContent: swatch.textContent.trim(),
        computedStyle: {
          display: getComputedStyle(swatch).display,
          visibility: getComputedStyle(swatch).visibility,
          pointerEvents: getComputedStyle(swatch).pointerEvents,
          position: getComputedStyle(swatch).position,
          zIndex: getComputedStyle(swatch).zIndex
        }
      });
      
      swatch.addEventListener('click', (e) => {
        console.log(`Color swatch ${index} clicked:`, {
          target: e.target,
          currentTarget: e.currentTarget,
          value: e.currentTarget.textContent.trim()
        });
        
        // Find the associated radio button
        const radioId = swatch.getAttribute('for');
        console.log('Looking for radio with ID:', radioId);
        const radio = this.querySelector(`#${radioId}`);
        if (radio) {
          console.log('Associated radio found:', radio);
          console.log('Radio value:', radio.value);
          console.log('Radio checked before:', radio.checked);
          
          // Manually trigger the radio change
          radio.checked = true;
          console.log('Radio checked after:', radio.checked);
          
          // Create and dispatch a proper change event
          const changeEvent = new Event('change', { 
            bubbles: true, 
            cancelable: true,
            detail: { value: radio.value }
          });
          radio.dispatchEvent(changeEvent);
          console.log('Radio change event dispatched');
          
          // Also trigger a click event on the radio for good measure
          const clickEvent = new Event('click', { 
            bubbles: true, 
            cancelable: true 
          });
          radio.dispatchEvent(clickEvent);
          console.log('Radio click event dispatched');
          
          // Call handleChange directly as a fallback
          console.log('Calling handleChange directly...');
          this.handleChange.call(this, changeEvent);
        } else {
          console.log('No associated radio found for ID:', radioId);
        }
      });
      
      // Add additional event listeners to catch any interaction
      swatch.addEventListener('mousedown', (e) => {
        console.log(`Color swatch ${index} mousedown:`, e.target);
      });
      
      swatch.addEventListener('mouseup', (e) => {
        console.log(`Color swatch ${index} mouseup:`, e.target);
      });
      
      swatch.addEventListener('touchstart', (e) => {
        console.log(`Color swatch ${index} touchstart:`, e.target);
      });
      
      swatch.addEventListener('touchend', (e) => {
        console.log(`Color swatch ${index} touchend:`, e.target);
      });
    });
    
    // Add debugging for the entire swatch wrapper
    const swatchWrappers = this.querySelectorAll('.swatch-wrapper');
    console.log('Found swatch wrappers:', swatchWrappers.length);
    swatchWrappers.forEach((wrapper, index) => {
      console.log(`Swatch wrapper ${index}:`, {
        className: wrapper.className,
        children: wrapper.children.length,
        computedStyle: {
          display: getComputedStyle(wrapper).display,
          visibility: getComputedStyle(wrapper).visibility,
          pointerEvents: getComputedStyle(wrapper).pointerEvents
        }
      });
      
      wrapper.addEventListener('click', (e) => {
        console.log(`Swatch wrapper ${index} clicked:`, e.target);
      });
    });
    
    console.log('FsVariantSelector initialization complete');
    
    // Add document-level click debugging
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (target.closest('fsb-variantselector') === this) {
        console.log('Document click captured in variant selector:', {
          target: target,
          targetTagName: target.tagName,
          targetClassName: target.className,
          targetId: target.id,
          targetDataset: target.dataset
        });
      }
    }, true); // Use capture phase to catch events early
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
  }

  initializeProperties() {
    console.log('Initializing FsVariantSelector properties');
    this.currentVariant = this.getVariantData();
    console.log('Initial currentVariant:', this.currentVariant);
    this.selectInitialVariant();
    this.updateOptionVisibility();
    console.log('FsVariantSelector properties initialized');
  }

  getJSONFromScript(dataAttribute) {
    try {
      const script = this.querySelector(`script[${dataAttribute}]`);
      if (!script) {
        console.warn(`No script element found with ${dataAttribute}`);
        return null;
      }

      const content = script.textContent.trim();
      console.log(`Raw script content for ${dataAttribute}:`, content);

      if (!content || content === '[]' || content === '[        ]') {
        console.log(`Empty content for ${dataAttribute}, returning empty array`);
        return [];
      }

      if (typeof content === 'object') {
        console.log(`Content is already an object for ${dataAttribute}:`, content);
        return content;
      }

      const cleanedContent = content.replace(/,(\s*[\]}])/g, '$1');
      console.log(`Cleaned content for ${dataAttribute}:`, cleanedContent);

      try {
        const parsed = JSON.parse(cleanedContent);
        console.log(`Successfully parsed JSON for ${dataAttribute}:`, parsed);

        if (Array.isArray(parsed)) {
          const filtered = parsed.filter((item) => item && typeof item === 'object');
          console.log(`Filtered array for ${dataAttribute}:`, filtered);
          return filtered;
        } else if (typeof parsed === 'object') {
          return parsed;
        }

        console.warn('Parsed content has unexpected format');
        return null;
      } catch (parseError) {
        console.error(`JSON parse error for ${dataAttribute}:`, parseError);
        throw parseError;
      }
    } catch (error) {
      console.error('Error parsing variant data:', error);

      try {
        const script = this.querySelector(`script[${dataAttribute}]`);
        if (!script) return null;

        const content = script.textContent
          .replace(/^\uFEFF/, '')
          .replace(/\s+/g, ' ')
          .trim();

        console.log(`Recovery attempt content for ${dataAttribute}:`, content);
        return JSON.parse(content);
      } catch (recoveryError) {
        console.error('Recovery failed, returning empty result');
        return null;
      }
    }
  }

  handleChange(event) {
    console.log('handleChange method called:', {
      target: event.target,
      type: event.type,
      tagName: event.target.tagName,
      value: event.target.value
    });
    
    const changedElement = event.target;
    const optionIndex = parseInt(changedElement.dataset.optionIndex);
    
    console.log('Variant change detected:', {
      element: changedElement,
      optionIndex: optionIndex,
      value: changedElement.value,
      type: changedElement.type,
      tagName: changedElement.tagName
    });
    
    if (changedElement.type === 'radio') {
      const relatedSelect = this.querySelector(`select[data-option-index="${optionIndex}"]`);
      if (relatedSelect) {
        relatedSelect.value = changedElement.value;
      }
    } else if (changedElement.tagName === 'SELECT') {
      const relatedRadios = this.querySelectorAll(`input[type="radio"][data-option-index="${optionIndex}"]`);
      relatedRadios.forEach((radio) => {
        radio.checked = radio.value === changedElement.value;
      });
    }
    
    if (optionIndex === 0) {
      // Only reset subsequent options if this is not a split product
      const productCard = this.closest('fsb-productcard');
      const isSplitProduct = productCard && productCard.dataset.splitOption;
      
      if (!isSplitProduct) {
        console.log('Not a split product, resetting subsequent options');
        this.resetSubsequentOptions();
      } else {
        console.log('Split product detected, skipping resetSubsequentOptions to preserve Size selections');
      }
    }
    
    this.updateOptionVisibility();
    this.currentVariant = this.getVariantData();
    
    console.log('Current variant after change:', this.currentVariant);
    
    // Only sync variant changes across related product cards if this is NOT a split product
    const productCard = this.closest('fsb-productcard');
    const isSplitProduct = productCard && productCard.dataset.splitOption;
    
    if (!isSplitProduct) {
      console.log('Not a split product, syncing across related cards');
      this.syncVariantAcrossSplitProducts(changedElement, optionIndex);
    } else {
      console.log('Split product detected, skipping sync to keep variants independent');
    }
    
    const variantChangeEvent = new CustomEvent('variantChange', {
      detail: { variant: this.currentVariant },
      bubbles: true,
      composed: true,
    });
    
    console.log('Dispatching variantChange event:', variantChangeEvent);
    this.dispatchEvent(variantChangeEvent);

    // --- Add: Update swatch selected class ---
    this.updateSwatchSelectedClasses();
    // --- End add ---
  }

  syncVariantAcrossSplitProducts(changedElement, optionIndex) {
    console.log('syncVariantAcrossSplitProducts called:', {
      changedElement: changedElement,
      optionIndex: optionIndex,
      value: changedElement.value
    });
    
    // Find the product card that contains this variant selector
    const productCard = this.closest('fsb-productcard');
    if (!productCard) {
      console.log('No product card found');
      return;
    }

    // Check if this is a split product
    const isSplitProduct = productCard.dataset.splitOption;
    if (!isSplitProduct) {
      console.log('Not a split product, skipping sync');
      return;
    }

    console.log('Split product detected:', isSplitProduct);

    // Get the product data to identify related cards
    const productData = JSON.parse(productCard.dataset.product || '{}');
    if (!productData.id) {
      console.log('No product ID found');
      return;
    }

    console.log('Product ID:', productData.id);

    // Find all product cards for the same product
    // Use a more robust approach to find related cards
    const allProductCards = document.querySelectorAll('fsb-productcard');
    const relatedCards = Array.from(allProductCards).filter(card => {
      try {
        const cardProductData = JSON.parse(card.dataset.product || '{}');
        return cardProductData.id === productData.id;
      } catch (e) {
        console.log('Error parsing product data for card:', e);
        return false;
      }
    });
    
    console.log('Found related product cards:', relatedCards.length);
    
    relatedCards.forEach((card, index) => {
      if (card === productCard) {
        console.log(`Skipping current card ${index}`);
        return; // Skip the current card
      }
      
      console.log(`Processing related card ${index}:`, card);
      
      const cardVariantSelector = card.querySelector('fsb-variantselector');
      if (!cardVariantSelector) {
        console.log(`No variant selector found in card ${index}`);
        return;
      }

      // Get the option name that was changed
      const optionName = changedElement.dataset.optionName;
      if (!optionName) {
        console.log('No option name found');
        return;
      }

      console.log('Option name to sync:', optionName);

      // Find the corresponding option in the other card
      const correspondingSelect = cardVariantSelector.querySelector(`select[data-option-name="${optionName}"]`);
      const correspondingRadios = cardVariantSelector.querySelectorAll(`input[type="radio"][data-option-name="${optionName}"]`);

      console.log('Found corresponding elements:', {
        select: correspondingSelect,
        radios: correspondingRadios.length
      });

      if (correspondingSelect && correspondingSelect.value !== changedElement.value) {
        console.log(`Updating card ${index} - changing ${correspondingSelect.value} to ${changedElement.value}`);
        
        // Only update the Material option, preserve the Size option
        if (optionName === 'Material') {
          console.log(`Syncing Material option to: ${changedElement.value}`);
          
          // Update the select value
          correspondingSelect.value = changedElement.value;
          
          // Update corresponding radio buttons
          correspondingRadios.forEach(radio => {
            radio.checked = radio.value === changedElement.value;
          });

          // Update the variant selector's current variant
          cardVariantSelector.currentVariant = cardVariantSelector.getVariantData();
          
          // Don't call updateOptionVisibility for split products to preserve Size selections
          const cardProductCard = card.closest('fsb-productcard');
          const isCardSplitProduct = cardProductCard && cardProductCard.dataset.splitOption;
          
          if (!isCardSplitProduct) {
            console.log('Updating option visibility for non-split product');
            cardVariantSelector.updateOptionVisibility();
          } else {
            console.log('Skipping updateOptionVisibility for split product to preserve Size selections');
          }
          
          // Only dispatch a variantChange event to ensure the product card updates
          const variantChangeEvent = new CustomEvent('variantChange', {
            detail: { variant: cardVariantSelector.currentVariant },
            bubbles: true,
            composed: true,
          });
          cardVariantSelector.dispatchEvent(variantChangeEvent);
          
          console.log(`Card ${index} synchronized successfully`);
        } else {
          console.log(`Skipping sync for option ${optionName} - only Material should be synced`);
        }
      } else {
        console.log(`Card ${index} already has the correct value or no corresponding select found`);
      }
    });
  }

  resetSubsequentOptions() {
    console.log('resetSubsequentOptions called');
    const selects = this.querySelectorAll('select');
    const firstOptionValue = selects[0].value;
    console.log('First option value:', firstOptionValue);
    
    const matchingVariants = this.variants.filter(
      (variant) => variant.available && variant.options[0] === firstOptionValue
    );
    console.log('Matching variants for first option:', matchingVariants.length);
    
    for (let i = 1; i < selects.length; i++) {
      const availableValues = new Set(matchingVariants.map((v) => v.options[i]));
      const firstAvailable = Array.from(availableValues)[0];
      console.log(`Option ${i} - available values:`, Array.from(availableValues), 'first available:', firstAvailable);
      
      if (firstAvailable) {
        // Check if the current value is still available
        const currentValue = selects[i].value;
        const isCurrentValueAvailable = availableValues.has(currentValue);
        
        if (isCurrentValueAvailable) {
          console.log(`Option ${i} - keeping current value:`, currentValue);
          // Keep the current value if it's still available
        } else {
          console.log(`Option ${i} - current value not available, setting to:`, firstAvailable);
          // Only reset if the current value is not available
          selects[i].value = firstAvailable;
          const radios = this.querySelectorAll(`input[type="radio"][data-option-index="${i}"]`);
          radios.forEach((radio) => {
            radio.checked = radio.value === firstAvailable;
          });
        }
      }
    }
  }

  updateOptionVisibility() {
    console.log('updateOptionVisibility called');
    const selects = Array.from(this.querySelectorAll('select'));
    const currentSelections = selects.map((select) => select.value);
    console.log('Current selections:', currentSelections);
    
    selects.length > 0 ? this.enableAllOptionsForSelect(selects[0]) : {};
    for (let i = 1; i < selects.length; i++) {
      const availableValues = this.getAvailableValues(i, currentSelections);
      const select = selects[i];
      console.log(`Option ${i} (${select.dataset.optionName}) - available values:`, Array.from(availableValues));
      console.log(`Option ${i} current value:`, select.value);
      
      Array.from(select.options).forEach((option) => {
        if (option.value) {
          const isAvailable = availableValues.has(option.value);
          option.disabled = !isAvailable;
          option.classList.toggle('unavailable', !isAvailable);
        }
      });
      const radios = this.querySelectorAll(`input[type="radio"][data-option-index="${i}"]`);
      radios.forEach((radio) => {
        const isAvailable = availableValues.has(radio.value);
        radio.disabled = !isAvailable;
        const swatch = radio.nextElementSibling;
        if (swatch && swatch.classList.contains('color-swatch')) {
          swatch.classList.toggle('unavailable', !isAvailable);
        }
      });
    }
  }

  enableAllOptionsForSelect(select) {
    Array.from(select.options).forEach((option) => {
      option.disabled = false;
      option.classList.remove('unavailable');
    });
    const radios = this.querySelectorAll(`input[type="radio"][data-option-index="${select.dataset.optionIndex}"]`);
    radios.forEach((radio) => {
      radio.disabled = false;
      const swatch = radio.nextElementSibling;
      if (swatch && swatch.classList.contains('color-swatch')) {
        swatch.classList.remove('unavailable');
      }
    });
  }

  getAvailableValues(optionIndex, currentSelections) {
    const matchingVariants = this.variants.filter((variant) => {
      return (
        variant.available &&
        currentSelections.every((selected, idx) => idx >= optionIndex || variant.options[idx] === selected)
      );
    });
    return new Set(matchingVariants.map((variant) => variant.options[optionIndex]));
  }

  getVariantData() {
    const selectedOptions = this.selectedOptionValues;
    console.log('Selected option values:', selectedOptions);
    console.log('Available variants:', this.variants);
    
    if (selectedOptions.length === 0) {
      console.log('No selected options, returning first variant:', this.variants[0]);
      return this.variants[0];
    }
    
    const matchingVariant = this.variants.find((variant) => 
      variant.options.every((option, index) => option === selectedOptions[index])
    );
    
    console.log('Matching variant found:', matchingVariant);
    return matchingVariant;
  }

  selectInitialVariant() {
    console.log('selectInitialVariant called, variants length:', this.variants.length);
    
    if (this.variants.length === 0) return;
    
    const firstVariant = this.variants.find(v => v.available) || this.variants[0];
    console.log('First available variant:', firstVariant);
    
    const selects = this.querySelectorAll('select');
    const radios = this.querySelectorAll('input[type="radio"]');
    console.log('Found selects:', selects.length, 'radios:', radios.length);
    
    // Set select values
    selects.forEach((select, index) => {
      if (firstVariant.options[index]) {
        const optionValue = firstVariant.options[index];
        console.log(`Set select ${index} to:`, optionValue);
        
        // Find the option with this value and select it
        const options = select.querySelectorAll('option');
        options.forEach(option => {
          option.selected = option.value === optionValue;
        });
      }
    });
    
    // Set radio values
    radios.forEach((radio, index) => {
      if (firstVariant.options[radio.dataset.optionIndex]) {
        const optionValue = firstVariant.options[radio.dataset.optionIndex];
        console.log(`Set radio ${index} to:`, optionValue);
        
        if (radio.value === optionValue) {
          radio.checked = true;
        } else {
          radio.checked = false;
        }
      }
    });
    
    // --- Add: Update swatch selected class ---
    this.updateSwatchSelectedClasses();
    // --- End add ---
    
    // Set the current variant
    this.currentVariant = firstVariant;
    console.log('Set currentVariant to:', this.currentVariant);
    
    // Dispatch initial variant change event
    const event = new CustomEvent('variantChange', {
      detail: { variant: firstVariant }
    });
    console.log('Dispatching initial variantChange event:', event);
    this.dispatchEvent(event);
  }

  get selectedOptionValues() {
    const values = [...this.querySelectorAll('select')].map((select) => select.value);
    console.log('selectedOptionValues:', values);
    return values;
  }

  updateSwatchSelectedClasses() {
    console.log('updateSwatchSelectedClasses called in', this, 'with data-debug:', this.getAttribute('data-debug'));
    // Remove .selected from all swatch labels
    this.querySelectorAll('.color-swatch.selected').forEach(label => {
      label.classList.remove('selected');
      console.log('Removed .selected from label:', label);
    });
    // Add .selected to the label for each checked radio
    this.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
      console.log('Checked radio:', radio);
      let label = this.querySelector(`label[for="${radio.id}"]`);
      console.log('Label found for radio:', label);
      if (label) {
        label.classList.add('selected');
        console.log('Added .selected to label (immediate):', label);
        setTimeout(() => {
          label.classList.add('selected');
          console.log('Added .selected to label (delayed):', label);
        }, 100);
      }
    });
  }
}

customElements.define('fsb-variantselector', FsVariantSelector);

/**
 * Tab navigation component
 * Manages product category tabs
 */
class FsbTabs extends HTMLElement {
  constructor() {
    super();
    this.navLinks = [];
    this.contentItems = [];
    this.hasAllTab = false;
    this.isSingleCategory = false;
    this.currentView = 'products'; // Default view
  }

  connectedCallback() {
    this.navLinks = this.querySelectorAll('.tabs__navigation-link');
    this.contentItems = this.querySelectorAll('.tabs__content-item');
    this.hasAllTab = this.navLinks[0]?.classList.contains('tabs__navigation-link--all');
    this.isSingleCategory = this.contentItems.length === 1;

    // Initialize view-specific class on the component
    this.classList.add('fsb-view--products');

    // Initialize bundle state reference
    SwatchBundleStateManager.initBundleState.call(this);
    if (this.bundleState) {
      this.bundleState.subscribe(this, this.handleStateChange);
    }

    this.render();
    if (!this.isSingleCategory) {
      this.addEventListeners();
    }
  }

  disconnectedCallback() {
    if (this.bundleState) {
      this.bundleState.unsubscribe(this);
    }
  }

  /**
   * Handle state changes from the bundle state
   * @param {string} changeType - Type of change
   */
  handleStateChange(changeType) {
    if (changeType === 'view_changed') {
      this.updateViewClasses();
    }
    // --- Custom logic for disabling add buttons in first tab ---
    if (changeType === 'bundleItems') {
      this.updateFirstTabAddButtons();
    }
  }

  render() {
    if (this.isSingleCategory) {
      this.contentItems[0].classList.add('active');
      return;
    }
    if (!this.navLinks.length || !this.contentItems.length) {
      console.error('Required elements not found in FsbTabs');
      return;
    }
    this.navLinks[0].classList.add('active');
    if (this.hasAllTab) {
      this.contentItems.forEach((item) => item.classList.add('active'));
    } else {
      this.contentItems[0].classList.add('active');
    }
    if (!this.hasAllTab) {
      this.contentItems.forEach((item, index) => {
        if (index !== 0) item.classList.remove('active');
      });
    }
  }

  addEventListeners() {
    this.navLinks.forEach((link, index) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        this.activateTab(link, index);
      });
    });
  }

  activateTab(link, index) {
    this.navLinks.forEach((link) => link.classList.remove('active'));
    link.classList.add('active');
    if (this.hasAllTab && index === 0) {
      this.contentItems.forEach((item) => item.classList.add('active'));
    } else {
      this.contentItems.forEach((item, itemIndex) => {
        if (this.hasAllTab) {
          item.classList.toggle('active', itemIndex === index - 1);
        } else {
          item.classList.toggle('active', itemIndex === index);
        }
      });
    }

    // Check if this is an add-ons tab and update the bundle state
    const tabContent = this.querySelector(`[aria-labelledby="${link.id}"]`);
    if (tabContent && tabContent.dataset.categoryName) {
      const isAddonTab = tabContent.dataset.categoryName.toLowerCase() === 'add-ons';
      if (this.bundleState) {
        this.bundleState.currentView = isAddonTab ? 'addons' : 'products';
        this.bundleState.notifySubscribers('view_changed');
      } else if (window.fsSwatchBundleState) {
        window.fsSwatchBundleState.currentView = isAddonTab ? 'addons' : 'products';
        window.fsSwatchBundleState.notifySubscribers('view_changed');
      }

      // Update view classes directly as well
      this.currentView = isAddonTab ? 'addons' : 'products';
      this.updateViewClasses();
    }
  }


  /**
   * Update CSS classes based on current view
   */
  updateViewClasses() {
    const currentView = this.getCurrentView();

    // Remove all view-related classes
    this.classList.remove('fsb-view--products', 'fsb-view--addons');

    // Add class for current view
    this.classList.add(`fsb-view--${currentView}`);

    // Also add class to container for easier styling
    const container = this.closest('.fsb');
    if (container) {
      container.classList.remove('fsb--products-view', 'fsb--addons-view');
      container.classList.add(`fsb--${currentView}-view`);
    }
  }

  // Helper method to get the current view
  getCurrentView() {
    return this.bundleState.currentView;
  }

  isProductsView() {
    return this.getCurrentView() === 'products';
  }

  isAddonsView() {
    return this.getCurrentView() === 'addons';
  }

  /**
   * Disable add buttons in the first tab if an item from that tab is in the bundle
   * Re-enable if none are in the bundle
   */
  updateFirstTabAddButtons() {
    console.log('[FSB DEBUG] updateFirstTabAddButtons called');
    // Find the tab whose data-category-name contains 'Dining Table' (case-insensitive)
    const diningTableTab = Array.from(this.contentItems).find(tab => {
      const cat = tab.getAttribute('data-category-name') || '';
      return cat.toLowerCase().includes('dining table');
    });
    if (!diningTableTab) {
      console.log('[FSB DEBUG] No Dining Table tab found');
      return;
    }
    console.log('[FSB DEBUG] Targeted tab:', diningTableTab.getAttribute('data-category-name'));
    // Get all product cards in the dining table tab
    const productCards = diningTableTab.querySelectorAll('fsb-productcard');
    // Get bundle items from state
    const bundleItems = this.bundleState?.bundleItems || {};
    // Gather all variant IDs for products in the Dining Table tab
    const diningTableVariantIds = [];
    productCards.forEach(card => {
      let variants = [];
      if (card.dataset && card.dataset.product) {
        try {
          const prod = JSON.parse(card.dataset.product);
          if (prod.variants) {
            variants = prod.variants.map(v => v.id?.toString());
          }
        } catch (e) {}
      }
      if (!variants.length) {
        const prodAttr = card.getAttribute('data-product');
        if (prodAttr) {
          try {
            const prod = JSON.parse(prodAttr);
            if (prod.variants) {
              variants = prod.variants.map(v => v.id?.toString());
            }
          } catch (e) {}
        }
      }
      diningTableVariantIds.push(...variants);
    });
    console.log('[FSB DEBUG] Variant IDs in Dining Table tab:', diningTableVariantIds);
    // Log bundle items and their variantId values
    const bundleVariantIds = Object.values(bundleItems).map(item => item.variantId?.toString());
    console.log('[FSB DEBUG] Bundle item variant IDs:', bundleVariantIds);
    // Check if any bundle item is from the dining table tab by variantId
    let found = false;
    for (const key in bundleItems) {
      const item = bundleItems[key];
      if (diningTableVariantIds.includes(item.variantId?.toString())) {
        found = true;
        break;
      }
    }
    console.log('[FSB DEBUG] Found Dining Table variant in bundle:', found);
    // Disable or enable add buttons in the dining table tab
    productCards.forEach(card => {
      const addBtn = card.querySelector('.fsb__atk-button--add');
      if (addBtn) {
        // Always add a click-blocking handler
        if (!addBtn._fsbClickBlocker) {
          addBtn.addEventListener('click', function(e) {
            if (this.disabled) {
              e.preventDefault();
              e.stopImmediatePropagation();
              console.log('[FSB DEBUG] Blocked click on disabled add button', this);
              return false;
            }
          }, true);
          addBtn._fsbClickBlocker = true;
        }
        if (found) {
          addBtn.setAttribute('disabled', '');
          addBtn.disabled = true;
          addBtn.classList.add('fsb__atk-button--force-disabled');
          console.log('[FSB DEBUG] Disabled add button for product card', addBtn, 'disabled:', addBtn.disabled, 'class:', addBtn.className);
        } else {
          addBtn.removeAttribute('disabled');
          addBtn.disabled = false;
          addBtn.classList.remove('fsb__atk-button--force-disabled');
          console.log('[FSB DEBUG] Enabled add button for product card', addBtn, 'disabled:', addBtn.disabled, 'class:', addBtn.className);
        }
      }
    });
    // --- MutationObserver for debugging DOM changes ---
    if (!diningTableTab._fsbMutationObserver) {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            console.log('[FSB DEBUG] MutationObserver: childList change in Dining Table tab', mutation);
          } else if (mutation.type === 'attributes') {
            console.log('[FSB DEBUG] MutationObserver: attribute change', mutation.target, mutation.attributeName);
          }
        }
      });
      observer.observe(diningTableTab, { childList: true, subtree: true, attributes: true });
      diningTableTab._fsbMutationObserver = observer;
      console.log('[FSB DEBUG] MutationObserver attached to Dining Table tab');
    }
    // --- End MutationObserver ---
    // Block all add actions in the Dining Table tab if a table is already in the bundle
    // Disable all quantity selectors and prevent form/click events
    const blockAll = found;
    // Disable/enable quantity selectors
    const qtySelectors = diningTableTab.querySelectorAll('.fsb__quantity-selector-increase, .fsb__quantity-selector-decrease');
    qtySelectors.forEach(btn => {
      if (blockAll) {
        btn.setAttribute('disabled', '');
        btn.disabled = true;
        btn.classList.add('fsb__atk-button--force-disabled');
        if (!btn._fsbClickBlocker) {
          btn._fsbClickBlockerHandler = function(e) {
            e.preventDefault();
            e.stopImmediatePropagation();
            console.log('[FSB DEBUG] Blocked click on disabled quantity button', this);
            return false;
          };
          btn.addEventListener('click', btn._fsbClickBlockerHandler, true);
          btn._fsbClickBlocker = true;
        }
      } else {
        btn.removeAttribute('disabled');
        btn.disabled = false;
        btn.classList.remove('fsb__atk-button--force-disabled');
        // Remove click-blocking handler if present
        if (btn._fsbClickBlocker) {
          btn.removeEventListener('click', btn._fsbClickBlockerHandler, true);
          btn._fsbClickBlocker = false;
          btn._fsbClickBlockerHandler = null;
        }
      }
    });
    // Also handle add buttons
    productCards.forEach(card => {
      const addBtn = card.querySelector('.fsb__atk-button--add');
      if (addBtn) {
        if (blockAll) {
          addBtn.setAttribute('disabled', '');
          addBtn.disabled = true;
          addBtn.classList.add('fsb__atk-button--force-disabled');
          if (!addBtn._fsbClickBlocker) {
            addBtn._fsbClickBlockerHandler = function(e) {
              if (this.disabled) {
                e.preventDefault();
                e.stopImmediatePropagation();
                console.log('[FSB DEBUG] Blocked click on disabled add button', this);
                return false;
              }
            };
            addBtn.addEventListener('click', addBtn._fsbClickBlockerHandler, true);
            addBtn._fsbClickBlocker = true;
          }
        } else {
          addBtn.removeAttribute('disabled');
          addBtn.disabled = false;
          addBtn.classList.remove('fsb__atk-button--force-disabled');
          // Remove click-blocking handler if present
          if (addBtn._fsbClickBlocker) {
            addBtn.removeEventListener('click', addBtn._fsbClickBlockerHandler, true);
            addBtn._fsbClickBlocker = false;
            addBtn._fsbClickBlockerHandler = null;
          }
        }
      }
    });
    // Prevent any form submission or click event in the tab
    if (!diningTableTab._fsbFormBlocker) {
      // Store a reference to the FsbTabs instance for dynamic state
      diningTableTab._fsbTabsInstance = this;
      diningTableTab.addEventListener('submit', function(e) {
        // Always check current bundle state
        const tabs = diningTableTab._fsbTabsInstance;
        const bundleItems = tabs?.bundleState?.bundleItems || {};
        // Gather all variant IDs for products in the Dining Table tab
        let diningTableVariantIds = [];
        const productCards = diningTableTab.querySelectorAll('fsb-productcard');
        productCards.forEach(card => {
          let variants = [];
          if (card.dataset && card.dataset.product) {
            try {
              const prod = JSON.parse(card.dataset.product);
              if (prod.variants) {
                variants = prod.variants.map(v => v.id?.toString());
              }
            } catch (e) {}
          }
          if (!variants.length) {
            const prodAttr = card.getAttribute('data-product');
            if (prodAttr) {
              try {
                const prod = JSON.parse(prodAttr);
                if (prod.variants) {
                  variants = prod.variants.map(v => v.id?.toString());
                }
              } catch (e) {}
            }
          }
          diningTableVariantIds.push(...variants);
        });
        let found = false;
        for (const key in bundleItems) {
          const item = bundleItems[key];
          if (diningTableVariantIds.includes(item.variantId?.toString())) {
            found = true;
            break;
          }
        }
        if (found) {
          e.preventDefault();
          e.stopImmediatePropagation();
          console.log('[FSB DEBUG] Blocked form submit in Dining Table tab', e.target);
          return false;
        }
      }, true);
      diningTableTab.addEventListener('click', function(e) {
        // Always check current bundle state
        const tabs = diningTableTab._fsbTabsInstance;
        const bundleItems = tabs?.bundleState?.bundleItems || {};
        // Gather all variant IDs for products in the Dining Table tab
        let diningTableVariantIds = [];
        const productCards = diningTableTab.querySelectorAll('fsb-productcard');
        productCards.forEach(card => {
          let variants = [];
          if (card.dataset && card.dataset.product) {
            try {
              const prod = JSON.parse(card.dataset.product);
              if (prod.variants) {
                variants = prod.variants.map(v => v.id?.toString());
              }
            } catch (e) {}
          }
          if (!variants.length) {
            const prodAttr = card.getAttribute('data-product');
            if (prodAttr) {
              try {
                const prod = JSON.parse(prodAttr);
                if (prod.variants) {
                  variants = prod.variants.map(v => v.id?.toString());
                }
              } catch (e) {}
            }
          }
          diningTableVariantIds.push(...variants);
        });
        let found = false;
        for (const key in bundleItems) {
          const item = bundleItems[key];
          if (diningTableVariantIds.includes(item.variantId?.toString())) {
            found = true;
            break;
          }
        }
        if (found) {
          // Only block if the target is a button or inside a form
          if (e.target.closest('button, form')) {
            e.preventDefault();
            e.stopImmediatePropagation();
            console.log('[FSB DEBUG] Blocked click in Dining Table tab', e.target);
            return false;
          }
        }
      }, true);
      diningTableTab._fsbFormBlocker = true;
    }
  }
}

customElements.define('fsb-tabs', FsbTabs);
/**
 * Quantity selector component
 * Handles product quantity selection in bundle
 */
class FsbQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.decreaseBtn = null;
    this.increaseBtn = null;
    this.input = null;
    this.productCard = null;
    this.minValue = 0;
  }

  connectedCallback() {
    this.decreaseBtn = this.querySelector('.fsb__quantity-selector-decrease');
    this.increaseBtn = this.querySelector('.fsb__quantity-selector-increase');
    this.input = this.querySelector('.fsb__quantity-selector-input');
    this.productCard = this.closest('fsb-productcard');

    if (!this.productCard) {
      console.warn('FsbQuantitySelector: No parent product card found');
      return;
    }

    // Set initial value
    if (!this.input.value || this.input.value === '0') {
      this.input.value = this.minValue.toString();
    }

    // Disable decrease button initially if at minimum
    if (parseInt(this.input.value) <= this.minValue) {
      this.decreaseBtn.setAttribute('disabled', '');
    }

    // Initialize with current state from bundle
    this.syncWithBundleState();
  }

  syncWithBundleState() {
    if (!this.productCard?.currentVariant?.id) return;

    const variantId = this.productCard.currentVariant.id;
    const bundleState = this.productCard.bundleState;

    if (!bundleState) return;

    // Count quantity in bundle
    let count = 0;
    for (const key in bundleState.bundleItems) {
      const item = bundleState.bundleItems[key];
      if (item.variantId == variantId) {
        count += item.quantity;
      }
    }

    // Update input and button states
    this.input.value = count;
    this.decreaseBtn.disabled = count <= this.minValue;

    // Check if at bundle capacity
    const canAutoUpgrade = bundleState.findNextBundle() !== null;
    const isAtCapacity = bundleState.currentQuantity >= bundleState.bundleQuantity && !canAutoUpgrade;
    this.increaseBtn.disabled = isAtCapacity;
  }
}

customElements.define('fsb-quantityselector', FsbQuantitySelector);

/**
 * Product card component
 * @customElement
 * @extends HTMLElement
 */
class FsbProductCard extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();
    /**
     * @type {import('./types').BundleState}
     */
    this.bundleState = null;
    this.bindEvents();
  }

  bindEvents() {
    // Bind all event handlers to the instance
    this.handleAddToKit = this.handleAddToKit.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleQuantityDecrease = this.handleQuantityDecrease.bind(this);
    this.handleQuantityIncrease = this.handleQuantityIncrease.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
  }

  connectedCallback() {
    console.log('FsbProductCard connectedCallback called for:', this.dataset.product ? 'product with data' : 'product without data');
    
    this.isAddon = this.getAttribute('is') === 'fsb-productaddon';

    SwatchBundleStateManager.initBundleState.call(this);
    this.bundleState.subscribe(this, this.handleStateChange);
    this.button = this.querySelector('.fsb__atk-button--add');
    
    try {
      this.product = JSON.parse(this.dataset.product || '{}');
      console.log('Successfully parsed product data:', this.product.title);
    } catch (error) {
      console.error('Error parsing product data:', error);
      console.error('Raw data-product attribute:', this.dataset.product);
      this.product = {};
    }
    
    this.category = this.dataset.category;
    this.variantSelector = this.querySelector('fsb-variantselector');
    console.log('Found variant selector:', this.variantSelector);
    
    // Wait for the variant selector to initialize before getting the current variant
    if (this.variantSelector) {
      console.log('Variant selector variants:', this.variantSelector.variants);
      // If the variant selector hasn't initialized yet, wait for it
      if (!this.variantSelector.currentVariant && this.variantSelector.variants?.length > 0) {
        this.currentVariant = this.variantSelector.variants[0];
        console.log('Set current variant from variants[0]:', this.currentVariant);
      } else {
        this.currentVariant = this.variantSelector.currentVariant;
        console.log('Set current variant from currentVariant:', this.currentVariant);
      }
    } else {
      console.warn('No variant selector found');
    }
    this.aside = document.querySelector('fsb-aside');
    this.priceElement = this.querySelector('#product-price');
    this.quantitySelector = this.querySelector('fsb-quantityselector');

    // Debug: Check if price element exists and has content
    if (this.priceElement) {
      console.log('Price element found for', this.product.title, ':', {
        innerHTML: this.priceElement.innerHTML,
        textContent: this.priceElement.textContent,
        className: this.priceElement.className
      });
    } else {
      console.warn('Price element NOT found for:', this.product.title);
    }

    if (this.variantSelector?.variants.length === 0 && window.fsbSettings.hide_unavailable_products) {
      this.hideProduct();
      return;
    }

    this.addEventListeners();
    this.initializeProductDetails();

    // Only initialize quantity selector for regular products, not add-ons
    if (!this.isAddon && this.quantitySelector) {
      this.initQuantitySelector();
    }

    // Fallback price display after a short delay to ensure variant selector is initialized
    setTimeout(() => {
      if (this.priceElement && !this.priceElement.innerHTML.trim()) {
        console.log('Delayed fallback price display for:', this.product.title);
        if (this.variantSelector?.variants?.length > 0) {
          const firstVariant = this.variantSelector.variants[0];
          if (firstVariant) {
            const displayPrice = firstVariant.compare_at_price && firstVariant.compare_at_price > 0 ? firstVariant.compare_at_price : firstVariant.price;
            if (displayPrice !== undefined) {
              this.priceElement.innerHTML = Fsb.formatMoney(displayPrice);
            }
          }
        }
      }
    }, 100);

    // Immediate fallback price display if we have a current variant
    if (this.currentVariant && this.priceElement && !this.priceElement.innerHTML.trim()) {
      console.log('Immediate fallback price display for:', this.product.title, this.currentVariant);
      const displayPrice = this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > 0 ? this.currentVariant.compare_at_price : this.currentVariant.price;
      if (displayPrice !== undefined) {
        this.priceElement.innerHTML = Fsb.formatMoney(displayPrice);
      }
    }

    // Test direct price display for debugging
    if (this.priceElement && this.variantSelector?.variants?.length > 0) {
      const testVariant = this.variantSelector.variants[0];
      console.log('Direct price test for', this.product.title, ':', {
        testVariant: testVariant,
        price: testVariant?.price,
        compareAtPrice: testVariant?.compare_at_price,
        priceElement: this.priceElement,
        priceElementHTML: this.priceElement.innerHTML
      });
      
      if (testVariant) {
        const displayPrice = testVariant.compare_at_price && testVariant.compare_at_price > 0 ? testVariant.compare_at_price : testVariant.price;
        if (displayPrice !== undefined) {
          console.log('Attempting direct price set for', this.product.title);
          this.priceElement.innerHTML = Fsb.formatMoney(displayPrice);
          console.log('Price element HTML after set:', this.priceElement.innerHTML);
        }
      }
    }
    if (this.bundleState && this.product.id && !this.isAddon) {
      const variantSelector = this.querySelector('fsb-variantselector');
      if (variantSelector && variantSelector.variants) {
        const variantCount = variantSelector.variants.length;
        this.bundleState.trackProductVariants(this.product.id, variantCount);
      }
    }

    this.checkAndUpdateButtonState(this.button);
  }

  disconnectedCallback() {
    if (this.bundleState) {
      this.bundleState.unsubscribe(this);
    }

    // Remove event listeners
    if (this.button) {
      this.button.removeEventListener('click', this.handleAddToKit);
    }

    this.removeEventListener('variantChange', this.handleVariantChange);

    // Remove quantity selector event listeners
    if (this.quantitySelector) {
      const decreaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-decrease');
      const increaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-increase');
      const inputField = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

      decreaseBtn?.removeEventListener('click', this.handleQuantityDecrease);
      increaseBtn?.removeEventListener('click', this.handleQuantityIncrease);
      inputField?.removeEventListener('change', this.handleQuantityChange);
    }
  }

  handleStateChange(changeType) {
    switch (changeType) {
      case 'bundleItems':
        if (this.quantitySelector && !this.isAddon) {
          this.updateQuantityValue();
          this.updateQuantityButtonStates();
        }
        this.checkAndUpdateButtonState(this.button);
        break;
      case 'addOnItems':
        if (this.isAddon) {
          this.checkAndUpdateButtonState(this.button);
        }
        break;
      case 'bundleQuantity':
        this.checkAndUpdateButtonState(this.button);
        break;
      case 'stateInitialized':
        this.checkAndUpdateButtonState(this.button);
        if (this.quantitySelector && !this.isAddon) {
          this.updateQuantityValue();
          this.updateQuantityButtonStates();
        }
        break;
    }
  }

  hideProduct() {
    this.setAttribute('hidden', '');
    if (this.button) {
      this.button.removeEventListener('click', this.handleAddToKit);
    }
    this.removeEventListener('variantChange', this.handleVariantChange);
  }

  addEventListeners() {
    if (this.button) {
      this.button.addEventListener('click', this.handleAddToKit);
    }

    this.addEventListener('variantChange', this.handleVariantChange);

    if (this.quantitySelector && !this.isAddon) {
      const decreaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-decrease');
      const increaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-increase');
      const inputField = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

      decreaseBtn?.addEventListener('click', this.handleQuantityDecrease);
      increaseBtn?.addEventListener('click', this.handleQuantityIncrease);
      inputField?.addEventListener('change', this.handleQuantityChange);
    }
  }

  handleVariantChange(event) {
    // Update the current variant from the event
    if (event.detail && event.detail.variant) {
      this.currentVariant = event.detail.variant;
      console.log('Updated currentVariant in product card:', this.currentVariant);
    }
    
    this.updateProductDetails(event);
    this.checkAndUpdateButtonState(this.button);

    if (this.quantitySelector && !this.isAddon) {
      this.updateQuantityValue();
      this.updateQuantityButtonStates();
    }
    if (this.bundleState && this.product.id && !this.isAddon) {
      const variantSelector = this.querySelector('fsb-variantselector');
      if (variantSelector && variantSelector.variants) {
        const variantCount = variantSelector.variants.length;
        this.bundleState.trackProductVariants(this.product.id, variantCount);
      }
    }
  }

  checkAndUpdateButtonState(button = this.button) {
    console.log('checkAndUpdateButtonState called:', {
      button: !!button,
      currentVariant: this.currentVariant,
      buttonText: button?.textContent
    });
    
    if (!button) {
      console.warn('No button found for button state update');
      return;
    }

    const variant = this.currentVariant;
    const available = this.isVariantAvailable(variant);
    console.log('Variant availability check:', {
      variant: variant,
      available: available,
      inventoryPolicy: variant?.inventory_policy,
      inventoryMgmt: variant?.inventory_management,
      inventoryQuantity: variant?.inventory_quantity
    });
    
    button.setAttribute('data-available', available);

    // Update button text based on availability
    if (available === false) {
      button.textContent = 'OUT OF STOCK';
      console.log('Button updated to OUT OF STOCK');
    } else if (available === 'unset') {
      button.textContent = 'SELECT OPTIONS';
      console.log('Button updated to SELECT OPTIONS');
    } else {
      // Get the original button text from section settings or use default
      const originalText = window.fsbSettings?.atk_label || 'Add to Bundle';
      button.textContent = originalText;
      console.log('Button updated to:', originalText);
    }

    if (this.isAddon) {
      // For add-ons, check against add-on limits
      if (this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum) {
        button.setAttribute('disabled', '');
      } else if (variant?.id && available) {
        button.removeAttribute('disabled');
      } else {
        button.setAttribute('disabled', '');
      }
    } else {
      // For regular bundle items, check against bundle size
      const canAutoUpgrade = this.bundleState.findNextBundle() != undefined;
      const isAtCapacity = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity && !canAutoUpgrade;
      if (isAtCapacity) {
        button.setAttribute('disabled', '');
        // Also disable quantity increase button if present
        this.quantitySelector?.querySelector('.fsb__quantity-selector-increase')?.setAttribute('disabled', '');
      } else if (variant?.id && available) {
        button.removeAttribute('disabled');
        // Enable quantity increase button if present
        this.quantitySelector?.querySelector('.fsb__quantity-selector-increase')?.removeAttribute('disabled', '');
      } else {
        button.setAttribute('disabled', '');
        // Disable quantity increase button if present
        this.quantitySelector?.querySelector('.fsb__quantity-selector-increase')?.setAttribute('disabled', '');
      }
    }
  }

  isVariantAvailable(variant) {
    console.log('isVariantAvailable called with:', {
      variant: variant,
      variantSelector: !!this.variantSelector,
      selectedOptionValues: this.variantSelector?.selectedOptionValues,
      bundleState: !!this.bundleState
    });
    
    // During initialization, if we have a variant but no variant selector is ready yet,
    // we can still check the variant's availability directly
    if (this.variantSelector && this.variantSelector.selectedOptionValues && this.variantSelector.selectedOptionValues.some((value) => !value)) {
      console.log('Returning unset due to incomplete option selection');
      return 'unset';
    }
    if (!variant) {
      console.log('Returning false due to no variant');
      return false;
    }

    const inventoryPolicy = variant.inventory_policy;
    const inventoryMgmt = variant.inventory_management;
    const inventoryQuantity = variant.inventory_quantity || 0;

    console.log('Inventory check:', {
      inventoryPolicy,
      inventoryMgmt,
      inventoryQuantity,
      variantAvailable: variant.available
    });

    if (!inventoryMgmt || inventoryPolicy === 'continue') {
      console.log('Returning variant.available:', variant.available);
      return variant.available;
    }

    if (inventoryPolicy === 'deny') {
      const totalBundleQuantity = Object.values(this.bundleState.bundleItems)
        .filter((item) => item.variantId === variant.id)
        .reduce((total, item) => total + item.quantity, 0);

      let totalAddonQuantity = 0;
      if (this.isAddon) {
        totalAddonQuantity = Object.values(this.bundleState.addOnItems)
          .filter((item) => item.variantId === variant.id)
          .reduce((total, item) => total + item.quantity, 0);
      }

      const totalUsedQuantity = totalBundleQuantity + totalAddonQuantity;
      const result = variant.available && inventoryQuantity > totalUsedQuantity;
      
      console.log('Deny policy calculation:', {
        totalBundleQuantity,
        totalAddonQuantity,
        totalUsedQuantity,
        result
      });
      
      return result;
    }

    console.log('Returning variant.available as fallback:', variant.available);
    return variant.available;
  }

  initializeProductDetails() {
    // Try to get the first available variant with a price
    let initialVariant = null;
    
    if (this.variantSelector?.variants?.length > 0) {
      // Always use the first variant with a price
      initialVariant = this.variantSelector.variants.find(v => v.price !== undefined) ||
                      this.variantSelector.variants[0];
    } else if (this.currentVariant) {
      initialVariant = this.currentVariant;
    }
    
    console.log('initializeProductDetails for', this.product.title, ':', {
      variantSelector: this.variantSelector,
      variants: this.variantSelector?.variants,
      currentVariant: this.currentVariant,
      initialVariant: initialVariant
    });
    
    if (initialVariant) {
      console.log('Calling updateProductDetails with:', initialVariant);
      this.updateProductDetails({
        detail: { variant: initialVariant },
      });
      
      // Check and update button state on page load
      // Wait for variant selector to be fully ready
      const checkButtonState = () => {
        if (this.variantSelector && this.variantSelector.selectedOptionValues) {
          console.log('Variant selector ready, checking button state');
          this.checkAndUpdateButtonState();
        } else {
          console.log('Variant selector not ready yet, retrying in 100ms');
          setTimeout(checkButtonState, 100);
        }
      };
      
      // Start checking after a short delay
      setTimeout(checkButtonState, 100);
    } else {
      console.warn('No initial variant found for:', this.product.title);
      // Fallback: try to display price from the first available variant
      if (this.variantSelector?.variants?.length > 0) {
        const firstVariant = this.variantSelector.variants[0];
        if (firstVariant && this.priceElement) {
          console.log('Fallback price display for:', this.product.title, firstVariant);
          const displayPrice = firstVariant.compare_at_price && firstVariant.compare_at_price > 0 ? firstVariant.compare_at_price : firstVariant.price;
          if (displayPrice !== undefined) {
            this.priceElement.innerHTML = Fsb.formatMoney(displayPrice);
          }
        }
      }
    }
  }

  handleAddToKit() {
    const variant = this.currentVariant;
    if (!variant?.id || !this.bundleState) return;

    // Helper function to get the display price (compare_at_price as primary, price as fallback)
    const getDisplayPrice = (variant) => {
      return variant.compare_at_price && variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
    };

    if (this.isAddon) {
      // Add-on product logic
      if (window.fsbSettings.show_personalization) {
        const personalizationModal = document.querySelector('fsb-personalization');
        if (personalizationModal) {
          personalizationModal.showPersonalization(this, variant);
        }
      } else {
        if (this.bundleState.addOnQuantity >= this.bundleState.addOnLimits.maximum) {
          const notification = document.createElement('div');
          notification.className = 'fsb__notification';
          notification.textContent = window.fsbSettings.personalization_max_error;
          this.appendChild(notification);
          setTimeout(() => notification.remove(), 3000);
          return;
        }

        // Add addon item with unique key
        const addonData = {
          price: getDisplayPrice(variant),
          title: this.product.title,
          variantTitle: !variant.is_default ? variant.title : null,
          image: variant.media_link || this.product.featured_image,
        };
        this.bundleState.addAddonItem(variant.id, addonData);
      }
    } else {
      // If we have a quantity selector, use the increment functionality
      if (this.quantitySelector) {
        this.handleQuantityIncrease();
        return;
      }

      // Standard add to kit logic for non-quantity products
      if (this.bundleState.currentQuantity >= this.bundleState.bundleQuantity) {
        const canAutoUpgrade = this.bundleState.findNextBundle() !== null;

        if (canAutoUpgrade) {
          const addedSuccessfully = this.bundleState.addBundleItem(variant.id, {
            price: getDisplayPrice(variant),
            title: this.product.title,
            variantTitle: !variant.is_default ? variant.title : null,
            image: variant.media_link || this.product.featured_image,
            variantId: variant.id,
          });

          if (!addedSuccessfully) {
            this.showNotification(window.fsbSettings.personalization_max_error || 'Maximum bundle size reached');
          }
        } else {
          this.showNotification(window.fsbSettings.personalization_max_error || 'Maximum bundle size reached');
        }
      } else {
        this.bundleState.addBundleItem(variant.id, {
          price: getDisplayPrice(variant),
          title: this.product.title,
          variantTitle: !variant.is_default ? variant.title : null,
          image: variant.media_link || this.product.featured_image,
          variantId: variant.id,
        });
      }
    }
  }


  updateProductDetails({ detail: { variant } }) {
    console.log('updateProductDetails called for', this.product.title, 'with variant:', variant);
    
    this.currentVariant = variant;
    if (!variant) {
      console.warn('No variant provided to updateProductDetails for:', this.product.title);
      return;
    }

    const priceElement = this.querySelector('#product-price');
    const imageElement = this.querySelector('.fsb__product-card__image');
    const addButton = this.querySelector('.fsb__atk-button--add');

    console.log('Price element found:', !!priceElement, 'Variant price:', variant.price);

    // Update price display - use compare_at_price as primary, price as fallback
    if (priceElement) {
      const displayPrice = variant.compare_at_price && variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      if (displayPrice !== undefined) {
        priceElement.innerHTML = Fsb.formatMoney(displayPrice);
      } else {
        console.warn('Cannot set price for', this.product.title, ':', {
          priceElement: !!priceElement,
          variantPrice: variant.price,
          variantCompareAtPrice: variant.compare_at_price
        });
      }
    }

    // Update button text based on variant availability
    if (addButton) {
      const isAvailable = this.isVariantAvailable(variant);
      if (isAvailable === false) {
        addButton.textContent = 'OUT OF STOCK';
        addButton.setAttribute('data-available', 'false');
      } else if (isAvailable === 'unset') {
        addButton.textContent = 'SELECT OPTIONS';
        addButton.setAttribute('data-available', 'unset');
      } else {
        // Get the original button text from section settings or use default
        const originalText = window.fsbSettings?.atk_label || 'Add to Bundle';
        addButton.textContent = originalText;
        addButton.setAttribute('data-available', 'true');
      }
    }

    // Always update image for variant changes, regardless of availability
    console.log('Image update logic:', {
      imageElement: !!imageElement,
      variantMediaLink: variant.media_link,
      productFeaturedImage: this.product.featured_image
    });
    
    if (imageElement && variant.media_link) {
      console.log('Updating image to variant media:', variant.media_link);
      imageElement.src = `${variant.media_link.replace('=450', '=50')}`;
      imageElement.src = `${variant.media_link}`;
      imageElement.removeAttribute('srcset');
      imageElement.alt = variant.media_alt || this.product.title || '';
    } else if (imageElement && this.product.featured_image) {
      console.log('Updating image to product featured image:', this.product.featured_image);
      imageElement.src = `${this.product.featured_image}&width=450`;
    } else {
      console.log('No image update possible - missing image element or media links');
    }
    
    // Update leadtime display
    const leadtimeElement = this.querySelector('#leadtime-display');
    if (leadtimeElement && variant.leadtime) {
      console.log('Updating leadtime to:', variant.leadtime);
      leadtimeElement.textContent = variant.leadtime;
    } else if (leadtimeElement) {
      console.log('No leadtime available for variant, clearing display');
      leadtimeElement.textContent = '';
    }
    
    // Update tooltip title to show variant name instead of product name
    if (variant.title && variant.title !== 'Default Title') {
      this.setAttribute('title', variant.title);
    } else {
      // Fallback to product title if variant has no meaningful title
      this.setAttribute('title', this.product.title);
    }
  }

  // Quantity selector methods
  initQuantitySelector() {
    if (!this.quantitySelector || this.isAddon) return;

    // Initialize to current variant quantity
    this.updateQuantityValue();
    this.updateQuantityButtonStates();
  }

  updateQuantityValue() {
    if (!this.quantitySelector || this.isAddon) return;

    if (!this.currentVariant) {
      console.warn('No current variant available for quantity value update');
      return;
    }

    const variantId = this.currentVariant.id;
    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

    if (quantityInput && variantId) {
      const currentQuantity = this.getVariantQuantityInBundle(variantId);
      quantityInput.value = currentQuantity;
    }
  }

  getVariantQuantityInBundle(variantId) {
    if (!variantId || !this.bundleState) return 0;

    let count = 0;

    // Count all matching variants in the bundle state
    // Note: variantId might be stored as string or number, so we check both
    for (const key in this.bundleState.bundleItems) {
      const item = this.bundleState.bundleItems[key];
      if (item.variantId == variantId) {
        // Intentional loose comparison
        count += item.quantity;
      }
    }

    return count;
  }

  updateQuantityButtonStates() {
    if (!this.quantitySelector || this.isAddon) return;

    const decreaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-decrease');
    const increaseBtn = this.quantitySelector.querySelector('.fsb__quantity-selector-increase');
    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');

    if (!decreaseBtn || !increaseBtn || !quantityInput) return;

    // Disable decrease button if quantity is 0 or 1
    const currentValue = parseInt(quantityInput.value, 10) || 0;
    decreaseBtn.disabled = currentValue <= 0;

    // Disable increase button if at capacity
    const canAutoUpgrade = this.bundleState.findNextBundle() != undefined;

    const isAtCapacity = this.bundleState.currentQuantity >= this.bundleState.bundleQuantity && !canAutoUpgrade;
    increaseBtn.disabled = isAtCapacity || !this.isVariantAvailable(this.currentVariant);

    // Show/hide quantity selector and ADD button based on quantity
    if (!this.currentVariant) {
      console.warn('No current variant available for quantity button state update');
      return;
    }
    const variantQuantityInBundle = this.getVariantQuantityInBundle(this.currentVariant.id);
    if (variantQuantityInBundle === 0) {
      this.quantitySelector.classList.add('hidden');
      if (this.button) {
        this.button.classList.remove('hidden');
      }
    } else {
      this.quantitySelector.classList.remove('hidden');
      if (this.button) {
        this.button.classList.add('hidden');
      }
    }
  }

  handleQuantityDecrease() {
    if (!this.quantitySelector || this.isAddon || !this.currentVariant) return;

    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');
    if (!this.currentVariant) {
      console.warn('No current variant available for quantity decrease');
      return;
    }
    const variantId = this.currentVariant.id;
    const currentValue = parseInt(quantityInput.value, 10) || 0;

    if (currentValue > 0) {
      // Update input value
      const newValue = currentValue - 1;
      quantityInput.value = newValue;

      // Remove one item from bundle
      this.updateQuantityInBundle(variantId, newValue);
    }

    this.updateQuantityButtonStates();
  }

  handleQuantityIncrease() {
    if (!this.quantitySelector || this.isAddon || !this.currentVariant) return;

    const quantityInput = this.quantitySelector.querySelector('.fsb__quantity-selector-input');
    if (!this.currentVariant) {
      console.warn('No current variant available for quantity increase');
      return;
    }
    const variantId = this.currentVariant.id;
    const currentValue = parseInt(quantityInput.value, 10) || 0;

    // Check if we can add more
    if (this.bundleState.currentQuantity >= this.bundleState.bundleQuantity) {
      const canAutoUpgrade = this.bundleState.findNextBundle() !== null;

      if (!canAutoUpgrade) {
        Fsb.showToast(window.fsbSettings.personalization_max_error || 'Maximum bundle size reached');
        return;
      }
    }

    // Update input value
    const newValue = currentValue + 1;
    quantityInput.value = newValue;

    // Add one item to bundle
    this.updateQuantityInBundle(variantId, newValue);
    this.updateQuantityButtonStates();
  }

  handleQuantityChange(event) {
    if (!this.quantitySelector || this.isAddon || !this.currentVariant) return;

    const quantityInput = event.target;
    if (!this.currentVariant) {
      console.warn('No current variant available for quantity change');
      return;
    }
    const variantId = this.currentVariant.id;
    let newValue = parseInt(quantityInput.value, 10);

    // Enforce minimum value of 0
    if (isNaN(newValue) || newValue < 0) {
      newValue = 0;
      quantityInput.value = newValue;
    }

    // Check if we're trying to add more than the maximum bundle quantity
    const currentCount = this.bundleState.bundleItems[variantId]?.quantity || 0;
    const bundleCapacity = this.bundleState.bundleQuantity - this.bundleState.currentQuantity + currentCount;

    if (newValue > currentCount && newValue - currentCount > bundleCapacity) {
      const canAutoUpgrade = this.bundleState.findNextBundle() !== null;

      if (!canAutoUpgrade) {
        newValue = currentCount + bundleCapacity;
        quantityInput.value = newValue;
        Fsb.showToast('Maximum bundle size reached');
      }
    }

    // Update bundle with new quantity
    this.updateQuantityInBundle(variantId, newValue);
    this.updateQuantityButtonStates();
  }

  updateQuantityInBundle(variantId, newQuantity) {
    if (!variantId || !(this.bundleState || window.fsSwatchBundleState)) return;

    if (!this.currentVariant) {
      console.warn('No current variant available for quantity bundle update', {
        variantId,
        hasBundleState: !!(this.bundleState || window.fsSwatchBundleState),
        currentVariant: this.currentVariant
      });
      return;
    }

    // Helper function to get the display price (compare_at_price as primary, price as fallback)
    const getDisplayPrice = (variant) => {
      return variant.compare_at_price && variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
    };

    const currentQuantity =
      this.bundleState?.bundleItems[variantId]?.quantity || window.fsSwatchBundleState.bundleItems[variantId]?.quantity || 0;

    if (newQuantity > currentQuantity) {
      // Need to add items
      const itemData = {
        price: getDisplayPrice(this.currentVariant),
        title: this.product.title,
        variantTitle: !this.currentVariant.is_default ? this.currentVariant.title : null,
        image: this.currentVariant.media_link || this.product.featured_image,
        variantId: this.currentVariant.id,
      };

      for (let i = 0; i < newQuantity - currentQuantity; i++) {
        this.bundleState.addBundleItem(variantId, itemData);
      }
    } else if (newQuantity < currentQuantity) {
      // Need to remove items
      let toRemove = currentQuantity - newQuantity;

      // Find items to remove

      const item = this.bundleState?.bundleItems[variantId] || window.fsSwatchBundleState.bundleItems[variantId];
      if (item && toRemove > 0) {
        const removeCount = Math.min(toRemove, item.quantity);

        for (let i = 0; i < removeCount; i++) {
          this.bundleState
            ? this.bundleState.removeBundleItem(variantId)
            : window.fsSwatchBundleState.removeBundleItem(variantId);
        }

        toRemove -= removeCount;
      }
    }
  }
}

customElements.define('fsb-productcard', FsbProductCard);

/**
 * Bundle aside component
 * @customElement
 * @extends HTMLElement
 */
class FsbAside extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();
    /**
     * @type {import('./types').BundleState}
     */
    this.bundleState = null;
    this.bundleOptions = null;
    this.continueButtons = null;
    this.addToCartButtons = null;
    this.backButtons = null;
    this.itemsContainer = null;
    this.addOnItemsSidebarContainer = null;
    this.addOnItemsContainers = null;
    this.bundlePriceElement = null;
    this.tabs = null;
    this.isExpanded = false;
    this.isMobile = window.innerWidth <= 749;
    this.bundleItemsWrapper = null;
  }

  /**
   * Called when element is connected to the DOM
   */
  connectedCallback() {
    SwatchBundleStateManager.initBundleState.call(this);
    // Ensure we're always using the global bundle state reference
    this.bundleState = this.bundleState || window.fsSwatchBundleState;
    if (!this.bundleState) {
      console.error('Failed to initialize bundle state in FsbAside');
      return;
    }
    console.log('Bundle state initialized:', this.bundleState);
    this.bundleState.subscribe(this, this.handleStateChange);
    this.initializeElements();
    this.setupInitialState();
    this.addEventListeners();
    this.setupMobileInteraction();

    // Handle resize events
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth <= 749;
      if (!this.isMobile) {
        this.classList.remove('expanded');
        this.isExpanded = false;
        if (this.bundleItemsWrapper) {
          this.bundleItemsWrapper.style.display = '';
        }
      }
    });
  }
  setupMobileInteraction() {
    if (!this.isMobile) return;

    const header = this.querySelector('.sidebar-header');
    this.bundleItemsWrapper = this.querySelector('.bundle-items-wrapper');

    if (!header || !this.bundleItemsWrapper) return;

    const toggleExpand = (e) => {
      if (!this.isMobile) return;

      // Don't toggle if clicking on interactive elements
      if (e.target.closest('button, a, select, input')) return;

      this.isExpanded = !this.isExpanded;
      this.classList.toggle('expanded', this.isExpanded);

      // Toggle only the bundle items wrapper
      if (this.bundleItemsWrapper) {
        this.bundleItemsWrapper.style.display = this.isExpanded ? 'block' : 'none';
      }
    };

    header.addEventListener('click', toggleExpand);

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.isExpanded || !this.isMobile) return;
      if (!this.contains(e.target)) {
        this.isExpanded = false;
        this.classList.remove('expanded');
        if (this.bundleItemsWrapper) {
          this.bundleItemsWrapper.style.display = 'none';
        }
      }
    });
  }

  disconnectedCallback() {
    this.bundleState.unsubscribe(this);
    this.removeEventListeners();
  }

  /**
   * Handle bundle option change events
   * @param {CustomEvent} event - The bundle option change event
   */
  bundleOptionChangeHandler(event) {
    // Check if the change was automatic (e.g. from auto-sizing)
    if (this.bundleState && this.bundleState.currentView === 'addons') {
      // Check if the user needs to add more products to meet the new bundle size
      if (this.bundleState.currentQuantity < this.bundleState.bundleQuantity) {
        this.backToProducts();
      }
    }
  }

  initializeElements() {
    this.bundleOptions = this.querySelector('fsb-bundleoptions');
    this.continueButtons = this.querySelectorAll('.fsb__bundle-continue');
    this.addToCartButtons = this.querySelectorAll('.fsb__bundle-add-to-cart');
    this.backButtons = this.querySelectorAll('.fsb__bundle-back');
    this.resetButtons = this.querySelectorAll('.fsb__bundle-reset');
    console.log('Reset buttons found in initializeElements:', this.resetButtons.length);
    this.itemsContainer = this.querySelector('.bundle-items-container');
    this.addOnItemsSidebarContainer = this.querySelector('.addon-items-sidebar-container');
    this.addOnItemsContainers = this.querySelectorAll('.addon-items-container');
    this.bundlePriceElement = this.querySelector('.bundle-price');
    this.tabs = this.closest('.fsb__tabs');
    // Ensure we're using the global bundle state reference
    this.bundleState = this.bundleState || window.fsSwatchBundleState;
    if (this.bundleState) {
      this.bundleState.currentView = 'products';
    }
    this.productsCountElements = document.querySelectorAll('.bundle-selection-count.products');
    this.addonsCountElements = document.querySelectorAll('.bundle-selection-count.addons');
    this.randomizeButtons = this.querySelectorAll('.fsb__button-randomize');
    setTimeout(() => {
      this.randomizeButtons.forEach((btn) => {
        btn?.classList.toggle('hidden', !this.bundleState.checkAllProductsHaveSingleVariant());
      });
    });
  }

  /**
   * Apply a function to each addon container
   * @param {Function} callback - Function to apply to each container
   */
  forEachAddonContainer(callback) {
    if (!this.addOnItemsContainers || this.addOnItemsContainers.length === 0) return;
    this.addOnItemsContainers.forEach((container) => callback(container));
  }

  setupInitialState() {
    this.hasAddons = this.addOnItemsContainers.length > 0 && this.bundleState.addOnLimits.maximum > 0;
    if (this.addOnItemsSidebarContainer) {
      this.addOnItemsSidebarContainer.classList.toggle('hidden', !this.hasAddons);
    }
    this.updateUI();
  }

  handleStateChange(changeType) {
    switch (changeType) {
      case 'bundleItems':
        this.updateItemsDisplay();
        this.updateButtonStates();
        this.updateTotalPrice();
        this.updateSelectionCounts();
        this.updateAccordionStates();

        // Expand bundle items when item is added to bundle
        const bundleItemsContainer = this.querySelector('.bundle-items-container');
        const bundleDetailsWrapper = this.querySelector('.fsb__bundle-details-wrapper');
        if (bundleItemsContainer && bundleDetailsWrapper && !bundleItemsContainer.classList.contains('expanded')) {
          this.toggleAccordion(bundleDetailsWrapper, bundleItemsContainer);
        }

        // Collapse addon items if they are expanded
        const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
        const addonHeaderContainer = this.querySelector('.addon-items-header');
        if (addonItemsContainer && addonHeaderContainer && addonItemsContainer.classList.contains('expanded')) {
          this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
        }
        break;

      case 'addOnItems':
        this.updateItemsDisplay();
        this.updateButtonStates();
        this.updateTotalPrice();
        this.updateSelectionCounts();
        this.updateAddonsVisibility();

        // Expand addon items when addon is added to bundle
        const addonContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
        const addonHeader = this.querySelector('.addon-items-header');
        if (addonContainer && addonHeader && !addonContainer.classList.contains('expanded')) {
          this.toggleAccordion(addonHeader, addonContainer);
        }

        // Collapse bundle items if they are expanded
        const bundleContainer = this.querySelector('.bundle-items-container');
        const bundleHeader = this.querySelector('.fsb__bundle-details-wrapper');
        if (bundleContainer && bundleHeader && bundleContainer.classList.contains('expanded')) {
          this.toggleAccordion(bundleHeader, bundleContainer);
        }
        break;

      case 'priceStrategy':
        this.updatePriceVisibility();
        this.updateTotalPrice();
        break;

      case 'stateInitialized':
        this.removeExcessAddOns();
        this.removeExcessItems();
        this.updateUI();
        this.initializeAccordionStates();
        this.bundleOptionChangeHandler();
        break;

      case 'view_changed':
        this.updateViewBasedUI();
        this.updateAccordionsBasedOnView();
        break;
    }
  }

  updateUI() {
    this.updateItemsDisplay();
    this.updateButtonStates();
    this.updatePriceVisibility();
    this.updateTotalPrice();
    this.updateSelectionCounts();
    this.updateViewBasedUI();
  }

  updateViewBasedUI() {
    const currentView = this.bundleState.currentView;

    if (currentView === 'products') {
      // Update selection count visibility
      document.querySelectorAll('.bundle-selection-count.products').forEach((el) => {
        el.style.display = 'block';
      });
      document.querySelectorAll('.bundle-selection-count.addons').forEach((el) => {
        el.style.display = 'none';
      });
    } else if (currentView === 'addons') {
      document.querySelectorAll('.bundle-selection-count.products').forEach((el) => {
        el.style.display = 'none';
      });
      document.querySelectorAll('.bundle-selection-count.addons').forEach((el) => {
        el.style.display = 'block';
      });
    }
  }

  /**
   * Sets accordion state directly without animation
   * Used for initialization and programmatic state changes
   * @param {HTMLElement} container - The accordion content container
   * @param {boolean} expanded - Whether the accordion should be expanded
   */
  setAccordionState(container, expanded = true) {
    if (!container) return;
    // Set state directly without animation
    container.style.height = expanded ? 'auto' : '0px';
    container.classList.toggle('expanded', expanded);

    // Find and update the matching header's icon if possible
    const header = this.getAccordionHeaderForContent(container);
    if (header) {
      const icon = header.querySelector('.icon-caret');
      if (icon) {
        icon.classList.toggle('icon-caret--rotated', expanded);
      }
    }
  }

  /**
   * Helper to find the header element for a given accordion content
   * @param {HTMLElement} contentContainer - The accordion content element
   * @returns {HTMLElement|null} - The header element or null
   */
  getAccordionHeaderForContent(contentContainer) {
    if (!contentContainer) return null;

    // Determine which header this content belongs to
    if (contentContainer.classList.contains('bundle-items-container')) {
      return this.querySelector('.fsb__bundle-details-wrapper');
    } else if (contentContainer.classList.contains('addon-items-container')) {
      return this.querySelector('.addon-items-header');
    }
    return null;
  }

  /**
   * Helper to set caret (arrow) visibility and interactivity
   * @param {HTMLElement} icon
   * @param {boolean} visible
   */
  setCaretState(icon, visible = true) {
    if (!icon) return;
    icon.classList.toggle('hidden', !visible);
    icon.setAttribute('aria-hidden', String(!visible));
    icon.style.pointerEvents = visible ? '' : 'none';
    icon.tabIndex = visible ? 0 : -1;
  }

  /**
   * Initializes accordion functionality for bundle containers
   */
  initializeAccordions() {
    // Only enable accordion logic if both products and add-ons are available
    if (!this.hasAddons) {
      // Always expand bundle items and disable caret
      this.setAccordionState(this.querySelector('.bundle-items-container'), true);
      this.setCaretState(this.querySelector('.fsb__bundle-details-wrapper .icon-caret'), false);
      return;
    }
    // Setup accordion for bundle items wrapper
    const bundleItemsWrapper = this.querySelector('.fsb__bundle-details-wrapper');
    if (bundleItemsWrapper) {
      const bundleItemsContainer = this.querySelector('.bundle-items-container');
      if (bundleItemsContainer) {
        bundleItemsWrapper.addEventListener('click', (e) => {
          if (!this.hasAddons) return; // Guard
          if (e.target.closest('button:not(.accordion-toggle), a, select, input')) return;
          this.toggleAccordion(bundleItemsWrapper, bundleItemsContainer);
        });
      }
    }
    // Setup accordion for add-on items container
    const addonHeaderContainer = this.querySelector('.addon-items-header');
    if (addonHeaderContainer) {
      const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
      if (addonItemsContainer) {
        addonHeaderContainer.addEventListener('click', (e) => {
          if (!this.hasAddons) return; // Guard
          if (e.target.closest('button:not(.accordion-toggle), a, select, input')) return;
          this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
        });
      }
    }
    this.updateAddonsVisibility();
  }

  /**
   * Toggles the accordion with animation effects
   * Used for user interactions
   * @param {HTMLElement} header - The accordion header element
   * @param {HTMLElement} content - The accordion content container
   */
  toggleAccordion(header, content) {
    if (!this.hasAddons && !this.isMobile) return;

    // Toggle expanded class
    const isExpanded = !content.classList.contains('expanded');
    content.classList.toggle('expanded', isExpanded);

    // Update icon rotation
    const icon = header.querySelector('.icon-caret');
    if (icon) {
      icon.classList.toggle('icon-caret--rotated', isExpanded);
    }

    // Apply height transition
    if (isExpanded) {
      // Expanding animation
      const contentHeight = content.scrollHeight;
      content.style.height = '0px';
      content.offsetHeight; // Force reflow
      content.style.height = `${contentHeight}px`;

      // Reset to auto after animation completes
      content.addEventListener(
        'transitionend',
        () => {
          content.style.height = 'auto';
        },
        { once: true }
      );
    } else {
      // Collapsing animation
      const currentHeight = content.scrollHeight;
      content.style.height = `${currentHeight}px`;
      content.offsetHeight; // Force reflow
      content.style.height = '0px';

      // Add proper cleanup after collapse animation completes
      content.addEventListener(
        'transitionend',
        () => {
          // Keep height at 0px but remove inline style if no longer needed
          if (!content.classList.contains('expanded')) {
            content.style.height = '0px';
          }
        },
        { once: true }
      );
    }
  }

  /**
   * Updates the visibility of add-on related elements based on add-on availability
   */
  updateAddonsVisibility() {
    // Use helper for caret and container
    const hasAddons = this.hasAddons;
    this.setCaretState(this.querySelector('.fsb__bundle-details-wrapper .icon-caret'), hasAddons);
    if (this.addOnItemsSidebarContainer) {
      this.addOnItemsSidebarContainer.classList.toggle('hidden', !hasAddons);
    }
  }
  updateSelectionCounts() {
    // Update products count
    this.productsCountElements.forEach((element) => {
      if (element) {
        const selectedProducts = this.bundleState.currentQuantity;
        const maxProducts = this.bundleState.bundleQuantity || 0;
        element.textContent = `${selectedProducts}/${maxProducts} ${window.fsbSettings.bundle_selection_count_text}`;
      }
    });

    // Update add-ons count
    this.addonsCountElements.forEach((element) => {
      if (element) {
        const selectedAddons = this.bundleState.addOnQuantity;
        const minAddons = this.bundleState.addOnLimits.minimum;
        const maxAddons = this.bundleState.addOnLimits.maximum;

        let addonText = '';
        if (minAddons === maxAddons) {
          // Fixed quantity case
          if (minAddons === 1) {
            addonText = window.fsbSettings.addons_text.replace('[qty]', minAddons).replace('add-ons', 'add-on');
          } else {
            addonText = window.fsbSettings.addons_text.replace('[qty]', minAddons);
          }
        } else {
          // Range case
          addonText = window.fsbSettings.addons_text_different.replace('[min]', minAddons).replace('[max]', maxAddons);
        }

        element.textContent = addonText.replace('[selected]', selectedAddons);
      }
    });
  }

  addEventListeners() {
    // Bind methods once
    this.continueToAddOns = this.continueToAddOns.bind(this);
    this.backToProducts = this.backToProducts.bind(this);
    this.handleRandomize = this.handleRandomize.bind(this);
    this.handleReset = this.handleReset.bind(this);

    // Add listeners to all instances of each button
    this.addToCartButtons.forEach((button) => {
      button.addEventListener('click', () => this.addBundleToCart());
    });

    this.continueButtons.forEach((button) => {
      button.addEventListener('click', this.continueToAddOns);
    });

    this.backButtons.forEach((button) => {
      button.addEventListener('click', this.backToProducts);
    });

    console.log('Reset buttons found:', this.resetButtons.length);
    this.resetButtons.forEach((button) => {
      console.log('Adding event listener to reset button:', button);
      button.addEventListener('click', this.handleReset);
    });

    this.randomizeButtons.forEach((button) => {
      button.addEventListener('click', this.handleRandomize);
    });

    this.initializeAccordions();
  }

  updateAddonsContainerInfo() {
    const addonsSidebarContainer = this.addOnItemsSidebarContainer;
    if (addonsSidebarContainer) {
      addonsSidebarContainer.classList.toggle('hidden', !this.hasAddons);
    }
  }

  updateItemsDisplay() {
    this.updateBundleItems();
    this.updateAddonItems();
  }

  updateBundleItems() {
    try {
      const currentItems = this.itemsContainer.querySelectorAll('.bundle-item:not([data-bundle-item-template])');
      const currentItemsMap = new Map();

      // Create a map of existing DOM elements by item key
      currentItems.forEach((item) => {
        const itemKey = item.getAttribute('data-item-key');
        if (itemKey) currentItemsMap.set(itemKey, item);
      });

      // Track keys that should exist after update
      const updatedItemKeys = new Set();

      // Update existing items or add new ones
      Object.entries(this.bundleState.bundleItems).forEach(([itemKey, itemData]) => {
        updatedItemKeys.add(itemKey);
        const { variantId, title, variantTitle, image, price, quantity } = itemData;
        const variantData = { id: variantId, title: variantTitle, media_link: image, bundlePrice: price };
        const productData = { title, featured_image: image };

        if (currentItemsMap.has(itemKey)) {
          // Update existing item
          this.updateItemInDOM(currentItemsMap.get(itemKey), itemData);
        } else {
          // Add new item
          this.addItemToDOM(itemKey, variantData, productData, this.itemsContainer);
        }
      });

      // Remove items that no longer exist in the state
      currentItemsMap.forEach((element, key) => {
        if (!updatedItemKeys.has(key)) {
          element.remove();
        }
      });

      // Add placeholder if bundle is empty
      if (Object.keys(this.bundleState.bundleItems).length === 0) {
        const existingPlaceholder = this.itemsContainer.querySelector('.items-placeholder');
        if (existingPlaceholder) existingPlaceholder.removeAttribute('hidden');
      } else {
        // Hide any existing placeholder if items exist
        const existingPlaceholder = this.itemsContainer.querySelector('.items-placeholder');
        if (existingPlaceholder) existingPlaceholder.setAttribute('hidden', true);
      }
    } catch (error) {
      console.error('Error updating bundle items:', error);
      // Try to show placeholder if there's an error
      try {
        const existingPlaceholder = this.itemsContainer.querySelector('.items-placeholder');
        if (existingPlaceholder) existingPlaceholder.removeAttribute('hidden');
      } catch (fallbackError) {
        console.error('Could not show placeholder after error:', fallbackError);
      }
    }
  }

  updateItemInDOM(itemElement, itemData) {
    if (!itemElement) return;

    // Update quantity
    const qtyElement = itemElement.querySelector('.bundle-item__qty');
    if (qtyElement) {
      qtyElement.textContent = itemData.quantity;
    }

    // Update price if needed
    const priceElement = itemElement.querySelector('.bundle-item__price');
    if (priceElement) {
      priceElement.innerHTML =
        itemData.price === 0 ? window.fsbSettings.free_label || 'Free' : Fsb.formatMoney(itemData.price);
    }

    // Only update other elements if they've changed
    // This avoids unnecessary DOM operations
    const nameElement = itemElement.querySelector('.bundle-item__name');
    if (nameElement && nameElement.textContent !== itemData.title) {
      nameElement.textContent = itemData.title;
    }

    const variantTitleElement = itemElement.querySelector('.bundle-item__variant-title');
    if (variantTitleElement && itemData.variantTitle && variantTitleElement.textContent !== itemData.variantTitle) {
      variantTitleElement.textContent = itemData.variantTitle;
    }

    const imageElement = itemElement.querySelector('.bundle-item__image');
    if (imageElement && imageElement.src !== itemData.image) {
      imageElement.src = itemData.image || imageElement.src;
    }
  }

  updateAddonItems() {
    // Apply to each addon container
    this.forEachAddonContainer((container) => {
      // First remove existing addon items
      const addonItems = container.querySelectorAll('.bundle-item:not([data-bundle-item-template])');
      addonItems.forEach((item) => item.remove());

      // Then add updated items to this container
      Object.entries(this.bundleState.addOnItems).forEach(([variantId, itemData]) => {
        const { title, image, price, personalization, variantTitle } = itemData;
        const variantData = {
          id: variantId,
          media_link: image,
          bundlePrice: price,
          personalization: personalization,
          title: variantTitle || '',
        };
        const productData = { title, featured_image: image };
        this.addItemToDOM(variantId, variantData, productData, container);
      });

      // Add placeholder if bundle is empty
      if (Object.keys(this.bundleState.addOnItems).length === 0) {
        this.addOnItemsContainers.forEach((container) => {
          container.querySelector('.items-placeholder')
            ? container.querySelector('.items-placeholder').removeAttribute('hidden')
            : null;
        });
      } else {
        this.addOnItemsContainers.forEach((container) => {
          container.querySelector('.items-placeholder')
            ? container.querySelector('.items-placeholder').setAttribute('hidden', true)
            : null;
        });
      }
    });
  }

  updateButtonStates() {
    this.updateAddToCartButtonState();
    this.updateContinueButtonState();
    this.updateBackButtonState();
    this.updateResetButtonVisibility();
  }

  /**
   * Updates the visibility of the reset button based on current selections
   */
  updateResetButtonVisibility() {
    if (this.resetButtons.length === 0) return;
    
    // Show reset button only if there are selected items
    const hasSelections = this.bundleState.currentQuantity > 0 || this.bundleState.addOnQuantity > 0;
    
    this.resetButtons.forEach((button) => {
      button.style.display = hasSelections ? 'block' : 'none';
    });
  }

  shouldDisableAddToCart() {
    // Check add-on minimum requirement
    if (
      this.hasAddons &&
      this.bundleState.addOnLimits.maximum > 0 &&
      this.bundleState.addOnQuantity < this.bundleState.addOnLimits.minimum
    )
      return true;
    
    // Check minimum bundle quantity requirement
    const minimumQuantity = parseInt(window.fsbSettings?.minimum_bundle_quantity || 1, 10);
    const currentQuantity = parseInt(this.bundleState.currentQuantity || 0, 10);
    
    if (currentQuantity < minimumQuantity) {
      console.log('Add to Cart disabled: currentQuantity (' + currentQuantity + ') < minimumQuantity (' + minimumQuantity + ')');
      return true;
    }
    
    // With minimum quantity feature, button is enabled once minimum is met
    // No need to require bundle to be at full capacity
    console.log('Add to Cart enabled: minimum quantity requirement met (current: ' + currentQuantity + ', minimum: ' + minimumQuantity + ')');
    return false;
  }

  shouldDisableContinue() {
    return this.bundleState.currentQuantity !== this.bundleState.bundleQuantity;
  }

  updateAddToCartButtonState() {
    if (this.addToCartButtons.length === 0) return;
    const isDisabled = this.shouldDisableAddToCart();
    const shouldHide = this.hasAddons && !this.isInAddOnsSection();

    console.log('updateAddToCartButtonState:', {
      isDisabled,
      currentQuantity: this.bundleState.currentQuantity,
      minimumQuantity: window.fsbSettings?.minimum_bundle_quantity,
      bundleQuantity: this.bundleState.bundleQuantity,
      hasAddons: this.hasAddons,
      addOnQuantity: this.bundleState.addOnQuantity
    });

    this.addToCartButtons.forEach((button) => {
      button.disabled = isDisabled;
      button.classList.toggle('hidden', shouldHide);
    });
  }

  updateContinueButtonState() {
    if (this.continueButtons.length === 0) return;
    const isDisabled = this.shouldDisableContinue();
    const shouldHide = !this.hasAddons || this.isInAddOnsSection();

    this.continueButtons.forEach((button) => {
      button.disabled = isDisabled;
      button.classList.toggle('hidden', shouldHide);
    });

    const shouldHideRandomize = this.isInAddOnsSection() || !this.bundleState.checkAllProductsHaveSingleVariant();

    this.randomizeButtons.forEach((button) => {
      button.classList.toggle('hidden', shouldHideRandomize);
    });
  }

  updateBackButtonState() {
    if (this.backButtons.length === 0) return;
    const shouldHide = !this.isInAddOnsSection();

    this.backButtons.forEach((button) => {
      button.classList.toggle('hidden', shouldHide);
    });
  }

  isInAddOnsSection() {
    return this.closest('.fsb__tabs').querySelector('.fsb__addons-container')?.classList.contains('hidden') === false;
  }

  continueToAddOns() {
    if (!this.hasAddons) return;
    const container = this.closest('.fsb__tabs');
    container.querySelector('.fsb__tabs-container')?.classList.add('hidden');
    container.querySelector('.tabs__navigation')?.classList.add('hidden');
    container.querySelector('.fsb__addons-container')?.classList.remove('hidden');
    this.updateButtonStates();
    this.bundleState.currentView = 'addons';
    this.bundleState.notifySubscribers('view_changed');
    container.scrollIntoView({ top: '80px', behavior: 'smooth' });
  }

  backToProducts() {
    const container = this.closest('.fsb__tabs');
    container.querySelector('.fsb__tabs-container')?.classList.remove('hidden');
    container.querySelector('.tabs__navigation')?.classList.remove('hidden');
    container.querySelector('.fsb__addons-container')?.classList.add('hidden');
    this.updateButtonStates();
    container.scrollIntoView({ top: '80px', behavior: 'smooth' });
    this.bundleState.currentView = 'products';
    this.bundleState.notifySubscribers('view_changed');
  }

  updatePriceVisibility(priceStrategy = this.bundleState.priceStrategy.strategy) {
    const tabsContainer = document.querySelector('.fsb__tabs-container');
    if (tabsContainer) {
      if (priceStrategy === 'fixed_pricing') {
        tabsContainer.classList.add('fsb__price-hidden');
      } else {
        tabsContainer.classList.remove('fsb__price-hidden');
      }
    }
  }

  parseBundleSettings() {
    const settingsString = this.getAttribute('data-bundle-settings');
    if (settingsString) {
      try {
        const settings = JSON.parse(settingsString);
        this.addOnLimits = settings.add_on || this.addOnLimits;
        this.priceStrategy = settings.price || this.priceStrategy;
      } catch (error) {
        console.error('Error parsing data-bundle-settings:', error);
      }
    }
  }

  initializeBundleQuantity(bundleOptions) {
    const selectedOption = bundleOptions.querySelector('[selected]');
    if (selectedOption) {
      this.bundleQuantity = parseInt(selectedOption.getAttribute('data-quantity')) || 0;
      this.selectedOption = selectedOption.dataset.optionId;
      const optionSettings = JSON.parse(selectedOption.dataset.option || '{}');
      if (optionSettings.add_on) {
        this.addOnLimits = optionSettings.add_on;
      }
      if (optionSettings.price) {
        this.priceStrategy = optionSettings.price;
      }
    } else {
      console.warn('No selected option found in fsb-bundleoptions.');
    }
  }

  addBundleToCart() {
    if (this.shouldDisableAddToCart()) {
      this.showErrorMessage(
        window.fsbSettings.addon_required_message || 'Please add the minimum required add-ons to continue.'
      );
      return;
    }

    // Ensure we're using the global bundle state reference
    const bundleState = this.bundleState || window.fsSwatchBundleState;
    if (!bundleState) {
      console.error('Bundle state not available for cart submission');
      this.showErrorMessage('Bundle state not initialized. Please refresh the page and try again.');
      return;
    }

    // Verify bundle has items before submitting
    const bundleItemsCount = Object.keys(bundleState.bundleItems || {}).length;
    const addOnItemsCount = Object.keys(bundleState.addOnItems || {}).length;
    
    if (bundleItemsCount === 0 && addOnItemsCount === 0) {
      console.error('Attempted to add empty bundle to cart');
      this.showErrorMessage('Please add items to your bundle before adding to cart.');
      return;
    }

    // Get the selected bundle option variant ID
    this.selectedOption = bundleState.selectedOption || this.dataset.variant;
    
    // Validate that we have a valid variant ID
    if (!this.selectedOption) {
      console.error('No bundle variant ID found. Bundle state:', {
        selectedOption: bundleState.selectedOption,
        datasetVariant: this.dataset.variant,
        bundleState: bundleState
      });
      this.showErrorMessage('Bundle configuration not found. Please refresh the page and try again.');
      return;
    }

    const variantId = parseInt(this.selectedOption, 10);
    if (isNaN(variantId) || variantId <= 0) {
      console.error('Invalid bundle variant ID:', this.selectedOption);
      this.showErrorMessage('Invalid bundle configuration. Please refresh the page and try again.');
      return;
    }

    // Bundle variants don't track inventory, so we skip availability checks
    // Shopify will handle availability validation at the cart level

    const identifier = `${bundleId}_${Date.now()}`;
    this.hideError();

    // Prepare bundle line item properties once
    const bundleLineItemProperties = this.prepareBundleLineItems();
    const selectedItemsProperties = this.prepareSelectedItemsProperties();

    // Double-check that we have items to add
    if (bundleLineItemProperties.length === 0) {
      console.error('No bundle line items prepared for cart submission');
      this.showErrorMessage('No items found in bundle. Please add items and try again.');
      return;
    }

    // Validate that all child variants are still available
    const inventoryValidation = this.validateChildVariantInventory(bundleLineItemProperties);
    if (!inventoryValidation.valid) {
      console.error('Child variant inventory validation failed:', inventoryValidation);
      this.showErrorMessage(
        inventoryValidation.message || 'One or more items in your bundle are no longer available. Please update your selection.'
      );
      return;
    }

    // Log the cart request data for debugging
    console.log('Adding bundle to cart:', {
      variantId: variantId,
      bundleItemsCount: bundleLineItemProperties.length,
      bundleItems: bundleLineItemProperties,
      selectedOption: this.selectedOption,
      bundleStateSelectedOption: bundleState.selectedOption
    });

    // Create cart form data
    const addToCartFormData = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: {
            ...selectedItemsProperties,
            '__foxsell:dynamic_add_on_bundle_id': identifier,
            '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(bundleLineItemProperties),
          },
        },
      ],
      sections: ['cart-drawer', 'cart-icon-bubble'],
    };

    // Add to cart with proper error handling
    this.submitCartRequest(addToCartFormData);
  }
  prepareBundleLineItems() {
    // Ensure we're using the global bundle state reference
    const bundleState = this.bundleState || window.fsSwatchBundleState;
    if (!bundleState) {
      console.error('Bundle state not available in prepareBundleLineItems');
      return [];
    }

    // Safely access bundle items with fallback to empty object
    const bundleItems = bundleState.bundleItems || {};
    const addOnItems = bundleState.addOnItems || {};

    const bundleItemsArray = Object.entries(bundleItems).map(([variantId, itemData]) => {
      if (!itemData || !itemData.variantId) {
        console.warn('Invalid bundle item data:', { variantId, itemData });
        return null;
      }
      return {
        variantId: parseInt(variantId, 10),
        quantity: itemData.quantity || 1,
        type: 'product',
        properties: {},
      };
    }).filter(item => item !== null);

    const addonItemsArray = Object.entries(addOnItems).map(([variantId, itemData]) => {
      if (!itemData || !itemData.variantId) {
        console.warn('Invalid addon item data:', { variantId, itemData });
        return null;
      }
      return {
        variantId: parseInt(variantId, 10),
        quantity: itemData.quantity || 1,
        type: 'addOns',
        properties: {
          [window.fsbSettings.personalization_property_name || 'Gift Message']: itemData.personalization || '',
        },
      };
    }).filter(item => item !== null);

    return [...bundleItemsArray, ...addonItemsArray];
  }

  /**
   * Validates that all child variants in the bundle are still available
   * Since bundle variants don't track inventory, we check the child variants
   * @param {Array} bundleLineItems - Array of bundle line items with variantId and quantity
   * @returns {Object} Validation result with valid flag and optional message
   */
  validateChildVariantInventory(bundleLineItems) {
    if (!bundleLineItems || bundleLineItems.length === 0) {
      return { valid: false, message: 'No items in bundle to validate' };
    }

    const bundleState = this.bundleState || window.fsSwatchBundleState;
    if (!bundleState) {
      console.warn('Bundle state not available for inventory validation');
      return { valid: true }; // Allow if we can't validate
    }

    const unavailableVariants = [];
    const bundleItems = bundleState.bundleItems || {};
    const addOnItems = bundleState.addOnItems || {};

    // Check each variant in the bundle
    for (const lineItem of bundleLineItems) {
      const variantId = lineItem.variantId;
      const requestedQuantity = lineItem.quantity || 1;

      // Find the variant data from product cards
      const allProductCards = document.querySelectorAll('fsb-productcard');
      let variantData = null;

      for (const card of allProductCards) {
        try {
          const productData = JSON.parse(card.dataset.product || '{}');
          if (productData.variants) {
            const variant = productData.variants.find(v => v.id === variantId || String(v.id) === String(variantId));
            if (variant) {
              variantData = variant;
              break;
            }
          }
        } catch (e) {
          // Continue searching
        }
      }

      if (!variantData) {
        console.warn(`Variant data not found for variant ID: ${variantId}`);
        continue; // Skip validation if we can't find the variant
      }

      // Check if variant is available
      if (!variantData.available) {
        unavailableVariants.push({
          variantId,
          title: variantData.title || `Variant ${variantId}`,
          reason: 'not available'
        });
        continue;
      }

      // Check inventory if tracking is enabled
      if (variantData.inventory_management && variantData.inventory_management !== null) {
        const inventoryQuantity = variantData.inventory_quantity || 0;
        const inventoryPolicy = variantData.inventory_policy;

        // If policy is 'deny', check if we have enough inventory
        if (inventoryPolicy === 'deny') {
          // Count how many of this variant are already in the bundle
          let totalInBundle = 0;
          for (const key in bundleItems) {
            const item = bundleItems[key];
            if (item.variantId === variantId || String(item.variantId) === String(variantId)) {
              totalInBundle += item.quantity || 1;
            }
          }
          for (const key in addOnItems) {
            const item = addOnItems[key];
            if (item.variantId === variantId || String(item.variantId) === String(variantId)) {
              totalInBundle += item.quantity || 1;
            }
          }

          if (inventoryQuantity < totalInBundle) {
            unavailableVariants.push({
              variantId,
              title: variantData.title || `Variant ${variantId}`,
              reason: 'insufficient inventory',
              available: inventoryQuantity,
              requested: totalInBundle
            });
          }
        }
      }
    }

    if (unavailableVariants.length > 0) {
      const variantTitles = unavailableVariants.map(v => v.title).join(', ');
      return {
        valid: false,
        message: `The following items are no longer available: ${variantTitles}. Please update your selection.`,
        unavailableVariants
      };
    }

    return { valid: true };
  }

  prepareSelectedItemsProperties() {
    // Ensure we're using the global bundle state reference
    const bundleState = this.bundleState || window.fsSwatchBundleState;
    if (!bundleState) {
      console.error('Bundle state not available in prepareSelectedItemsProperties');
      return {};
    }

    const properties = {};
    const propNameKey = window.fsbSettings.personalization_property_name || 'Gift Message';

    // Safely access bundle items with fallback to empty objects
    const bundleItems = bundleState.bundleItems || {};
    const addOnItems = bundleState.addOnItems || {};

    // Combine bundle and addon items to process in a single loop
    const allItems = [
      ...Object.entries(bundleItems).map(([id, data]) => ({ ...data, isAddon: false })),
      ...Object.entries(addOnItems).map(([id, data]) => ({ ...data, isAddon: true })),
    ];

    // Process all items in one pass
    allItems.forEach((itemData) => {
      const itemName = itemData.title;

      // Update or set quantity string
      if (properties[itemName]) {
        const currentCount = parseInt(properties[itemName].replace(/[^0-9]/g, ''));
        properties[itemName] = `x${currentCount + itemData.quantity}`;
      } else {
        properties[itemName] = `x${itemData.quantity}`;
      }

      // Add personalization as separate property if present
      if (itemData.personalization) {
        const safeKey = `${itemName} - ${propNameKey}`.substring(0, 40);
        properties[safeKey] = itemData.personalization.substring(0, 100);
      }
    });

    return properties;
  }

  submitCartRequest(data) {
    // Log the request data for debugging
    console.log('Submitting cart request:', {
      url: window.Shopify.routes.root + 'cart/add.js',
      data: data,
      variantId: data.items[0]?.id,
      bundleItemsCount: JSON.parse(data.items[0]?.properties['__foxsell:dynamic_add_on_bundle_items'] || '[]').length
    });

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        // Get response text first to handle both success and error cases
        const responseText = await response.text();
        let responseData;
        
        try {
          responseData = JSON.parse(responseText);
        } catch (e) {
          console.error('Failed to parse response as JSON:', responseText);
          throw new Error('Invalid response from server');
        }

        // Log the full response for debugging
        console.log('Cart API response:', {
          status: response.status,
          statusText: response.statusText,
          data: responseData
        });

        // Check for HTTP errors (4xx, 5xx)
        if (!response.ok) {
          const errorMessage = responseData.description || responseData.message || `Server error: ${response.status} ${response.statusText}`;
          console.error('Cart API error:', {
            status: response.status,
            statusText: response.statusText,
            error: errorMessage,
            fullResponse: responseData
          });
          this.handleErrorMessage(errorMessage);
          return;
        }

        // Check for Shopify API errors (status field in response)
        if (responseData.status) {
          this.handleErrorMessage(responseData.description || 'An error occurred while adding to cart.');
          return;
        }

        // Success
        this.showSuccessMessage(window.fsbSettings.added_toast_message || 'Bundle successfully added to cart!');
        this.updateCartUI(responseData);
      })
      .catch((error) => {
        console.error('Error adding bundle to cart:', error);
        this.showErrorMessage(
          window.fsbSettings.cart_error_message || 'An error occurred while adding to cart. Please try again.'
        );
      });
  }

  // 5. Optimize error message handling
  showErrorMessage(message) {
    const errorMessageWrapper = document.querySelector('.fsb__error-message-wrapper');
    if (!errorMessageWrapper) return;

    const errorMessageElement = errorMessageWrapper.querySelector('.fsb__error-message');
    if (errorMessageElement) {
      errorMessageElement.textContent = message;
    }

    errorMessageWrapper.toggleAttribute('hidden', !message);
    console.error('Error:', message);
  }

  handleErrorMessage(errorMessage) {
    // Defensive: older implementations expected submitButton/submitButtonText
    // but this section uses plain buttons. Guard everything to avoid runtime errors.
    this.showErrorMessage(errorMessage);

    const addToCartButton = this.querySelector('.fsb__bundle-add-to-cart');
    const soldOutMessage = addToCartButton?.querySelector?.('.sold-out-message');

    if (addToCartButton) {
      addToCartButton.setAttribute('aria-disabled', true);
    }
    if (soldOutMessage) {
      soldOutMessage.classList.remove('hidden');
    }

    this.error = true;
  }

  showSuccessMessage(message) {
    // Remove any existing toast
    let toast = document.querySelector('.fsb__toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fsb__toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('fsb__toast--visible');

    // Remove after 2.5s
    clearTimeout(this._toastTimeout);
    this._toastTimeout = setTimeout(() => {
      toast.classList.remove('fsb__toast--visible');
    }, 2000);

    // Allow dismiss on click
    toast.onclick = () => {
      toast.classList.remove('fsb__toast--visible');
      clearTimeout(this._toastTimeout);
    };
  }

  hideError() {
    const errorMessageWrapper = document.querySelector('.fsb__error-message-wrapper');
    errorMessageWrapper.setAttribute('hidden', '');
  }

  showErrorMessage(message) {
    const errorMessageWrapper = document.querySelector('.fsb__error-message-wrapper');
    const errorMessageElement = errorMessageWrapper.querySelector('.fsb__error-message');
    errorMessageWrapper.toggleAttribute('hidden', !message);
    if (message) {
      errorMessageElement.textContent = message;
    }
    console.error('Error:', message);
  }

  updateCartUI(response) {
    if (fsbAdditionalSettings?.custom_cart_event) {
      const events = fsbAdditionalSettings.custom_cart_event.split(',').map((event) => event.trim());
      events.forEach((event) => {
        document.dispatchEvent(
          new CustomEvent(event, {
            bubbles: true,
          })
        );
      });
    }

    // Try to open cart drawer first
    const cart = document.querySelector('cart-drawer') || document.querySelector('.js-cart-drawer');
    if (cart) {
      try {
        // Get the section ID from the cart drawer element
        const sectionId = cart.dataset.sectionId || cart.getAttribute('data-section-id');
        
        // Update cart drawer with new contents if sections are available in response
        if (response.sections && sectionId && response.sections[sectionId]) {
          // If sections are in the response, use them directly
          cart.updateFromCartChange(response.sections[sectionId]);
          
          // Update cart icon bubble if present
          if (response.sections && response.sections['cart-icon-bubble']) {
            const cartIconBubble = document.querySelector('[data-section-id*="cart-icon"]');
            if (cartIconBubble && cartIconBubble.updateFromCartChange) {
              cartIconBubble.updateFromCartChange(response.sections['cart-icon-bubble']);
            }
          }
          
          // Remove empty class if present
          if (cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          
          // Open the cart drawer
          cart.open();
          console.log('Cart drawer opened successfully with response sections');
          return;
        } else if (response.sections && response.sections['cart-drawer']) {
          // Fallback: try direct section key
          cart.updateFromCartChange(response.sections['cart-drawer']);
          
          // Update cart icon bubble if present
          if (response.sections && response.sections['cart-icon-bubble']) {
            const cartIconBubble = document.querySelector('[data-section-id*="cart-icon"]');
            if (cartIconBubble && cartIconBubble.updateFromCartChange) {
              cartIconBubble.updateFromCartChange(response.sections['cart-icon-bubble']);
            }
          }
          
          if (cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          
          cart.open();
          console.log('Cart drawer opened successfully with direct section key');
          return;
        }
        
        // If no sections in response (JSON API doesn't return sections), refresh the drawer
        // Dispatch refresh event which will update the cart drawer contents
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        
        // Remove empty class if present
        if (cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
        
        // Wait a brief moment for refresh to start, then open
        setTimeout(() => {
          cart.open();
          console.log('Cart drawer opened after refresh');
        }, 100);
        
        return; // Success - cart drawer will open
      } catch (e) {
        console.error('Cart drawer failed to open:', e);
      }
    } else {
      console.log('Cart drawer element not found');
    }

    // Fallback: redirect to cart page if drawer fails or doesn't exist
    console.log('Falling back to cart page redirect');
    window.location.href = window?.routes?.cart_url || '/cart';
  }

  isAllTabActive() {
    return this.tabs?.querySelector('.tabs__navigation-link.active')?.classList.contains('tabs__navigation-link--all');
  }

  getCurrentTabIndex() {
    const activeTab = this.tabs.querySelector('.tabs__navigation-link.active');
    return Array.from(this.tabs.querySelectorAll('.tabs__navigation-link')).indexOf(activeTab);
  }

  /**
   * Handles click on the randomize button
   * Clears the current bundle and fills it with random products
   */
  handleRandomize() {
    // Display a notification to the user
    Fsb.showToast(window.fsbSettings.randomize_notification);

    // Get all eligible products efficiently
    const availableProducts = this.getRandomizableProducts();

    if (!availableProducts.length) {
      return;
    }

    // Calculate how many more items we need to add
    const targetQuantity = this.bundleState.bundleQuantity;
    const currentQuantity = this.bundleState.currentQuantity;
    const itemsToAdd = Math.max(0, targetQuantity - currentQuantity);

    if (itemsToAdd === 0) {
      return;
    }

    // Randomize the bundle with safety limits
    const addedCount = this.randomizeBundleWithProducts(availableProducts, itemsToAdd);
  }

  /**
   * Gets all products that can be randomized
   * Filters for single-variant products that are available and visible
   * @returns {Array} Array of eligible products
   */
  getRandomizableProducts() {
    // Create a Set to avoid duplicate products
    const productSet = new Set();
    // Cache for variant availability status
    const availabilityCache = new Map();

    // Get active tabs (categories) once
    const activeTabs = document.querySelectorAll('.tabs__content-item.active');

    // Process each active tab
    activeTabs.forEach((tab) => {
      // Only process visible product cards - do this once per tab
      const productCards = tab.querySelectorAll('fsb-productcard:not([hidden]):not([is="fsb-productaddon"])');

      productCards.forEach((card) => {
        try {
          // Check for valid product data
          if (!card.dataset.product) return;

          const productData = JSON.parse(card.dataset.product);
          const variantSelector = card.querySelector('fsb-variantselector');

          // Skip invalid products
          if (!variantSelector || !productData.id) return;

          // Get variants and check if there's only one
          const variants = variantSelector.variants || [];
          if (variants.length !== 1) return;

          const variant = variants[0];
          const cacheKey = `${productData.id}-${variant.id}`;

          // Check cache for availability status
          let isAvailable = availabilityCache.get(cacheKey);

          if (isAvailable === undefined) {
            // Use the card's availability check method
            isAvailable = card.isVariantAvailable && card.isVariantAvailable(variant) === true;
            availabilityCache.set(cacheKey, isAvailable);
          }

          if (isAvailable) {
            // Use the variant ID as a key to avoid duplicates
            if (!productSet.has(variant.id)) {
              productSet.add(variant.id);
              // Store minimal required data
              const product = {
                card,
                product: productData,
                variant: variant,
              };
              // Precompute any necessary properties right away
              product.key = variant.id;
              // Helper function to get the display price (compare_at_price as primary, price as fallback)
              const getDisplayPrice = (variant) => {
                return variant.compare_at_price && variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
              };
              product.bundleData = {
                price: getDisplayPrice(variant),
                title: productData.title,
                variantTitle: !variant.is_default ? variant.title : null,
                image: variant.media_link || productData.featured_image,
                variantId: variant.id,
                productId: productData.id,
              };
              productSet.add(product);
            }
          }
        } catch (e) {
          // Silently handle errors without breaking the flow
        }
      });
    });

    // Convert set to array for further processing
    return Array.from(productSet).filter((item) => typeof item === 'object');
  }

  /**
   * Fills the bundle with randomly selected products
   * @param {Array} availableProducts Array of eligible products
   * @param {Number} itemsToAdd Number of items to add
   * @returns {Number} Number of items actually added
   */
  randomizeBundleWithProducts(availableProducts, itemsToAdd) {
    if (!availableProducts.length || itemsToAdd <= 0) {
      return 0;
    }

    // Shuffle the products efficiently
    const shuffledProducts = this.shuffleArray(availableProducts);

    // Keep track of how many products we've added
    let addedCount = 0;

    // Safety limits to prevent infinite loops
    const maxAttempts = Math.min(availableProducts.length * 3, 100);
    let attempts = 0;

    // Set of variant IDs we've already tried to add
    const attemptedVariants = new Set();

    // Add products until we reach the target or hit safety limits
    while (addedCount < itemsToAdd && attempts < maxAttempts) {
      attempts++;

      // If we've tried all products at least once, get a random one
      const index =
        attempts <= shuffledProducts.length ? attempts - 1 : Math.floor(Math.random() * shuffledProducts.length);

      const product = shuffledProducts[index];

      // Skip if we've already tried this variant
      if (attemptedVariants.has(product.key)) {
        continue;
      }

      // Mark this variant as attempted
      attemptedVariants.add(product.key);

      const success = this.bundleState.addBundleItem(product.key, product.bundleData);

      if (success) {
        addedCount++;

        // If we've added enough items, stop
        if (addedCount >= itemsToAdd) {
          break;
        }
      }
    }

    // Return the number of items we successfully added
    return addedCount;
  }

  /**
   * Shuffles array using Fisher-Yates algorithm
   * @param {Array} array The array to shuffle
   * @returns {Array} New shuffled array
   */
  shuffleArray(array) {
    // Create a copy to avoid modifying the original
    const shuffled = Array.from(array);

    // Fisher-Yates shuffle algorithm
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
  }

  /**
   * Handles click on the reset button
   * Clears all selected items from the bundle
   */
  handleReset() {
    console.log('handleReset called, this.bundleState:', this.bundleState);
    
    // Check if bundle state exists
    if (!this.bundleState) {
      console.error('Bundle state not initialized');
      return;
    }
    
    // Clear all selections using the bundle state method
    this.bundleState.clearAllSelections();
    
    // Update the UI to reflect the cleared state
    this.updateBundleItems();
    this.updateAddToBundleButtons();
    this.updateTotalPrice();
    this.updateButtonStates();
    this.updateResetButtonVisibility();
    
    // Show a notification to the user
    if (window.fsbSettings && window.fsbSettings.reset_notification) {
      Fsb.showToast(window.fsbSettings.reset_notification);
    }
  }

  addItemToDOM(itemKey, variantData, productData, container) {
    const template = this.querySelector('[data-bundle-item-template]');
    if (!template || !container) return;
    const clone = template.cloneNode(true);
    const itemElement = clone.querySelector('.bundle-item');
    itemElement.classList.remove('hidden');
    itemElement.setAttribute('data-item-key', itemKey);
    const imageElement = itemElement.querySelector('.bundle-item__image');
    if (imageElement) {
      if (variantData.media_link || productData.featured_image) {
        imageElement.src = variantData.media_link || productData.featured_image;
        imageElement.alt = productData.title || '';
        imageElement.classList.remove('hidden');
      } else {
        imageElement.classList.add('hidden');
      }
    }
    const nameElement = itemElement.querySelector('.bundle-item__name');
    if (nameElement) {
      nameElement.textContent = productData.title;
    }
    const variantTitleElement = itemElement.querySelector('.bundle-item__variant-title');
    if (variantTitleElement && variantData.title) {
      variantTitleElement.textContent = variantData.title;
    }
    const qtyElement = itemElement.querySelector('.bundle-item__qty');
    if (
      qtyElement &&
      (this.bundleState.bundleItems[itemKey]?.quantity || this.bundleState.addOnItems[itemKey].quantity)
    ) {
      qtyElement.textContent =
        this.bundleState.bundleItems[itemKey]?.quantity || this.bundleState.addOnItems[itemKey]?.quantity;
    }
    const priceElement = itemElement.querySelector('.bundle-item__price');
    if (priceElement) {
      priceElement.innerHTML =
        variantData.bundlePrice === 0
          ? window.fsbSettings.free_label || 'Free'
          : Fsb.formatMoney(variantData.bundlePrice);
    }
    const removeButton = itemElement.querySelector('.bundle-item__remove');
    if (removeButton) {
      const isAddon = container === this.addOnItemsContainers[0] || container === this.addOnItemsContainers[1];

      removeButton.addEventListener('click', () => {
        try {
          // Ensure bundle state is available
          if (!this.bundleState) {
            console.error('Bundle state not available');
            return;
          }

          if (isAddon) {
            this.bundleState.removeAddonItem(itemKey);
            // Update addon items display
            if (typeof this.updateAddonItems === 'function') {
              this.updateAddonItems();
            }
          } else {
            // For bundle items, directly remove the item instead of calling updateQuantityInBundle
            this.bundleState.removeBundleItem(itemKey);
            // Update the UI using the proper update methods
            if (typeof this.updateBundleItems === 'function') {
              this.updateBundleItems();
            }
            if (typeof this.updateAddToBundleButtons === 'function') {
              this.updateAddToBundleButtons();
            }
            if (typeof this.updateTotalPrice === 'function') {
              this.updateTotalPrice();
            }
          }
        } catch (error) {
          console.error('Error removing item from bundle:', error);
          // Fallback: try to update the UI anyway
          try {
            if (typeof this.updateBundleItems === 'function') {
              this.updateBundleItems();
            }
            if (typeof this.updateAddToBundleButtons === 'function') {
              this.updateAddToBundleButtons();
            }
            if (typeof this.updateTotalPrice === 'function') {
              this.updateTotalPrice();
            }
          } catch (fallbackError) {
            console.error('Fallback update also failed:', fallbackError);
          }
        }
      });
    }

    // Check if this is an add-on item with personalization
    if (
      (container === this.addOnItemsContainers[0] || container === this.addOnItemsContainers[1]) &&
      variantData.personalization
    ) {
      if (itemElement) {
        const detailsElement = itemElement.querySelector('.bundle-item__details');

        if (detailsElement) {
          // Add View Message button after the name
          const viewMessageButton = document.createElement('button');
          viewMessageButton.className = 'fsb__button--link';
          viewMessageButton.textContent = `${window.fsbSettings.view_message_button_text}`;
          nameElement.parentNode.insertBefore(viewMessageButton, nameElement.nextSibling);

          // Add click handler to open personalization modal
          viewMessageButton.addEventListener('click', () => {
            const personalizationModal = document.querySelector('fsb-personalization');
            if (personalizationModal) {
              // Create a dummy product card with necessary data
              productData.button = viewMessageButton;
              const dummyProductCard = {
                product: productData,
                bundleState: this.bundleState,
              };

              // Show the modal with existing data
              personalizationModal.showPersonalizationWithData(
                dummyProductCard,
                variantData,
                variantData.personalization,
                itemKey
              );
            }
          });

          const personalizationElement = document.createElement('p');
          personalizationElement.className = 'bundle-item__personalization';
          // Prepend a pencil icon and truncate the message to ~30 characters
          const preview =
            variantData.personalization.length > 30
              ? variantData.personalization.substring(0, 27) + '...'
              : variantData.personalization;
          // Use the merchant-defined property name in the tooltip/title
          personalizationElement.innerHTML = `<span class="personalization-icon">✏️</span> ${preview}`;
          // If truncated, add a tooltip with the full message
          if (variantData.personalization.length > 30) {
            personalizationElement.title = variantData.personalization;
          }
          detailsElement.appendChild(personalizationElement);
        }
      }
    }

    container.appendChild(itemElement);
  }

  removeItemFromDOM(itemKey, container) {
    // If a specific container is provided, remove from just that container
    if (container) {
      const itemToRemove = container.querySelector(`[data-item-key="${itemKey}"]`);
      if (itemToRemove) {
        container.removeChild(itemToRemove);
      }
      return;
    }

    if (this.bundleState.addOnItems[itemKey]) {
      this.forEachAddonContainer((container) => {
        const itemToRemove = container.querySelector(`[data-item-key="${itemKey}"]`);
        if (itemToRemove) {
          container.removeChild(itemToRemove);
        }
      });
    } else {
      // Otherwise, it's a bundle item, remove from bundle container
      const itemToRemove = this.itemsContainer.querySelector(`[data-item-key="${itemKey}"]`);
      if (itemToRemove) {
        this.itemsContainer.removeChild(itemToRemove);
      }
    }
  }

  removeVariantFromBundle(itemKey) {
    const variantRemovedEvent = new CustomEvent('variantRemovedFromBundle', {
      detail: { itemKey },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(variantRemovedEvent);
  }

  removeAddonVariantFromBundle(variantId) {
    const addonVariantRemovedEvent = new CustomEvent('addonVariantRemoved', {
      detail: { variantId },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(addonVariantRemovedEvent);
  }

  updateAddToBundleButtons() {
    const addToBundleButtons = document.querySelectorAll('.fsb__atk-button--add');
    addToBundleButtons.forEach((button) => {
      const productCard = button.closest('fsb-productcard, fsb-productaddon');
      if (productCard && typeof productCard.checkAndUpdateButtonState === 'function') {
        // Pass the button itself instead of this.button which doesn't exist in FsbAside
        productCard.checkAndUpdateButtonState(button);
      }
    });
  }

  removeEventListeners() {
    // Remove event listeners from all button instances
    this.addToCartButtons.forEach((button) => {
      button.removeEventListener('click', this.addBundleToCart);
    });

    this.continueButtons.forEach((button) => {
      button.removeEventListener('click', this.continueToAddOns);
    });

    this.backButtons.forEach((button) => {
      button.removeEventListener('click', this.backToProducts);
    });

    this.randomizeButtons.forEach((button) => {
      button.removeEventListener('click', this.handleRandomize);
    });

    this.resetButtons.forEach((button) => {
      button.removeEventListener('click', this.handleReset);
    });
  }

  removeExcessAddOns(newMaximum = this.bundleState.addOnLimits.maximum) {
    while (this.bundleState.addOnQuantity > newMaximum) {
      const variantIds = Object.keys(this.bundleState.addOnItems);
      if (variantIds.length === 0) break;

      const lastVariantId = variantIds[variantIds.length - 1];
      this.bundleState.removeAddonItem(lastVariantId);
      this.updateAddToBundleButtons();

      // This will now remove from all addon containers
      this.removeItemFromDOM(lastVariantId);
    }
  }

  removeExcessItems() {
    const excessQuantity = this.bundleState.currentQuantity - this.bundleState.bundleQuantity;
    if (excessQuantity <= 0) return;
    const itemKeys = Object.keys(this.bundleState.bundleItems);
    itemKeys.sort((b, a) => this.bundleState.bundleItems[b].addedAt - this.bundleState.bundleItems[a].addedAt);
    let remainingToRemove = excessQuantity;
    let currentKeyIndex = itemKeys.length - 1;
    while (remainingToRemove > 0 && currentKeyIndex >= 0) {
      const itemKey = itemKeys[currentKeyIndex];
      const item = this.bundleState.bundleItems[itemKey];
      const removeFromThis = Math.min(remainingToRemove, item.quantity);
      for (let i = 0; i < removeFromThis; i++) {
        this.bundleState.removeBundleItem(itemKey);
      }
      remainingToRemove -= removeFromThis;
      currentKeyIndex--;
    }
    this.updateAddToBundleButtons();
    this.updateTotalPrice();
    this.updateButtonStates();
  }

  updateTotalPrice() {
    try {
      if (!this.bundleState) {
        console.error('Bundle state not available for price update');
        return;
      }

      let bundleItemsTotal = 0;
      let addOnItemsTotal = 0;
      
      Object.values(this.bundleState.bundleItems).forEach((item) => {
        if (item && typeof item.price === 'number' && typeof item.quantity === 'number') {
          bundleItemsTotal += item.price * item.quantity;
        }
      });
      
      Object.values(this.bundleState.addOnItems).forEach((item) => {
        if (item && typeof item?.price === 'number' && typeof item?.quantity === 'number') {
          addOnItemsTotal += item.price * item.quantity;
        }
      });

      // For dynamic pricing strategies that might need recalculation
      if (this.bundleState.priceStrategy.strategy === 'fixed_pricing') {
        const finalPrice = this.bundleState.priceStrategy.value * 100 + addOnItemsTotal;
        this.updateBundlePrice(finalPrice);
      } else if (this.bundleState.priceStrategy.strategy === 'dynamic_pricing') {
        const discountPercentage = this.bundleState.priceStrategy.value / 100;
        const originalPrice = bundleItemsTotal + addOnItemsTotal;
        const discountAmount = bundleItemsTotal * discountPercentage;
        const finalPrice = originalPrice - discountAmount;
        this.updateBundlePrice(finalPrice, originalPrice);
      } else {
        this.updateBundlePrice(bundleItemsTotal + addOnItemsTotal);
      }
    } catch (error) {
      console.error('Error updating total price:', error);
    }
  }

  updateBundlePrice(finalPrice, originalPrice = null) {
    try {
      if (!this.bundlePriceElement) {
        console.warn('Bundle price element not found');
        return;
      }
      
      if (typeof finalPrice !== 'number' || isNaN(finalPrice)) {
        console.error('Invalid final price:', finalPrice);
        return;
      }

      if (originalPrice) {
        if (typeof originalPrice !== 'number' || isNaN(originalPrice)) {
          console.error('Invalid original price:', originalPrice);
          return;
        }
        
        this.bundlePriceElement.innerHTML = `
          <span class="original-price">${Fsb.formatMoney(originalPrice)}</span>
          <span class="discounted-price">${Fsb.formatMoney(finalPrice)}</span>
        `;
        this.bundlePriceElement.classList.add('has-discount');
      } else {
        this.bundlePriceElement.innerHTML = Fsb.formatMoney(finalPrice);
        this.bundlePriceElement.classList.remove('has-discount');
      }
    } catch (error) {
      console.error('Error updating bundle price:', error);
    }
  }

  /**
   * Initialize accordion states based on initial content
   */
  initializeAccordionStates() {
    const bundleContainer = this.querySelector('.bundle-items-container');
    const bundleCaret = this.querySelector('.fsb__bundle-details-wrapper .icon-caret');
    const addonContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
    const addonCaret = this.querySelector('.addon-items-header .icon-caret');

    const inProductsView = this.bundleState.currentView === 'products';
    const inAddonsView = this.bundleState.currentView === 'addons';

    if (!this.hasAddons) {
      // Without add-ons: always expand bundle items and hide accordions on desktop
      this.setAccordionState(bundleContainer, true);
      this.setCaretState(bundleCaret, false);

      // Hide add-on elements
      this.setAccordionState(addonContainer, false);
      this.setCaretState(addonCaret, false);
    } else {
      // With add-ons: set initial states based on current view
      this.setAccordionState(bundleContainer, inProductsView);
      this.setCaretState(bundleCaret, true);

      this.setAccordionState(addonContainer, inAddonsView);
      this.setCaretState(addonCaret, true);
    }

    this.updateAddonsVisibility();
  }

  /**
   * Updates accordion states based on content changes
   */
  updateAccordionStates() {
    // If bundle items container is expanded, update its height
    const bundleItemsContainer = this.querySelector('.bundle-items-container');
    if (bundleItemsContainer && bundleItemsContainer.classList.contains('expanded')) {
      // Allow height to adjust to new content
      bundleItemsContainer.style.height = 'auto';
      const newHeight = bundleItemsContainer.scrollHeight;
      bundleItemsContainer.style.height = `${newHeight}px`;
    }

    // If addon items container is expanded, update its height
    const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
    if (addonItemsContainer && addonItemsContainer.classList.contains('expanded')) {
      // Allow height to adjust to new content
      addonItemsContainer.style.height = 'auto';
      const newHeight = addonItemsContainer.scrollHeight;
      addonItemsContainer.style.height = `${newHeight}px`;
    }
  }

  /**
   * Updates accordions based on current view
   */
  updateAccordionsBasedOnView() {
    const currentView = this.bundleState.currentView;
    const bundleItemsContainer = this.querySelector('.bundle-items-container');
    const bundleDetailsWrapper = this.querySelector('.fsb__bundle-details-wrapper');
    const addonItemsContainer = this.querySelector('.addon-items-container.fsb__desktop-only');
    const addonHeaderContainer = this.querySelector('.addon-items-header');

    if (!bundleItemsContainer || !bundleDetailsWrapper || !addonItemsContainer || !addonHeaderContainer) {
      return;
    }

    if (currentView === 'products') {
      // Expand bundle items if not already expanded
      if (!bundleItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(bundleDetailsWrapper, bundleItemsContainer);
      }

      // Collapse addon items if expanded
      if (addonItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
      }
    } else if (currentView === 'addons') {
      // Expand addon items if not already expanded
      if (!addonItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(addonHeaderContainer, addonItemsContainer);
      }

      // Collapse bundle items if expanded
      if (bundleItemsContainer.classList.contains('expanded')) {
        this.toggleAccordion(bundleDetailsWrapper, bundleItemsContainer);
      }
    }
  }
}

customElements.define('fsb-aside', FsbAside);

/**
 * Popover UI component
 * Handles dropdown menus and tooltips
 */
class FsbPopover extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.trigger = null;
    this.content = null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('.fsb__popover-trigger');
    this.content = this.querySelector('.fsb__popover-content');
    if (!this.trigger || !this.content) {
      console.error('FsbPopover: Missing trigger or content element');
      return;
    }
    this.trigger.addEventListener('click', () => this.toggle());
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.content.classList.add('fsb__popover-content--active');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.isOpen = false;
    this.content.classList.remove('fsb__popover-content--active');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  handleOutsideClick(event) {
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }
}

customElements.define('fsb-popover', FsbPopover);

/**
 * Base modal dialog component
 * Handles basic modal functionality
 */
class FsbModalDialog extends HTMLElement {
  constructor() {
    super();
    this.closer = this.querySelector('[id^="ModalClose"]');
    this.content = this.querySelector('.fsb__modal-content');
    this.openedBy = null;
    this.previouslyFocusedElement = null;

    // Bind methods
    this.handleKeyup = this.handleKeyup.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.hide = this.hide.bind(this);
  }

  connectedCallback() {
    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    this.closer?.addEventListener('click', this.hide);
    this.addEventListener('keyup', this.handleKeyup);
    this.addEventListener('click', this.handleOutsideClick);
  }

  removeEventListeners() {
    this.closer?.removeEventListener('click', this.hide);
    this.removeEventListener('keyup', this.handleKeyup);
    this.removeEventListener('click', this.handleOutsideClick);
  }

  handleKeyup(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.hide();
  }

  handleOutsideClick(event) {
    if (event.target === this) this.hide();
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.previouslyFocusedElement = document.activeElement;

    this.setAttribute('open', '');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');

    this.setAttribute('tabindex', '-1');
    this.focus();

    queueMicrotask(() => {
      this.content.scrollTop = 0;
      this.trapFocus();
    });
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    this.restoreFocus();
    this.cleanup();
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length) {
      this.firstFocusable = focusableElements[0];
      this.lastFocusable = focusableElements[focusableElements.length - 1];
      this.firstFocusable.focus();
      this.addEventListener('keydown', this.handleTabKey);
    }
  }

  handleTabKey(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }

  restoreFocus() {
    if (this.openedBy) {
      this.openedBy.button?.focus();
    }
  }

  cleanup() {
    this.content.innerHTML = '';
    this.removeEventListener('keydown', this.handleTabKey);
    this.removeAttribute('role');
    this.removeAttribute('aria-modal');
    this.firstFocusable = null;
    this.lastFocusable = null;
  }
}

customElements.define('fsb-modaldialog', FsbModalDialog);

/**
 * Product modal dialog component
 * Extends base modal with product-specific functionality
 */
class FsbProductModal extends FsbModalDialog {
  constructor() {
    super();
    this.currentProduct = null;
    this.variantSelector = null;
    this.mediaGallery = null;
    this.productCard = null;
    this.buttonObserver = null;
    this.variantObserver = null;

    // Bind additional methods
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.addEventListener('variantChange', this.handleVariantChange);
    this.handleProductImageClick = this.handleProductImageClick.bind(this);
    this.handleProductImageKeydown = this.handleProductImageKeydown.bind(this);
    this.delegatedClickHandler = this.delegatedClickHandler.bind(this);
    this.delegatedKeydownHandler = this.delegatedKeydownHandler.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setupProductImageListeners();

    const productContainer = document.querySelector('.fsb__tabs');
    if (productContainer) {
      productContainer.addEventListener('click', this.delegatedClickHandler);
      productContainer.addEventListener('keydown', this.delegatedKeydownHandler);
    }
    // Add MutationObserver for debugging DOM changes
    if (!this._debugObserver) {
      this._debugObserver = new MutationObserver((mutations) => {
        console.log('Modal variant selector mutated:', mutations, 'in', this, 'with data-debug:', this.getAttribute('data-debug'));
      });
      this._debugObserver.observe(this, { childList: true, subtree: true });
    }
    // Add click event to swatch labels to manually check radios in modal
    this.querySelectorAll('.color-swatch').forEach(label => {
      label.addEventListener('click', (e) => {
        const radioId = label.getAttribute('for');
        const radio = this.querySelector(`#${radioId}`);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          console.log('Manually checked radio in modal:', radio);
        }
      });
    });
  }

  delegatedClickHandler(event) {
    const productImage = event.target.closest('.fsb__category-grid__product-card .fsb__product-card__image');
    if (productImage) {
      // Use the original handler logic with the found element
      this.handleProductImageClick(event, productImage);
    }
  }

  delegatedKeydownHandler(event) {
    const productImage = event.target.closest('.fsb__category-grid__product-card .fsb__product-card__image');
    if (productImage && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.handleProductImageClick(event, productImage);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleProductImageClick);
  }

  setupProductImageListeners() {
    document.querySelectorAll('.fsb__category-grid__product-card .fsb__product-card__image').forEach((image) => {
      image.removeEventListener('click', this.handleProductImageClick);
      image.addEventListener('click', this.handleProductImageClick);

      // Make image focusable with keyboard
      if (!image.hasAttribute('tabindex')) {
        image.setAttribute('tabindex', '0');
      }

      // Add keyboard event listener
      image.removeEventListener('keydown', this.handleProductImageKeydown);
      image.addEventListener('keydown', this.handleProductImageKeydown);
    });
  }

  removeProductImageListeners() {
    document.querySelectorAll('.fsb__category-grid__product-card .fsb__product-card__image').forEach((image) => {
      image.removeEventListener('click', this.handleProductImageClick);
      image.removeEventListener('keydown', this.handleProductImageKeydown);
    });
  }

  handleProductImageClick(event, productImage = event.currentTarget) {
    event.preventDefault();
    event.stopPropagation();

    if (!productImage) return;

    const productCard = productImage.closest('.fsb__category-grid__product-card');
    if (!productCard) return;

    if (event.target.closest('fsb-variantselector') || event.target.closest('.fsb__atk-button')) {
      return;
    }

    this.showProduct(productImage);
  }

  showProduct(opener) {
    this.productCard = opener.closest('.fsb__category-grid__product-card');

    try {
      // Get the raw product data and clean it before parsing
      const rawProductData = this.productCard.dataset.product;
      if (!rawProductData) {
        throw new Error('No product data found in dataset');
      }
      
      // Log the raw JSON data for debugging
      console.log('Raw JSON data:', rawProductData);
      console.log('JSON length:', rawProductData.length);
      console.log('First 100 characters:', rawProductData.substring(0, 100));
      console.log('Last 100 characters:', rawProductData.substring(rawProductData.length - 100));
      
      // Simple cleaning - just remove problematic whitespace
      let cleanedProductData = rawProductData
        .replace(/\r\n/g, ' ')  // Replace Windows line endings
        .replace(/\n/g, ' ')    // Replace Unix line endings
        .replace(/\r/g, ' ')    // Replace Mac line endings
        .replace(/\t/g, ' ')    // Replace tabs
        .replace(/\s+/g, ' ')   // Replace multiple spaces with single space
        .trim();
      
      console.log('Cleaned JSON data:', cleanedProductData);
      
      // Check if JSON appears to be truncated (doesn't end with })
      if (!cleanedProductData.trim().endsWith('}')) {
        console.warn('JSON appears to be truncated, attempting to fix...');
        
        // Try to fix truncated JSON by adding missing closing braces
        let fixedJson = cleanedProductData.trim();
        
        // Count opening and closing braces
        const openBraces = (fixedJson.match(/\{/g) || []).length;
        const closeBraces = (fixedJson.match(/\}/g) || []).length;
        
        console.log('Open braces:', openBraces, 'Close braces:', closeBraces);
        
        // If we're missing closing braces, add them
        if (openBraces > closeBraces) {
          // Find the last complete property and close the JSON properly
          const lastCompleteProperty = fixedJson.lastIndexOf('",');
          console.log('Last complete property at position:', lastCompleteProperty);
          
          if (lastCompleteProperty > 0) {
            fixedJson = fixedJson.substring(0, lastCompleteProperty + 1);
            // Add missing closing braces
            for (let i = 0; i < openBraces - closeBraces; i++) {
              fixedJson += '}';
            }
            console.log('Fixed JSON:', fixedJson);
            cleanedProductData = fixedJson;
          } else {
            // If we can't find a complete property, try a different approach
            console.log('No complete property found, trying alternative fix...');
            
            // Find the last quote and close the string, then add missing braces
            const lastQuote = fixedJson.lastIndexOf('"');
            if (lastQuote > 0) {
              fixedJson = fixedJson.substring(0, lastQuote + 1);
              // Add missing closing braces
              for (let i = 0; i < openBraces - closeBraces; i++) {
                fixedJson += '}';
              }
              console.log('Alternative fixed JSON:', fixedJson);
              cleanedProductData = fixedJson;
            }
          }
        }
      }
      
      try {
        this.currentProduct = JSON.parse(cleanedProductData);
      } catch (parseError) {
        console.log('JSON parse still failed, trying emergency fix...');
        
        // Emergency fix: try to create a minimal valid JSON
        try {
          // Extract what we can from the truncated JSON
          const idMatch = cleanedProductData.match(/"id":\s*(\d+)/);
          const titleMatch = cleanedProductData.match(/"title":\s*"([^"]*)"/);
          const handleMatch = cleanedProductData.match(/"handle":\s*"([^"]*)"/);
          const vendorMatch = cleanedProductData.match(/"vendor":\s*"([^"]*)"/);
          
          if (idMatch && titleMatch && handleMatch && vendorMatch) {
            // Create a minimal valid JSON with the data we can extract
            const emergencyJson = {
              id: parseInt(idMatch[1]),
              title: titleMatch[1],
              handle: handleMatch[1],
              vendor: vendorMatch[1],
              description: "Product description not available",
              featured_image: null,
              featured_media: null,
              media: [],
              options_with_values: [],
              variants: [],
              selected_or_first_available_variant: null,
              has_only_default_variant: true
            };
            
            console.log('Using emergency JSON:', emergencyJson);
            this.currentProduct = emergencyJson;
          } else {
            throw new Error('Could not extract basic product information');
          }
        } catch (emergencyError) {
          console.error('Emergency fix also failed:', emergencyError);
          throw parseError; // Re-throw the original error
        }
      }
      
      // Use data-variant-id if present to set the initial variant
      const variantId = this.productCard.getAttribute('data-variant-id');
      if (variantId && this.currentProduct.variants) {
        const initialVariant = this.currentProduct.variants.find(v => String(v.id) === String(variantId));
        if (initialVariant) {
          this.currentProduct.selected_or_first_available_variant = initialVariant;
        }
      }
      
      // Ensure we have a fallback variant even for out-of-stock items
      if (!this.currentProduct.selected_or_first_available_variant && this.currentProduct.variants && this.currentProduct.variants.length > 0) {
        this.currentProduct.selected_or_first_available_variant = this.currentProduct.variants[0];
      }
      
      super.show(this.productCard);
      this.setupModalContent();
      this.setupStateSynchronization();
    } catch (error) {
      console.error('Error setting up product modal:', error);
      // Log additional debugging information
      const rawData = this.productCard?.dataset?.product || '';
      
      console.log('Debug info:', {
        productCard: this.productCard,
        currentProduct: this.currentProduct,
        variantSelector: this.productCard?.querySelector('fsb-variantselector'),
        currentVariant: this.productCard?.querySelector('fsb-variantselector')?.currentVariant,
        rawProductDataLength: rawData.length,
        fullRawData: rawData, // Log the full raw data for inspection
        error: error.message
      });
      
      // Show a user-friendly error message
      this.showErrorMessage('Unable to load product details. Please try again.');
      this.hide();
    }
  }

  showErrorMessage(message) {
    // Create a simple error message display
    const errorDiv = document.createElement('div');
    errorDiv.className = 'fsb__modal-error';
    errorDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #fff;
      padding: 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      max-width: 400px;
      text-align: center;
    `;
    errorDiv.innerHTML = `
      <p style="margin: 0 0 15px 0; color: #d32f2f;">${message}</p>
      <button onclick="this.parentElement.remove()" style="
        background: #1976d2;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
      ">OK</button>
    `;
    document.body.appendChild(errorDiv);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentElement) {
        errorDiv.remove();
      }
    }, 5000);
  }

  setupModalContent() {
    if (!this.content || !this.currentProduct) return;

    const modalHTML = this.createModalHTML();
    this.content.innerHTML = modalHTML;

    this.setupMediaGallery();
    this.setupVariantSelector();
    this.setupProductCarousel();
  }

  createModalHTML() {
    const { title, description, metafields } = this.currentProduct;
    
    // Safely get the current variant and price, with fallbacks for out-of-stock items
    let currentVariant = null;
    let price = null;
    
    // Try to get the current variant from the variant selector
    if (this.productCard.variantSelector && this.productCard.variantSelector.currentVariant) {
      currentVariant = this.productCard.variantSelector.currentVariant;
      price = currentVariant.compare_at_price && currentVariant.compare_at_price > 0 ? currentVariant.compare_at_price : currentVariant.price;
    }
    
    // If no current variant, try to get it from the product data
    if (!currentVariant && this.currentProduct.variants && this.currentProduct.variants.length > 0) {
      // Use the first variant as fallback, or the selected variant if available
      currentVariant = this.currentProduct.selected_or_first_available_variant || this.currentProduct.variants[0];
      price = currentVariant.compare_at_price && currentVariant.compare_at_price > 0 ? currentVariant.compare_at_price : currentVariant.price;
    }
    
    // Final fallback to product data
    if (!price && this.currentProduct.price) {
      price = this.currentProduct.price;
    }

    const materials = metafields?.custom?.pim_materials;
    const productCare = metafields?.custom?.product_care;

    return `
      <div class="fsb__modal-layout">
        <div class="fsb__modal-media" id="modal-media"></div>
        <div class="fsb__modal-details">
          <h2 class="fsb__modal-title">${title || 'Product Details'}</h2>
          <div class="fsb__modal-price">${price ? Fsb.formatMoney(price) : 'Price not available'}</div>
          <div class="fsb__modal-variant-container fsb__category-grid__product-card"></div>
          <div class="fsb__modal-description">${description || 'No description available'}</div>
          ${materials ? `<div class='fsb__modal-section'><h3>Materials</h3><div>${materials}</div></div>` : ''}
          ${productCare ? `<div class='fsb__modal-section'><h3>Product Care</h3><div>${productCare}</div></div>` : ''}
          <div class="fsb__modal-products-section">
            <h3 class="fsb__modal-products-title">Available Products:</h3>
            <div class="fsb__modal-products-carousel" id="fsb-modal-products-carousel"></div>
          </div>
        </div>
      </div>
    `;
  }

  setupStateSynchronization() {
    const originalSelector = this.productCard.querySelector('fsb-variantselector');
    const modalSelector = this.content.querySelector('fsb-variantselector');

    // Only set up variant sync if both selectors are available
    if (originalSelector && modalSelector) {
      this.setupVariantSync(originalSelector, modalSelector);
    } else {
      console.log('Variant selector not available for sync - this is normal for out-of-stock items');
    }
  }

  setupVariantSync(originalSelector, modalSelector) {
    this.syncVariantState(originalSelector, modalSelector);

    modalSelector.addEventListener('change', (event) => {
      const { name, value } = event.target;
      const originalSelect = originalSelector.querySelector(`select[name="${name}"]`);
      if (originalSelect && originalSelect.value !== value) {
        originalSelect.value = value;
        originalSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    this.variantObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const originalSelect = mutation.target;
          const modalSelect = modalSelector.querySelector(`select[name="${originalSelect.name}"]`);
          if (modalSelect && modalSelect.value !== originalSelect.value) {
            modalSelect.value = originalSelect.value;
            modalSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });

    originalSelector.querySelectorAll('select').forEach((select) => {
      this.variantObserver.observe(select, {
        attributes: true,
        attributeFilter: ['value'],
      });
    });
  }

  syncVariantState(originalSelector, modalSelector) {
    modalSelector.querySelectorAll('select').forEach((select) => {
      const originalSelect = originalSelector.querySelector(`select[name="${select.name}"]`);
      if (originalSelect) {
        select.value = originalSelect.value;
      }
    });

    modalSelector.querySelectorAll('input[type="radio"]').forEach((input) => {
      const originalInput = originalSelector.querySelector(
        `input[type="radio"][name="${input.name}"][value="${input.value}"]`
      );
      if (originalInput) {
        input.checked = originalInput.checked;
      }
    });
    // --- Add: Update swatch selected class in modal ---
    if (typeof modalSelector.updateSwatchSelectedClasses === 'function') {
      modalSelector.updateSwatchSelectedClasses();
    }
    // --- End add ---
  }

  setupButtonSync(originalButtons, modalButtons) {
    const syncButtonStates = () => {
      modalButtons.forEach((modalButton, index) => {
        const originalButton = originalButtons[index];
        if (originalButton) {
          modalButton.disabled = originalButton.disabled;
          modalButton.classList = originalButton.classList;
          modalButton.textContent = originalButton.textContent;
          modalButton.dataset.available = originalButton.dataset.available;
        }
      });
    };

    syncButtonStates();

    this.buttonObserver = new MutationObserver(syncButtonStates);

    originalButtons.forEach((button) => {
      this.buttonObserver.observe(button, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'data-available'],
        childList: true,
        characterData: true,
      });
    });

    modalButtons.forEach((modalButton, index) => {
      modalButton.addEventListener('click', () => {
        const originalButton = originalButtons[index];
        if (originalButton && !originalButton.disabled) {
          originalButton.click();
          this.hide();
        }
      });
    });
  }

  handleVariantChange(event) {
    const { variant } = event.detail;
    if (!variant) return;

    console.log('Modal variant change:', variant.title);

    const priceElement = this.content.querySelector('.fsb__modal-price');
    if (priceElement) {
      // Update price display - use compare_at_price as primary, price as fallback
      const displayPrice = variant.compare_at_price && variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      if (displayPrice) {
        priceElement.innerHTML = Fsb.formatMoney(displayPrice);
      }
    }

    // Update the gallery with filtered media based on the selected variant
    if (this.mediaGallery && this.currentProduct) {
      // Debounce gallery updates to prevent multiple rapid updates
      if (this.galleryUpdateTimeout) {
        clearTimeout(this.galleryUpdateTimeout);
      }
      
      this.galleryUpdateTimeout = setTimeout(() => {
        console.log('Updating modal gallery for variant:', variant.title);
        const filteredMedia = FsbProductGallery.filterMediaForVariant(
          this.currentProduct.media || [],
          variant,
          this.currentProduct.title
        );
        
        this.mediaGallery.updateGalleryForVariant(filteredMedia, variant.title);
      }, 100); // 100ms debounce
    }

    // DO NOT re-render the modal or variant selector here!
  }

  setupMediaGallery() {
    const mediaContainer = this.content.querySelector('#modal-media');
    if (!mediaContainer) return;

    console.log('Setting up modal media gallery for product:', this.currentProduct.title);

    // Get the current variant from the product card if available
    const productCard = this.openedBy?.closest('.fsb__category-grid__product-card');
    const variantSelector = productCard?.querySelector('fsb-variantselector');
    let currentVariant = null;
    
    // Try to get the current variant from the selector
    if (variantSelector) {
      currentVariant = variantSelector.currentVariant || 
                      variantSelector.selectedVariant || 
                      variantSelector.getAttribute('data-current-variant');
      
      // If it's a string, try to parse it
      if (typeof currentVariant === 'string') {
        try {
          currentVariant = JSON.parse(currentVariant);
        } catch (e) {
          console.warn('Could not parse current variant:', currentVariant);
          currentVariant = null;
        }
      }
    }
    
    console.log('Current variant from product card:', currentVariant?.title);
    
    // Filter media based on current variant
    let initialMedia = this.currentProduct.media || [];
    if (currentVariant && this.currentProduct.title) {
      console.log('Filtering initial media for variant:', currentVariant.title);
      initialMedia = FsbProductGallery.filterMediaForVariant(
        this.currentProduct.media || [],
        currentVariant,
        this.currentProduct.title
      );
    }

    console.log('Initial media count:', initialMedia.length);

    // If no filtered media found, use all media as fallback
    if (initialMedia.length === 0) {
      console.log('No filtered media found, using all media as fallback');
      initialMedia = this.currentProduct.media || [];
    }

    this.mediaGallery = new FsbProductGallery(
      mediaContainer,
      initialMedia,
      this.currentProduct.featured_image
    );
    this.mediaGallery.init();
  }

  setupVariantSelector() {
    const container = this.content.querySelector('.fsb__modal-variant-container');
    if (!container) return;

    const originalSelector = this.openedBy
      ?.closest('.fsb__category-grid__product-card')
      ?.querySelector('fsb-variantselector');

    if (originalSelector) {
      this.variantSelector = this.cloneVariantSelector(originalSelector);
      container.appendChild(this.variantSelector);
      // Log to confirm initialization
      setTimeout(() => {
        if (this.variantSelector) {
          console.log('Modal variant selector (post-append):', this.variantSelector, this.variantSelector.getAttribute('data-modal'));
        }
      }, 0);
      // Listen for variantChange on the modal's selector and handle it
      this.variantSelector.addEventListener('variantChange', (event) => {
        this.handleVariantChange(event);
      });
    } else {
      // Handle case where there's no variant selector (e.g., out-of-stock items)
      console.log('No variant selector found - this is normal for out-of-stock items');
      container.innerHTML = '<div class="fsb__modal-no-variants">No variants available for this product</div>';
    }
  }

  cloneVariantSelector(original) {
    // Create a new fsb-variantselector element so the custom element lifecycle runs
    const newSelector = document.createElement('fsb-variantselector');
    // Copy all attributes except id
    Array.from(original.attributes).forEach(attr => {
      if (attr.name !== 'id') {
        newSelector.setAttribute(attr.name, attr.value);
      }
    });
    // Add debug attribute to distinguish modal
    newSelector.setAttribute('data-debug', 'modal');
    // Add unique attribute for modal
    newSelector.setAttribute('data-modal', 'true');
    // Manually clone and append all child nodes (including scripts)
    Array.from(original.childNodes).forEach(node => {
      newSelector.appendChild(node.cloneNode(true));
    });

    // --- Make all radio IDs and label[for] unique in the modal ---
    const uniquePrefix = `modal-${Date.now()}-${Math.floor(Math.random()*10000)}-`;
    // Find all radios and labels in the newSelector
    const radios = newSelector.querySelectorAll('input[type="radio"]');
    const labels = newSelector.querySelectorAll('label');
    // Map old id to new id
    const idMap = {};
    radios.forEach((radio, idx) => {
      if (radio.id) {
        const newId = uniquePrefix + radio.id;
        idMap[radio.id] = newId;
        radio.id = newId;
      }
    });
    labels.forEach(label => {
      const oldFor = label.getAttribute('for');
      if (oldFor && idMap[oldFor]) {
        label.setAttribute('for', idMap[oldFor]);
      }
    });
    return newSelector;
  }

  setupAddToCartButton() {
    const button = this.content.querySelector('.fsb__atk-button');
    if (!button) return;

    button.addEventListener('click', () => {
      const originalButton = this.openedBy
        ?.closest('.fsb__category-grid__product-card')
        ?.querySelector(`.fsb__atk-button[data-action="${button.dataset.action}"`);

      if (originalButton) {
        originalButton.click();
        this.hide();
      }
    });
  }

  setupProductCarousel() {
    const carouselContainer = this.content.querySelector('.fsb__modal-products-carousel');
    if (!carouselContainer || !this.currentProduct) return;

    // Get products from the script tag containing dining bundles products data
    // This is more reliable than trying to parse from JSON metafields
    const productCard = this.productCard || this.openedBy?.closest('.fsb__category-grid__product-card');
    
    console.log('Product carousel setup - finding product card:', {
      hasThisProductCard: !!this.productCard,
      hasOpenedBy: !!this.openedBy,
      productCard: productCard,
      productCardTagName: productCard?.tagName,
      productCardClasses: productCard?.className
    });
    
    if (!productCard) {
      console.log('No product card found for carousel');
      const productsSection = carouselContainer.closest('.fsb__modal-products-section');
      if (productsSection) {
        productsSection.style.display = 'none';
      }
      return;
    }

    // Find the script tag with dining bundles products data
    // It could be inside the product card element or as a sibling
    let diningProductsScript = productCard.querySelector('.fsb__dining-bundles-products-data');
    
    // If not found, try searching by product ID in the document
    if (!diningProductsScript && this.currentProduct?.id) {
      diningProductsScript = document.querySelector(`.fsb__dining-bundles-products-data[data-product-id="${this.currentProduct.id}"]`);
    }
    
    // If still not found, try searching in parent or siblings
    if (!diningProductsScript) {
      // Check if productCard is a custom element (fsb-productcard)
      const parentElement = productCard.parentElement;
      if (parentElement) {
        // Try to find it as a sibling (if script is outside the custom element)
        const productId = productCard.getAttribute('data-product-id') || this.currentProduct?.id;
        if (productId) {
          diningProductsScript = parentElement.querySelector(`.fsb__dining-bundles-products-data[data-product-id="${productId}"]`);
        }
      }
    }
    
    console.log('Looking for dining products script:', {
      foundScript: !!diningProductsScript,
      productCardId: productCard.getAttribute('data-product-id') || this.currentProduct?.id,
      currentProductId: this.currentProduct?.id,
      allScripts: productCard.querySelectorAll('script').length,
      scriptContent: diningProductsScript?.textContent?.substring(0, 200)
    });
    
    if (!diningProductsScript) {
      console.log('No dining_bundles_products script tag found, hiding section');
      // Hide the section if no metafield data
      const productsSection = carouselContainer.closest('.fsb__modal-products-section');
      if (productsSection) {
        productsSection.style.display = 'none';
      }
      return;
    }

    // Parse the JSON data from the script tag
    let diningBundlesData;
    try {
      diningBundlesData = JSON.parse(diningProductsScript.textContent);
      console.log('Parsed dining bundles data:', diningBundlesData);
    } catch (e) {
      console.error('Error parsing dining bundles products data:', e);
      const productsSection = carouselContainer.closest('.fsb__modal-products-section');
      if (productsSection) {
        productsSection.style.display = 'none';
      }
      return;
    }
    
    const productReferences = diningBundlesData?.products || [];
    
    console.log('Product references from script:', productReferences);

    if (productReferences.length === 0) {
      console.log('No product references found, hiding section');
      const productsSection = carouselContainer.closest('.fsb__modal-products-section');
      if (productsSection) {
        productsSection.style.display = 'none';
      }
      return;
    }

    // Fetch products and render them
    this.loadProductsFromReferences(carouselContainer, productReferences);
  }

  async loadProductsFromReferences(container, productReferences) {
    // Show loading state
    container.innerHTML = '<div class="fsb__carousel-loading">Loading products...</div>';

    try {
      // Extract product handles or IDs from references
      // Metafield could contain product objects with handle/id, or just handles
      const productIdentifiers = [];
      
      productReferences.forEach((ref, index) => {
        console.log(`Processing product reference ${index}:`, ref);
        
        if (typeof ref === 'object' && ref !== null) {
          // The ref should have handle, id, and title from our Liquid template
          if (ref.handle) {
            console.log(`  Found handle: ${ref.handle}`);
            productIdentifiers.push({ type: 'handle', value: ref.handle, product: ref });
          } else if (ref.gid) {
            // Extract numeric ID from GID format (gid://shopify/Product/123456)
            const idMatch = ref.gid.toString().match(/\/Product\/(\d+)/);
            if (idMatch) {
              const productId = idMatch[1];
              console.log(`  Found ID from GID: ${productId}`);
              productIdentifiers.push({ type: 'id', value: productId, product: ref });
            }
          } else if (ref.id) {
            // Extract numeric ID from GID format or use as-is
            const idMatch = ref.id.toString().match(/\/Product\/(\d+)/);
            const productId = idMatch ? idMatch[1] : ref.id.toString();
            console.log(`  Found ID: ${productId}`);
            productIdentifiers.push({ type: 'id', value: productId, product: ref });
          }
        } else if (typeof ref === 'string') {
          // Check if it's a GID or handle
          if (ref.includes('/Product/')) {
            const idMatch = ref.match(/\/Product\/(\d+)/);
            if (idMatch) {
              productIdentifiers.push({ type: 'id', value: idMatch[1] });
            }
          } else if (ref.startsWith('gid://')) {
            // GID format
            const idMatch = ref.match(/\/Product\/(\d+)/);
            if (idMatch) {
              productIdentifiers.push({ type: 'id', value: idMatch[1] });
            }
          } else {
            // Assume it's a handle
            productIdentifiers.push({ type: 'handle', value: ref });
          }
        }
      });
      
      console.log('Product identifiers extracted:', productIdentifiers);

      if (productIdentifiers.length === 0) {
        container.innerHTML = '<p>No products found</p>';
        return;
      }

      // Check if we have product data already in the identifier objects WITH images
      const productsWithData = productIdentifiers
        .filter(identifier => identifier.product && identifier.product.handle && (identifier.product.featured_image || identifier.product.featured_media))
        .map(identifier => ({
          handle: identifier.product.handle,
          title: identifier.product.title,
          featured_image: identifier.product.featured_image,
          featured_media: identifier.product.featured_media,
          price: identifier.product.price || identifier.product.price_min,
          compare_at_price: identifier.product.compare_at_price || identifier.product.compare_at_price_min,
          id: identifier.product.id || identifier.value
        }));

      // Try to find existing product-block elements on the page first
      const existingProductBlocks = [];
      const allProductBlocks = document.querySelectorAll('product-block[data-product-id]');
      
      productIdentifiers.forEach(identifier => {
        const matchingBlock = Array.from(allProductBlocks).find(block => {
          const blockProductId = block.getAttribute('data-product-id');
          if (identifier.type === 'id') {
            return blockProductId === identifier.value;
          }
          // For handles, we'd need to check product handle - this might not be available
          return false;
        });
        
        if (matchingBlock) {
          existingProductBlocks.push(matchingBlock);
        }
      });

      // If we found existing product blocks, clone them (this uses product-block snippet)
      if (existingProductBlocks.length > 0) {
        console.log('Using existing product blocks:', existingProductBlocks.length);
        this.renderProductBlocksInCarousel(container, existingProductBlocks);
      } else if (productsWithData.length > 0 && productsWithData.length === productIdentifiers.length) {
        // Use product data from metafield if ALL products have image data
        console.log('Using products with data from metafield:', productsWithData.length, productsWithData);
        this.renderProductsInCarousel(container, productsWithData);
      } else {
        // Otherwise, fetch product data and render custom cards (we need images!)
        console.log('Fetching products by identifiers (need full product data with images):', productIdentifiers.length);
        const products = await this.fetchProductsByIdentifiers(productIdentifiers);
        console.log('Fetched products:', products.length, products);
        if (products.length > 0) {
          this.renderProductsInCarousel(container, products);
        } else {
          container.innerHTML = '<p>No products available</p>';
        }
      }
    } catch (error) {
      console.error('Error loading products for carousel:', error);
      container.innerHTML = '<p>Error loading products</p>';
    }
  }

  async fetchProductsByIdentifiers(identifiers) {
    const products = [];
    
    for (const identifier of identifiers) {
      try {
        let productData = null;
        const fetchUrl = `${window.Shopify.routes.root}products/${identifier.value}.js`;
        console.log(`Fetching product from: ${fetchUrl}`);
        
        if (identifier.type === 'handle') {
          // Fetch by handle using product recommendation or direct endpoint
          const response = await fetch(fetchUrl);
          if (response.ok) {
            productData = await response.json();
            console.log(`Fetched product data for ${identifier.value}:`, productData);
          } else {
            console.warn(`Failed to fetch product ${identifier.value}: ${response.status} ${response.statusText}`);
          }
        } else if (identifier.type === 'id') {
          // Try to fetch by ID - this might require a different endpoint
          // For now, we'll try to get it via handle lookup or use the product ID
          // Note: Direct product ID lookup might not work via standard endpoints
          const response = await fetch(fetchUrl);
          if (response.ok) {
            productData = await response.json();
            console.log(`Fetched product data for ID ${identifier.value}:`, productData);
          } else {
            console.warn(`Failed to fetch product by ID ${identifier.value}: ${response.status} ${response.statusText}`);
          }
        }
        
        if (productData) {
          const productObj = {
            handle: productData.handle,
            title: productData.title,
            featured_image: productData.featured_image,
            featured_media: productData.featured_media,
            price: productData.price || productData.price_min,
            compare_at_price: productData.compare_at_price || productData.compare_at_price_min,
            id: productData.id
          };
          console.log(`Processed product object:`, productObj, 'has image:', !!(productObj.featured_image || productObj.featured_media));
          products.push(productObj);
        }
      } catch (e) {
        console.warn(`Error fetching product ${identifier.value}:`, e);
      }
    }
    
    console.log(`Returning ${products.length} products from fetchProductsByIdentifiers`);
    return products;
  }

  renderProductBlocksInCarousel(container, productBlocks) {
    const scrollContainer = document.createElement('div');
    scrollContainer.className = 'fsb__modal-products-scroll';

    productBlocks.forEach(productBlock => {
      // Clone the product block
      const clonedBlock = productBlock.cloneNode(true);
      
      // Add carousel-specific classes and styles
      clonedBlock.style.maxWidth = '200px';
      clonedBlock.style.flexShrink = '0';
      clonedBlock.classList.add('fsb__modal-product-block');
      
      // Hide quick buy buttons in the carousel
      clonedBlock.querySelectorAll('.quickbuy-toggle, .quickbuy-container').forEach(el => {
        el.style.display = 'none';
      });
      
      scrollContainer.appendChild(clonedBlock);
    });

    container.innerHTML = '';
    container.appendChild(scrollContainer);
    
    // Initialize theme image loading if available
    if (window.theme && typeof window.theme.manuallyLoadImages === 'function') {
      window.theme.manuallyLoadImages(container);
    }
  }

  renderProductsInCarousel(container, products) {
    console.log('renderProductsInCarousel called with products:', products.length, products);
    
    const scrollContainer = document.createElement('div');
    scrollContainer.className = 'fsb__modal-products-scroll';

    products.forEach((product, index) => {
      console.log(`Rendering product ${index}:`, product);
      
      const productItem = document.createElement('div');
      productItem.className = 'fsb__modal-product-item';
      productItem.style.maxWidth = '200px';
      productItem.style.flexShrink = '0';
      
      const productUrl = `/products/${product.handle || ''}`;
      
      // Handle image URL formatting - Shopify product API returns featured_image as a full URL or object
      let imageUrl = '';
      if (product.featured_image) {
        if (typeof product.featured_image === 'string') {
          // If it's already a URL string, use it
          imageUrl = product.featured_image;
        } else if (product.featured_image.src) {
          // If it's an object with src property
          imageUrl = product.featured_image.src;
        } else if (product.featured_media && product.featured_media.preview) {
          // Check featured_media as fallback
          imageUrl = product.featured_media.preview.image?.src || product.featured_media.preview?.image?.url || '';
        }
      } else if (product.featured_media) {
        // Fallback to featured_media
        if (typeof product.featured_media === 'string') {
          imageUrl = product.featured_media;
        } else if (product.featured_media.preview?.image?.src) {
          imageUrl = product.featured_media.preview.image.src;
        } else if (product.featured_media.preview?.image?.url) {
          imageUrl = product.featured_media.preview.image.url;
        } else if (product.featured_media.src) {
          imageUrl = product.featured_media.src;
        }
      }
      
      console.log(`Product ${index} image URL:`, imageUrl, 'featured_image:', product.featured_image, 'featured_media:', product.featured_media);
      
      // Format image URL for 200px width if it's a Shopify CDN URL
      if (imageUrl) {
        // If URL already has query params, append width param
        if (imageUrl.includes('?')) {
          imageUrl = `${imageUrl}&width=200`;
        } else {
          imageUrl = `${imageUrl}?width=200`;
        }
      }
      
      const price = product.price || product.price_min || 0;
      const compareAtPrice = product.compare_at_price || product.compare_at_price_min || 0;
      
      productItem.innerHTML = `
        <a href="${productUrl}" class="fsb__modal-product-link">
          ${imageUrl ? `<div class="fsb__modal-product-image-wrapper" style="width: 200px; margin-bottom: 8px;">
            <img src="${imageUrl}" alt="${product.title || ''}" loading="lazy" style="width: 200px; height: auto; display: block; border-radius: 4px;">
          </div>` : '<div style="width: 200px; height: 200px; background: #f0f0f0; margin-bottom: 8px; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999;">No Image</div>'}
          <div class="fsb__modal-product-info">
            <h4 class="fsb__modal-product-title" style="font-size: 14px; font-weight: 500; margin: 0 0 4px 0; line-height: 1.3;">${product.title || 'Product'}</h4>
            <div class="fsb__modal-product-price" style="font-size: 13px;">
              ${compareAtPrice > price ? `<span class="price-compare" style="text-decoration: line-through; color: #999; margin-right: 8px;">${Fsb.formatMoney(compareAtPrice)}</span>` : ''}
              <span class="price-current">${price ? Fsb.formatMoney(price) : ''}</span>
            </div>
          </div>
        </a>
      `;
      
      scrollContainer.appendChild(productItem);
    });

    container.innerHTML = '';
    container.appendChild(scrollContainer);
    console.log('Finished rendering products in carousel');
  }

  async loadProductsIntoCarousel(container, products) {
    // Show loading state
    container.innerHTML = '<div class="fsb__carousel-loading">Loading products...</div>';

    if (products.length === 0) {
      container.innerHTML = '<p>No products available</p>';
      return;
    }

    try {
      // Collect existing product-block elements from the page that match our products
      const productBlocks = [];
      const allProductBlocks = document.querySelectorAll('product-block[data-product-id]');
      
      products.forEach(product => {
        // Find matching product-block on the page
        const matchingBlock = Array.from(allProductBlocks).find(block => {
          const blockProductId = block.getAttribute('data-product-id');
          return blockProductId && blockProductId === product.id.toString();
        });
        
        if (matchingBlock) {
          productBlocks.push(matchingBlock);
        }
      });

      if (productBlocks.length === 0) {
        // Fallback: create simple product items if no product blocks found
        let carouselHTML = '<div class="fsb__modal-products-scroll">';
        products.forEach(product => {
          const productUrl = product.handle?.startsWith('/') ? product.handle : `/products/${product.handle || ''}`;
          const imageUrl = product.featured_image || '';
          const price = product.price || 0;
          
          carouselHTML += `
            <div class="fsb__modal-product-item" style="max-width: 200px; flex-shrink: 0;">
              <a href="${productUrl}" class="fsb__modal-product-link">
                ${imageUrl ? `<img src="${imageUrl}&width=200" alt="${product.title || ''}" loading="lazy" style="width: 100%; height: auto; display: block;">` : ''}
                <div class="fsb__modal-product-info">
                  <h4 class="fsb__modal-product-title">${product.title || 'Product'}</h4>
                  ${price ? `<div class="fsb__modal-product-price">${Fsb.formatMoney(price)}</div>` : ''}
                </div>
              </a>
            </div>
          `;
        });
        carouselHTML += '</div>';
        container.innerHTML = carouselHTML;
        return;
      }

      // Clone product blocks and prepare for carousel
      const scrollContainer = document.createElement('div');
      scrollContainer.className = 'fsb__modal-products-scroll';
      
      productBlocks.forEach(productBlock => {
        // Clone the product block
        const clonedBlock = productBlock.cloneNode(true);
        
        // Add carousel-specific classes and styles
        clonedBlock.style.maxWidth = '200px';
        clonedBlock.style.flexShrink = '0';
        clonedBlock.classList.add('fsb__modal-product-block');
        
        // Hide quick buy buttons in the carousel
        clonedBlock.querySelectorAll('.quickbuy-toggle, .quickbuy-container').forEach(el => {
          el.style.display = 'none';
        });
        
        scrollContainer.appendChild(clonedBlock);
      });
      
      container.innerHTML = '';
      container.appendChild(scrollContainer);
      
      // Initialize theme image loading if available
      if (window.theme && typeof window.theme.manuallyLoadImages === 'function') {
        window.theme.manuallyLoadImages(container);
      }
    } catch (error) {
      console.error('Error loading products into carousel:', error);
      container.innerHTML = '<p>Unable to load products</p>';
    }
  }

  cleanup() {
    this.buttonObserver?.disconnect();
    this.variantObserver?.disconnect();
    this.buttonObserver = null;
    this.variantObserver = null;
    this.currentProduct = null;
    this.productCard = null;
    this.mediaGallery?.destroy();
    this.mediaGallery = null;
    this.variantSelector = null;
    
    // Clear any pending gallery updates
    if (this.galleryUpdateTimeout) {
      clearTimeout(this.galleryUpdateTimeout);
      this.galleryUpdateTimeout = null;
    }
    
    super.cleanup();
  }

  handleProductImageKeydown(event) {
    if (fsbAdditionalSettings.show_popup === true) {
      if (event.key === 'Enter' || event.key === ' ') {
        const handleProductImageClick = document.querySelector('fsb-product-modal').handleProductImageClick;
        event.preventDefault();
        handleProductImageClick(event);
      }
    }
  }
}

customElements.define('fsb-product-modal', FsbProductModal);

/**
 * Product gallery handler for modal
 * Manages media display and thumbnails
 */
class FsbProductGallery {
  constructor(container, mediaItems = [], fallbackImage = null) {
    this.container = container;
    this.mediaItems = mediaItems;
    this.fallbackImage = fallbackImage;
    this.currentIndex = 0;
  }

  init() {
    this.container.innerHTML = this.createGalleryHTML();

    if (this.mediaItems.length > 0) {
      this.setupMediaElements();
    } else if (this.fallbackImage) {
      this.setupFallbackImage();
    }

    this.setupEventListeners();
  }

  createGalleryHTML() {
    return `
      <div class="fsb__gallery-main"></div>
      ${this.mediaItems.length > 1 ? '<div class="fsb__gallery-thumbs"></div>' : ''}
    `;
  }

  setupMediaElements() {
    const mainContainer = this.container.querySelector('.fsb__gallery-main');
    const thumbsContainer = this.container.querySelector('.fsb__gallery-thumbs');

    // Setup main image
    const firstMedia = this.mediaItems[0];
    const imageSrc = firstMedia.src || 
                    firstMedia.media_link || 
                    firstMedia.url ||
                    firstMedia.preview_image?.src ||
                    firstMedia.preview_image?.url ||
                    firstMedia.image?.src ||
                    firstMedia.image?.url;
    const imageAlt = firstMedia.alt || firstMedia.media_alt;
    
    if (imageSrc) {
      const mainImage = this.createImage(imageSrc, imageAlt);
      mainContainer.appendChild(mainImage);
    } else {
      console.warn('No image source found for first media item:', firstMedia);
    }

    // Setup thumbnails if multiple images
    if (thumbsContainer && this.mediaItems.length > 1) {
      const fragment = document.createDocumentFragment();

      for (const [index, media] of this.mediaItems.entries()) {
        const thumb = this.createThumbnail(media, index === 0);
        if (thumb.children.length > 0) { // Only add if thumbnail has an image
          fragment.appendChild(thumb);
        }
      }

      thumbsContainer.appendChild(fragment);
    }
  }

  createImage(src, alt = '') {
    const img = new Image();
    img.src = src;
    img.alt = alt;
    img.className = 'fsb__gallery-image';
    return img;
  }

  createThumbnail(media, isActive = false) {
    const thumb = document.createElement('button');
    thumb.className = `fsb__gallery-thumb ${isActive ? 'is-active' : ''}`;
    thumb.setAttribute('aria-label', `View ${media.alt || media.media_alt || 'product image'}`);

    // Handle different media property names - check multiple possible sources
    const imageSrc = media.src || 
                    media.media_link || 
                    media.url || 
                    media.preview_image?.src ||
                    media.preview_image?.url ||
                    media.image?.src ||
                    media.image?.url;
                    
    if (!imageSrc) {
      console.warn('No image source found for media:', media);
      return thumb;
    }

    const img = this.createImage(`${imageSrc}&width=100`, media.alt || media.media_alt);
    thumb.appendChild(img);

    return thumb;
  }

  setupFallbackImage() {
    const mainContainer = this.container.querySelector('.fsb__gallery-main');
    const img = new Image();
    img.src = this.fallbackImage;
    img.alt = '';
    img.className = 'fsb__gallery-image';
    mainContainer.appendChild(img);
  }

  setupEventListeners() {
    const thumbsContainer = this.container.querySelector('.fsb__gallery-thumbs');
    if (!thumbsContainer) return;

    thumbsContainer.addEventListener('click', (e) => {
      const thumb = e.target.closest('.fsb__gallery-thumb');
      if (!thumb) return;

      const index = Array.from(thumbsContainer.children).indexOf(thumb);
      this.updateMainImage(index);
    });
  }

  updateMainImage(index) {
    if (index === this.currentIndex) return;

    const mainImage = this.container.querySelector('.fsb__gallery-main img');
    if (!mainImage) return;

    const media = this.mediaItems[index];
    const imageSrc = media.src || 
                    media.media_link || 
                    media.url ||
                    media.preview_image?.src ||
                    media.preview_image?.url ||
                    media.image?.src ||
                    media.image?.url;
    const imageAlt = media.alt || media.media_alt || '';
    
    if (imageSrc) {
      mainImage.src = imageSrc;
      mainImage.alt = imageAlt;
    } else {
      console.warn('No image source found for media at index:', index, media);
    }

    // Update thumbnails
    const thumbs = this.container.querySelectorAll('.fsb__gallery-thumb');
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
    });

    this.currentIndex = index;
  }

  /**
   * Updates the gallery with filtered media items based on variant selection
   * @param {Array} filteredMedia - Array of media items to display
   * @param {string} variantTitle - The variant title to match against
   */
  updateGalleryForVariant(filteredMedia, variantTitle) {
    if (!filteredMedia || filteredMedia.length === 0) return;

    console.log('Updating gallery for variant:', variantTitle, 'with', filteredMedia.length, 'media items');

    this.mediaItems = filteredMedia;
    this.currentIndex = 0;

    // Update main image
    const mainContainer = this.container.querySelector('.fsb__gallery-main');
    if (mainContainer) {
      mainContainer.innerHTML = '';
      const imageSrc = filteredMedia[0].src || 
                      filteredMedia[0].media_link || 
                      filteredMedia[0].url ||
                      filteredMedia[0].preview_image?.src ||
                      filteredMedia[0].preview_image?.url ||
                      filteredMedia[0].image?.src ||
                      filteredMedia[0].image?.url;
      const imageAlt = filteredMedia[0].alt || filteredMedia[0].media_alt;
      
      if (imageSrc) {
        const mainImage = this.createImage(imageSrc, imageAlt);
        mainContainer.appendChild(mainImage);
        console.log('Updated main image with:', imageSrc);
      } else {
        console.warn('No image source found for main image:', filteredMedia[0]);
      }
    }

    // Update thumbnails
    const thumbsContainer = this.container.querySelector('.fsb__gallery-thumbs');
    if (thumbsContainer) {
      thumbsContainer.innerHTML = '';
      
      if (filteredMedia.length > 1) {
        const fragment = document.createDocumentFragment();
        
        for (const [index, media] of filteredMedia.entries()) {
          const thumb = this.createThumbnail(media, index === 0);
          fragment.appendChild(thumb);
        }
        
        thumbsContainer.appendChild(fragment);
        console.log('Updated thumbnails with', filteredMedia.length, 'items');
      }
    }

    // Re-setup event listeners for new thumbnails
    this.setupEventListeners();
  }

  /**
   * Filters media items based on variant selection
   * @param {Array} allMedia - All available media items
   * @param {Object} variant - The selected variant
   * @param {string} productTitle - The product title
   * @returns {Array} Filtered media items
   */
  static filterMediaForVariant(allMedia, variant, productTitle) {
    if (!allMedia || allMedia.length === 0) return [];

    console.log('Filtering media for variant:', variant?.title, 'Product:', productTitle);
    console.log('Total media items:', allMedia.length);
    
    // Debug: Show structure of first few media items
    if (allMedia.length > 0) {
      console.log('Sample media item structure:', {
        first: allMedia[0],
        second: allMedia[1],
        third: allMedia[2]
      });
    }

    // If no variant is selected or it's the default variant, show all media
    if (!variant || !variant.title || variant.is_default) {
      console.log('No variant or default variant, showing all media');
      return allMedia;
    }

    const variantTitle = variant.title.toLowerCase();
    const productTitleLower = productTitle.toLowerCase();

    console.log('Looking for variant:', variantTitle, 'in product:', productTitleLower);

    // Filter media items based on alt text matching the variant
    const filteredMedia = allMedia.filter(media => {
      // Skip media items with blank alt text to avoid duplicates/older images
      if (!media.alt && !media.media_alt) {
        console.log('Skipping media with blank alt text:', media.id);
        return false;
      }
      
      const mediaAlt = (media.alt || media.media_alt || '').toLowerCase();
      
      // Skip if alt text is just whitespace
      if (mediaAlt.trim() === '') {
        console.log('Skipping media with whitespace-only alt text:', media.id);
        return false;
      }
      
      // For multi-variant products, require EXACT alt text match
      // This ensures we only show images specifically tagged for the selected variant
      if (mediaAlt === variantTitle) {
        console.log('Found exact alt text match:', media.alt);
        return true;
      }
      
      // Check for common variant patterns (e.g., "Vail Dining Chair - Black")
      const variantPattern = `${productTitleLower} - ${variantTitle}`;
      if (mediaAlt === variantPattern) {
        console.log('Found match by variant pattern:', media.alt);
        return true;
      }
      
      // For backward compatibility, allow partial matches ONLY for single-variant products
      // (products with only one option like color)
      if (variantTitle.split(' ').length <= 2) {
        // Check if the media alt text contains the exact variant title
        if (mediaAlt.includes(variantTitle)) {
          console.log('Found partial match for single-variant product:', media.alt);
          return true;
        }
      }
      
      return false;
    });

    console.log('Filtered media count:', filteredMedia.length);

    // If no filtered media found, return the first few items as fallback
    if (filteredMedia.length === 0) {
      console.log('No matches found, using fallback media');
      return allMedia.slice(0, Math.min(6, allMedia.length));
    }

    return filteredMedia;
  }

  destroy() {
    this.container.innerHTML = '';
  }
}

customElements.define('fsb-product-gallery', FsbProductGallery);

/**
 * Personalization modal component
 * Extends base modal with personalization functionality
 */
class FsbPersonalizationModal extends FsbModalDialog {
  constructor() {
    super();
    this.currentVariant = null;
    this.productCard = null;
    this.maxLength = window.fsbSettings.personalization_char;

    // Set up modal event listeners
    this.setupEventListeners();
  }

  showPersonalization(productCard, variant) {
    this.productCard = productCard;
    this.currentVariant = variant;
    this.setupModalContent();
    super.show(productCard);
  }

  setupModalContent() {
    if (!this.content || !this.currentVariant) return;

    const modalHTML = this.createModalHTML();
    this.content.innerHTML = modalHTML;
    this.setupFormEventListeners();

    // Focus the textarea after content is set up
    queueMicrotask(() => {
      const messageArea = this.content.querySelector('#addon-message');
      if (messageArea) messageArea.focus();
    });
  }

  setupFormEventListeners() {
    const messageArea = this.content.querySelector('#addon-message');
    const charCount = this.content.querySelector('#char-count');
    const addButton = this.content.querySelector('#add-personalization');
    const cancelButton = this.content.querySelector('#cancel-personalization');

    messageArea?.addEventListener('input', (e) => {
      const count = e.target.value.length;
      if (charCount) charCount.textContent = count;
    });

    addButton?.addEventListener('click', () => {
      const message = messageArea?.value || '';
      this.handleAddToBundle(message);
    });

    cancelButton?.addEventListener('click', () => {
      this.hide();
    });
  }

  createModalHTML() {
    const { title } = this.productCard.product;
    const imageUrl = this.currentVariant.media_link || this.productCard.product.featured_image;
    const displayTitle =
      this.currentVariant.title && !this.currentVariant.is_default ? `${title} - ${this.currentVariant.title}` : title;

    return `
    <div class="fsb__personalization-modal">
      <h2 class="fsb__personalization-title">${displayTitle}</h2>
      <div class="fsb__personalization-layout">
        ${
          imageUrl
            ? `<div class="fsb__personalization-image-container">
                  <img src="${imageUrl}" alt="${title}" class="fsb__personalization-image">
                </div>`
            : ''
        }
        <div class="fsb__personalization-content">
          <div class="fsb__personalization-form">
            <label for="addon-message" class="fsb__personalization-label">
              ${window.fsbSettings.personalization_title}
            </label>
            <textarea 
              id="addon-message" 
              class="fsb__personalization-textarea" 
              placeholder="${window.fsbSettings.personalization_placeholder}"
              maxlength="${this.maxLength}"
            ></textarea>
            <div class="fsb__personalization-counter">
              <span id="char-count">0</span>/${this.maxLength}
            </div>
          </div>
          <div class="fsb__personalization-actions">
            <button type="button" class="fsb__button fsb__button-inverted" id="cancel-personalization">
              ${window.fsbSettings.personalization_cancel}
            </button>
            <button type="button" class="fsb__button" id="add-personalization">
              ${window.fsbSettings.personalization_add}
            </button>
          </div>
        </div>
      </div>
    </div>
    `;
  }

  handleAddToBundle(message) {
    if (!this.currentVariant?.id || !this.productCard?.bundleState) return;

    if (this.itemKey) {
      // Update existing item
      const addonItems = this.productCard.bundleState.addOnItems;
      if (addonItems[this.itemKey]) {
        addonItems[this.itemKey].personalization = message.trim();
        this.productCard.bundleState.notifySubscribers('addOnItems');
      }
    } else {
      // Add new item
      if (this.productCard.bundleState.addOnQuantity >= this.productCard.bundleState.addOnLimits.maximum) {
        const notification = document.createElement('div');
        notification.className = 'fsb__notification';
        notification.textContent = window.fsbSettings.personalization_max_error;
        this.productCard.appendChild(notification);
        setTimeout(() => notification.remove(), 3000);
        return;
      }

      // Helper function to get the display price (compare_at_price as primary, price as fallback)
      const getDisplayPrice = (variant) => {
        return variant.compare_at_price && variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      };

      this.productCard.bundleState.addAddonItem(this.currentVariant.id, {
        price: getDisplayPrice(this.currentVariant),
        title: this.productCard.product.title,
        variantTitle: !this.currentVariant.is_default ? this.currentVariant.title : null,
        image: this.currentVariant.media_link || this.productCard.product.featured_image,
        personalization: message.trim(),
      });
    }

    this.hide();
  }

  hide() {
    super.hide();
  }

  cleanup() {
    this.currentVariant = null;
    this.productCard = null;
    this.itemKey = null;
    super.cleanup();
  }

  showPersonalizationWithData(productCard, variant, existingMessage, itemKey) {
    this.productCard = productCard;
    this.currentVariant = variant;
    this.itemKey = itemKey;
    this.setupModalContent();
    super.show(productCard);

    // Pre-fill the message
    const messageArea = this.content.querySelector('#addon-message');
    const charCount = this.content.querySelector('#char-count');
    if (messageArea && existingMessage) {
      messageArea.value = existingMessage;
      if (charCount) {
        charCount.textContent = existingMessage.length;
      }
    }

    // Update the add button text to indicate editing
    const addButton = this.content.querySelector('#add-personalization');
    if (addButton) {
      // Use "Update" + property name to clearly indicate what's being updated
      addButton.textContent =
        window.fsbSettings.personalization_add || `${window.fsbSettings.personalization_property_name}`;
    }
  }
}

customElements.define('fsb-personalization', FsbPersonalizationModal);

// Add a simple test to check if variant selectors are being created
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM Content Loaded');
  const variantSelectors = document.querySelectorAll('fsb-variantselector');
  console.log('Found variant selectors:', variantSelectors.length);
  
  variantSelectors.forEach((selector, index) => {
    console.log(`Variant selector ${index}:`, selector);
    const selects = selector.querySelectorAll('select');
    const radios = selector.querySelectorAll('input[type="radio"]');
    console.log(`  - Selects: ${selects.length}, Radios: ${radios.length}`);
  });
  
  // Add a test function to manually trigger Walnut swatch click
  window.testWalnutClick = () => {
    console.log('Testing Walnut click...');
    const walnutSwatch = document.querySelector('.color-swatch[data-color-name="walnut"]');
    if (walnutSwatch) {
      console.log('Found Walnut swatch, triggering click...');
      walnutSwatch.click();
    } else {
      console.log('Walnut swatch not found');
    }
  };
  
  // Add a test function to check all swatches
  window.testAllSwatches = () => {
    console.log('Testing all swatches...');
    const allSwatches = document.querySelectorAll('.color-swatch');
    console.log('Found swatches:', allSwatches.length);
    allSwatches.forEach((swatch, index) => {
      console.log(`Swatch ${index}:`, {
        colorName: swatch.dataset.colorName,
        for: swatch.getAttribute('for'),
        textContent: swatch.textContent.trim()
      });
    });
  };
});

testAllSwatches()

// Add a style for the not-allowed cursor on force-disabled buttons (only once)
if (!window._fsbForceDisabledCursorStyle) {
  const style = document.createElement('style');
  style.textContent = `
    .fsb__atk-button--force-disabled {
      cursor: not-allowed !important;
    }
  `;
  document.head.appendChild(style);
  window._fsbForceDisabledCursorStyle = true;
}

// Add styles for the Learn More link in modal (only once)
if (!window._fsbModalLearnMoreStyle) {
  const learnMoreStyle = document.createElement('style');
  learnMoreStyle.textContent = `
    .fsb__modal-learn-more {
      margin-top: 12px;
      text-align: center;
    }
    .fsb__modal-learn-more-link {
      color: #333;
      text-decoration: underline;
      font-size: 14px;
      font-weight: normal;
      cursor: pointer;
      transition: color 0.2s ease;
    }
    .fsb__modal-learn-more-link:hover {
      color: #666;
    }
  `;
  document.head.appendChild(learnMoreStyle);
  window._fsbModalLearnMoreStyle = true;
}
